// src/components/desk/HistoryCards.jsx — the Recent-entries ledger: label maps,
// summarize/kindOf, the history card renderers (HistoryItem/HistoryGroup/groupHistory),
// AreaPreview and the entries map view, plus PlatformPill — extracted VERBATIM from
// IntakeTab.jsx (DESK_MODE_REWRITE_PLAN §3 decomposition, behavior-preserving).
// Everything IntakeTab still references (compactSummary, CSV export, handleCardClick,
// activity labels, list rows) is exported and imported back there.

import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { MEETING_ACTIVE } from '../../../core/meeting-statuses.mjs'
import { createPortal } from 'react-dom'
import L from 'leaflet'
import 'leaflet.markercluster'
import { t, tLog, getLanguage } from '../../lib/i18n.js'
import { todayStr } from '../../lib/clock.js'
import { Icon, IconText } from '../Icon.jsx'
import { ReshareMark } from './ReshareMark.jsx'
import { card, Badge, COUNTER, pill, popoverPos, useCloseOnPageMove, useHoverCapable, useCanHover, useMoney, useAdText, priceTitle, ObservedPriceHistoryBadge, glowAmber, glowBlue } from './primitives.jsx'
import { formatMoney } from '../../lib/money.js'
import { intentLabel, kindLabel } from '../../lib/labels.js'
import { ListingGallery, cityForDisplay } from './ListingGallery.jsx'
import { CooperationChips, ShelfToggles, coopMeetingSeed, coopIntentOf } from './CooperationChips.jsx'
import DistinctNote, { pickDistinct } from './DistinctNote.jsx'
import AgentFacts from './AgentFacts.jsx'
import DevFloorPlan from './DevFloorPlan.jsx'
import { PLATFORM_LABEL, PLATFORM_COLOR, PIN_COLOR, platformKey } from '../ListingsTab.jsx'
import { buildChain } from '../../lib/chainModel.js'
import { mapCoverageNote } from '../../lib/deskAnswerState.js'
import { getFbShareStats, getPropertyContacts, getListingBriefs } from '../../db/core.js'
import { nearbyLocations } from '../../db/locations.js'
import { LOC_RADIUS_BY_TYPE, LOC_TYPE_ICON, LOC_FETCH_RADIUS_M, pickNearbyChips, briefLabel } from '../../lib/deskFeed.js'
// TYPE_PILL_LABEL / kindOf / entryCoords now live in the pure feed module so `node --test`
// can run them (this file imports leaflet + JSX and Node cannot load it). Re-exported here
// unchanged, so every existing `from './HistoryCards.jsx'` import site is untouched.
import { TYPE_PILL_LABEL, kindOf, entryCoords } from '../../lib/deskFeed.js'
import { markerSpecs, syncMarkerLayer, leafletMarkerOps } from '../../lib/mapMarkers.js'
import { sellerLine } from './SellerFacets.jsx'
export { TYPE_PILL_LABEL, kindOf, entryCoords }

// Colored platform pill — identical palette to the Listings tab tallies (K/M/S chips).
// Falls back to the raw source string uncolored when the platform isn't recognized.
// `count` (2026-07-21): the POSTED ON roll-up groups an agent's reposts per platform, so
// three MyHome reposts render ONE pill reading "MyHome ×3" instead of three identical pills.
// Omitted everywhere else — a single posting shows no multiplier.
// ── Nearby places on hover of the address (2026-07-30 owner request) ──────────────────
// Answers "where actually is this?" without opening the modal. Renders the SAME chips the
// modal's ლოკაცია section does (pickNearbyChips + LOC_RADIUS_BY_TYPE, shared in deskFeed).
//
// ⚠️ GATED ON is_representative, and that gate is the SERVER's answer, not a client guess.
// Until 2026-07-30 the feed shipped an exact pin to every viewer while the modal degraded
// street+pin to a district for non-representatives — this hover is precisely what would
// have weaponised that (three named distances trilaterate a doorstep). The server now
// nulls lat/lng for non-reps, so `hasPin` is false for them and this never renders; the
// flag is checked anyway, because two independent reasons to stay closed is the point.
//
// A popover, not `title=`: the address element's title already carries the
// inherited-address provenance warning and the untruncated address, and clobbering either
// would trade one fact for another. Fetch is lazy (first hover), cached per property for
// the tab's lifetime, and hover-only — on touch the modal remains the way in.
function NearbyHover({ propertyId, lat, lng, isRep, children, className, title }) {
  const hoverCapable = useHoverCapable()
  const hasPin = lat != null && lng != null
  const enabled = hoverCapable && isRep && hasPin && !!propertyId
  const [pos, setPos] = useState(null)
  const [rows, setRows] = useState(null)
  const ref = useRef(null)
  const closeT = useRef(null)
  const cancelClose = () => { if (closeT.current) { clearTimeout(closeT.current); closeT.current = null } }
  const scheduleClose = () => { cancelClose(); closeT.current = setTimeout(() => setPos(null), 120) }

  const open = () => {
    if (!enabled || !ref.current) return
    // 'down': this anchor is mid-card, so the upward default would cover the card above.
    setPos(popoverPos(ref.current, 300, 'down'))
    if (rows === null) {
      // Cached by property: a scroll back over the same card must not re-query PostGIS.
      const hit = nearbyHoverCache.get(propertyId)
      if (hit) { hit.then(setRows); return }
      // THREE CLOSEST, not the full set (owner, 2026-07-30: "we don't need these full
      // list it's too much"). pickNearbyChips still runs first, so the per-type radius
      // cutoffs apply and a 2km mall cannot crowd out a 400m park — then plain nearest-first.
      // The MODAL keeps the full list: it is the detail view, this is a glance.
      const pr = nearbyLocations(lat, lng, LOC_FETCH_RADIUS_M, null)
        .then(rs => pickNearbyChips(rs, LOC_RADIUS_BY_TYPE)
          .slice().sort((a, b) => a.distance_m - b.distance_m).slice(0, 3))
        .catch(() => [])
      nearbyHoverCache.set(propertyId, pr)
      pr.then(setRows)
    }
  }
  // Closes when the PAGE moves, never when the panel's own body is scrolled — see
  // useCloseOnPageMove. The ref must reach the panel element or the guard has nothing to test.
  const panelRef = useCloseOnPageMove(!!pos, () => setPos(null))

  if (!enabled) return <p ref={ref} className={className} title={title}>{children}</p>
  // ⚠️ NO native `title` on the enabled branch, or the browser tooltip fires ALONGSIDE the
  // popover and the agent gets two overlapping boxes saying different things (reported from
  // the screen, 2026-07-30). The title was carrying two facts — the untruncated address and
  // the inherited-address provenance warning — so they move INTO the panel rather than being
  // dropped: same information, one surface.
  return (<>
    <p ref={ref} className={className}
      onMouseEnter={() => { cancelClose(); open() }} onMouseLeave={scheduleClose}>{children}</p>
    {pos && createPortal(
      <div ref={panelRef} style={pos} onMouseEnter={cancelClose} onMouseLeave={scheduleClose}
        className="desk-popover fixed z-[200] bg-card border border-border rounded-lg shadow-xl py-2 px-3 w-[300px] text-sm overflow-y-auto overscroll-contain">
        {title && <p className="text-xs text-foreground/90 mb-1.5 leading-snug">{title}</p>}
        {rows === null
          ? <span className="text-muted-foreground text-xs">…</span>
          : rows.length
            ? <div className="flex flex-wrap gap-1.5">
                {rows.map(r => (
                  <span key={r.id} className={pill('none', { extra: 'bg-muted border-transparent text-foreground/80' })}>
                    <Icon name={LOC_TYPE_ICON[r.type] || 'map-pin'} /> {locNameOf(r)}
                    <span className="text-muted-foreground tabular-nums">{locDistLabel(r.distance_m)}</span>
                  </span>
                ))}
              </div>
            : <span className="text-muted-foreground text-xs">{t('desk_loc_none_nearby')}</span>}
      </div>, document.body)}
  </>)
}
// One platform chip on a card face, owning its own hover panel.
//
// ⚠️ EXTRACTED INTO A COMPONENT (2026-08-02) because the chips render inside a .map() and
// hover state needs a hook — you cannot call one per iteration. Do not inline this back.
//
// ⚠️ The ad text used to ride the native `title` attribute. It was verifiably present and
// correct on the live page — right element, 135 chars, stable node, no re-render churn,
// pointer-events auto — and the OS tooltip still did not appear for the owner, twice, on
// two different screens. A mechanism that is provably correct and still does not deliver
// the payload is not a working mechanism, so this now uses the SAME popover idiom
// NearbyHover above already uses: instant instead of the OS delay, styled like the rest of
// the Desk, and it wraps long Georgian prose instead of one OS-truncated line.
//
// The `title` survives ONLY on the no-hover branch (touch), where a popover cannot open and
// the native tooltip is the last resort before the modal. On the hover branch there is
// deliberately NO title — NearbyHover's header records why: both fire at once and the agent
// gets two overlapping boxes saying different things.
//
// The chip stays an <a>: its click must remain "open the ad", and a tap on touch must not be
// hijacked into revealing text that the modal already shows in full.
// `dot` (2026-08-04, owner: "on hover on those listings dots show what hover on pills did"):
// the state column's owner's-ads row draws each platform as a DOT, and the dot must answer
// hover exactly like the pill it replaced — same popover, same ad list, same links. One
// component, two bodies, so the two renderings cannot drift into two different answers.
// Exported ONLY so `src/harness.jsx` can mount it (case P). The Desk is login-gated, and the
// one thing this component's popover must do — scroll a long ad text without dismissing
// itself — is invisible to every gate. Nothing else imports it; it stays a card-face internal.
// FB publication counts for the F chip's popover (owner 2026-08-20: one row per posting
// spree — the hover says how far it reached). Fetched lazily on FIRST hover, cached per
// property for the page's life: a card list must never fan out N stats calls on render.
const fbStatsCache = new Map()
async function fbStatsFor(propertyId) {
  if (!fbStatsCache.has(propertyId)) {
    fbStatsCache.set(propertyId, getFbShareStats(propertyId).then(v => v?.rows || []).catch(() => []))
  }
  return fbStatsCache.get(propertyId)
}
export function PlatformChip({ c, label, pk, caption, dot = false, propertyId = null }) {
  // ⚠️ useCanHover, NOT useHoverCapable — the latter also demands `pointer: fine`, which
  // describes the PRIMARY pointing device. A touch-capable Windows laptop reports `coarse`
  // even with a mouse attached, so this panel was invisible to a real mouse user while being
  // verifiably correct on every machine reporting `fine` (owner-reported three times before
  // the gate was identified as the cause). "Can they hover" is the only question here.
  const hoverCapable = useCanHover()
  const [pos, setPos] = useState(null)
  const ref = useRef(null)
  const closeT = useRef(null)
  const cancelClose = () => { if (closeT.current) { clearTimeout(closeT.current); closeT.current = null } }
  const scheduleClose = () => { cancelClose(); closeT.current = setTimeout(() => setPos(null), 120) }
  // ⚠️ This is the panel the owner reported (2026-08-09): a full ad description is routinely
  // taller than `popoverPos`'s maxHeight, and the old close-on-any-scroll ate the wheel event
  // the moment the agent tried to read past the fold.
  const panelRef = useCloseOnPageMove(!!pos, () => setPos(null))
  // A chip covering more than one ad cannot be a link — see the ads[] note in the builder.
  // It opens its list instead, in the popover this chip already owns. Single-ad chips are
  // untouched: still an <a>, still "click opens the ad".
  const multi = (c.ads?.length || 0) > 1
  const note = [caption, c.adText].filter(Boolean).join('\n\n')
  // A dot ALWAYS answers hover: unlike the pill it carries no platform name on its face, so
  // "which site is this" is exactly the question the popover exists to settle.
  const enabled = hoverCapable && (multi || !!note || dot)
  const body = dot
    // Dot grammar (design 4b): the dot is the PLATFORM's colour; a closed listing goes grey;
    // the ring marks the owner's SOURCE ad on that platform — and when no root is known, no
    // chip is `origin`, so no ring renders and nothing claims to be first.
    ? (<>
        <span className="w-1.5 h-1.5 rounded-full inline-block"
          style={{
            background: c.status === 'active' ? ((pk && PIN_COLOR[pk]) || 'currentColor') : 'hsl(220 9% 45%)',
            boxShadow: c.origin ? `0 0 0 2px ${((pk && PIN_COLOR[pk]) || 'currentColor')}55` : undefined,
          }} />
        {c.count}
      </>)
    : (<>
        <span className={`w-1.5 h-1.5 rounded-full ${c.status === 'active' ? 'bg-green-500' : 'bg-muted-foreground/40'}`} />
        {c.origin && <Icon name="globe" className="opacity-70" />}
        {label}{c.count > 1 ? <span className="opacity-70">×{c.count}</span> : null}
        {/* `layers` not `list`: Icon renders NOTHING for a name it doesn't know, silently. */}
        {(multi || c.source_url) && <span><Icon name={multi ? 'layers' : 'external-link'} /></span>}
      </>)
  const cls = dot
    ? 'inline-flex items-center gap-1 cursor-pointer tabular-nums'
    : pill('none', { door: true, extra: `${c.origin ? 'border-dashed border-muted-foreground/50' : 'border-border'} ${pk ? PLATFORM_COLOR[pk] : 'bg-background'}` })
  const [fbStats, setFbStats] = useState(null)   // {post_count, group_count} for THIS chip's listing
  const open = () => {
    cancelClose(); setPos(popoverPos(ref.current, 300, 'down'))
    if (pk === 'facebook' && propertyId && fbStats === null) {
      fbStatsFor(propertyId).then(rows => {
        // this chip's own listing row when the endpoint knows it; a spree registered
        // before the counts rail existed just shows nothing extra (rows empty → 0-guard)
        const own = rows.find(r => r.listing_id === c.id) || (rows.length === 1 ? rows[0] : null)
        setFbStats(own || { post_count: 0, group_count: 0 })
      })
    }
  }
  return (<>
    {multi
      // Click TOGGLES rather than opens: on touch there is no hover, so this is the only way
      // in — and the only way back out without leaving the card.
      ? <button ref={ref} type="button"
        onClick={e => { e.stopPropagation(); if (pos) setPos(null); else open() }}
        title={enabled ? undefined : (note || undefined)}
        onMouseEnter={enabled ? open : undefined}
        onMouseLeave={enabled ? scheduleClose : undefined}
        className={cls}>{body}</button>
      : <a ref={ref} href={c.source_url || undefined} target={c.source_url ? '_blank' : undefined} rel="noreferrer"
        onClick={e => e.stopPropagation()}
        title={enabled ? undefined : (note || undefined)}
        onMouseEnter={enabled ? open : undefined}
        onMouseLeave={enabled ? scheduleClose : undefined}
        className={cls}>{body}</a>}
    {pos && createPortal(
      <div ref={panelRef} style={pos} onMouseEnter={cancelClose} onMouseLeave={scheduleClose}
        className="desk-popover fixed z-[200] bg-card border border-border rounded-lg shadow-xl py-2 px-3 w-[300px] text-sm overflow-y-auto overscroll-contain">
        {/* The dot's face carries no platform name — the popover leads with it. */}
        {dot && <p className="text-xs font-medium mb-1">{label}{c.count > 1 ? ` ×${c.count}` : ''}</p>}
        {/* One F chip = a whole posting spree (owner 2026-08-20) — the hover carries its
            reach; the chip's link is the canonical post (our own group when we have one). */}
        {pk === 'facebook' && fbStats?.post_count > 0 && (
          <p className="text-xs text-muted-foreground mb-1.5">
            {fbStats.post_count} {t('desk_lineage_fb_posts')}{fbStats.group_count > 0 && <> · {fbStats.group_count} {t('desk_lineage_fb_groups')}</>}
          </p>
        )}
        {caption && <p className="text-xs text-muted-foreground mb-1.5">{caption}</p>}
        {c.adText && <p className="text-xs text-foreground/90 leading-snug whitespace-pre-line">{c.adText}</p>}
        {(multi || (dot && c.ads?.length > 0)) && (
          <div className={c.adText || caption ? 'mt-2 pt-2 border-t border-border' : ''}>
            <p className="text-xs text-muted-foreground mb-1">{t('desk_entry_platform_ads_pick')}</p>
            {c.ads.map(a => (
              <a key={a.id} href={a.url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                className="flex items-center gap-1.5 py-1 text-xs text-foreground/90 hover:text-foreground hover:underline">
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${a.status === 'active' ? 'bg-green-500' : 'bg-muted-foreground/40'}`} />
                {/* The ad's own portal id names WHICH posting; the date is what an agent
                    actually recognises. Both, because the id alone is a number nobody
                    remembers and the date alone cannot separate two postings made the same
                    day. The id is absent only on rows shaped before 2026-08-03 added it. */}
                {a.adId && <span className="font-mono opacity-70 shrink-0">#{a.adId}</span>}
                <span className="truncate">{a.at ? new Date(a.at).toLocaleDateString() : t('desk_entry_platform_ads_undated')}</span>
                {/* Each posting's OWN asking price — the card face shows one number, and the
                    whole reason to open this list is that the others may differ. */}
                {a.price != null && (
                  <span className="ml-auto shrink-0 tabular-nums">{a.price}{a.currency ? ` ${a.currency}` : ''}</span>
                )}
                <Icon name="external-link" className={`${a.price != null ? 'ml-1.5' : 'ml-auto'} shrink-0 opacity-70`} />
              </a>
            ))}
          </div>
        )}
      </div>, document.body)}
  </>)
}
// ─── B274 — the district question, ON THE CARD ─────────────────────────────────────────────
// RULING B (0364) counts a disputed property under BOTH candidate districts so an agent can
// see it and correct us — owner: *"we show if we're not sure and later it will be corrected"*.
// The showing half shipped; the ASKING half did not, and the gap is measurable: 1,392 disputed
// properties against **7 votes in a year** (2026-08-02). The question was never hard to answer,
// it was three clicks away in the lineage modal, which nobody opens mid-search.
//
// Same mechanism as the modal's box (`voteDistrict`, 0101) — deliberately NOT a second vote
// path. What changes is only WHERE it is asked. Local state mirrors the modal's: a vote's
// response carries the fresh tally, so the chip updates in place without refetching the feed.
//
// ⚠️ Renders ONLY where a vote can actually be cast (`onVote` supplied — today the Leads
// feed). A question with no answerable buttons is worse than silence: it labels the card
// unreliable and offers no way out. Every other HistoryGroup caller passes nothing and is
// unchanged.
//
// ⚠️ `stopPropagation` on every control. The whole card is a click target that opens the
// lineage modal — without it, answering the question closes the card behind a modal, and the
// agent loses their place in the feed. That is precisely the friction this row exists to remove.
function DistrictDisputeChip({ listing, districts, onVote }) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [votes, setVotes] = useState(listing.district_votes || { declared: 0, polygon: 0 })
  const [mine, setMine] = useState(null)
  const nameOf = code => {
    const d = districts?.find(x => x.code === code)
    if (!d) return code
    return getLanguage() === 'ka' ? (d.name_ka || d.name_en) : (d.name_en || d.name_ka)
  }
  const declared = nameOf(listing.declared_district_code)
  const polygon = nameOf(listing.district_code)
  const cast = async (e, choice) => {
    e.stopPropagation(); e.preventDefault()
    if (busy) return
    setBusy(true)
    try {
      const r = await onVote(listing.property_id, choice)
      setVotes(r?.votes || votes)
      setMine(r?.my_vote || choice)
    } catch (err) { console.error('[district-vote]', err.message) }
    setBusy(false)
  }
  const stop = e => { e.stopPropagation(); e.preventDefault() }
  if (mine) return (
    <span className={pill('ok')} onClick={stop}>
      <Icon name="check" /> {nameOf(mine === 'declared' ? listing.declared_district_code : listing.district_code)}
      <span className="opacity-70">{votes.declared}:{votes.polygon}</span>
    </span>
  )
  if (!open) return (
    <span className={pill('warn', { door: true })} title={tLog('desk_district_dispute_question', { declared, polygon })}
      onClick={e => { stop(e); setOpen(true) }}>
      <Icon name="alert-triangle" /> {tLog('desk_district_dispute_chip', { declared, polygon })}
    </span>
  )
  return (<>
    <button disabled={busy} onClick={e => cast(e, 'declared')} className={pill('warn', { door: true })}>
      <Icon name="check" /> {declared}
    </button>
    <button disabled={busy} onClick={e => cast(e, 'polygon')} className={pill('warn', { door: true })}>
      <Icon name="check" /> {polygon}
    </button>
    <span className={pill('neutral', { door: true })} onClick={e => { stop(e); setOpen(false) }}>
      <Icon name="x" />
    </span>
  </>)
}

const nearbyHoverCache = new Map()
// Same two formatters the modal uses. Kept tiny and local rather than exported from the
// modal, which would import a 2,400-line component into every card render.
const locDistLabel = m => m < 1000 ? `${Math.round(m / 10) * 10}${t('desk_loc_unit_m')}` : `${(m / 1000).toFixed(1)}${t('desk_loc_unit_km')}`
const locNameOf = r => (getLanguage() === 'ka' && r.name_ka) ? r.name_ka : r.name

export function PlatformPill({ source, count, compact = false }) {
  const key = platformKey(source)
  const label = key ? PLATFORM_LABEL[key] : (source || '?')
  // compact (§11, owner 2026-07-24): single-initial pill — S / M / K — for dense rows
  // (collapsed agent-group headers) where full names truncated; color stays the carrier,
  // the full name survives in the title.
  return (
    <span title={compact ? label : undefined}
      className={`${COUNTER} font-semibold ${key ? PLATFORM_COLOR[key] : ''}`}>
      {compact ? label.charAt(0).toUpperCase() : label}{count > 1 && <span className="opacity-75"> ×{count}</span>}
    </span>
  )
}

// ─── Recent entries list — the "who made this" ledger ───────────────────────
// Design rule: an entry reads as the OBJECT it produced (a client, a meeting, a
// candidate — same nouns as the four Desk buttons, same icons), never as the raw
// event name that ran. The full technical event (proposal_kind, ops, JSON) is one
// click away behind ▼ for whoever needs it — background stays precise, front stays plain.
export const OBJECT_TYPE = {
  extract_requirement: 'client', amend_requirement: 'client',
  close_requirement: 'client', reopen_requirement: 'client',
  extract_listing: 'property',
  schedule_meeting: 'meeting', add_candidate: 'candidate',
  // BOTH read-only scraped-listing kinds are LISTINGS (immutable ad observations) → the
  // Listings pill, read-only. A reshare (listing_readonly, has an outbound derived_from edge,
  // 0041) is the agent's own repost; a plain capture (scraped_listing_readonly) is an observed
  // ad. The EDITABLE property (belief layer) lives under the separate 'Property' pill, which is
  // its own feed (PropertyFeed → agent-properties), NOT these listing rows. (2026-07-08: was
  // scraped_listing_readonly:'property'; moved to 'listing' so scraped captures read-only under
  // Listings, and Property shows one editable card per physical property instead.)
  listing_readonly: 'listing', scraped_listing_readonly: 'listing',
}
// Icon-registry names (see Icon.jsx), not emoji — rendered via <Icon name={OBJECT_ICON[...]}/>.
export const OBJECT_ICON = { client: 'user-check', property: 'house', meeting: 'calendar', candidate: 'briefcase', listing: 'file-text' }
// Type pill labels — a plain capitalize-first-letter doesn't work for 'listing' → 'Listings'
// (plural, matches the "🏠 Listings" nav tab wording), so this is an explicit map, not a rule.
// Definition moved to ../../lib/deskFeed.js; re-exported above.
const TYPE_PILL_LABEL_KEYS = { client: 'desk_type_client', property: 'desk_type_property', meeting: 'desk_type_meeting', listing: 'desk_type_listings' }
// Entry-type filter <select> uses the English label text itself as both value and display
// (matched against TYPE_PILL_LABEL output elsewhere) — keep the value literal, translate only the text.
export const ENTRY_TYPE_FILTER_LABEL_KEYS = { Client: 'desk_type_client', Property: 'desk_type_property', Meeting: 'desk_type_meeting', Listings: 'desk_type_listings', Business: 'desk_type_business', Leads: 'desk_type_leads' }
// Deal-type wording for a LISTING's own intent (sale/rent/daily_rent) — deliberately separate
// from INTENT_LABEL below, which is demand-side phrasing ("Renting" = I'm looking to rent);
// a listing card needs supply-side wording ("For rent" = this is available to rent).
// (map retired 2026-07-23 → src/lib/labels.js intentLabel — the ONE display dictionary)

// A third-party "staticmap" render service (tried first) failed DNS resolution entirely in this
// environment (net::ERR_NAME_NOT_RESOLVED, confirmed live) — an unverified dependency for
// something shown on every card is too fragile. Two zero-risk alternatives instead, both used
// nowhere else needing npm install (unsafe in this Drive-synced folder, see memory) or a paid
// Mapbox/Google key (none configured):
//   1) listings: ONE raw XYZ tile from the SAME OpenStreetMap tile server PolygonMapPicker
//      already uses successfully elsewhere in this file, with a pin drawn in CSS on top — real
//      map context, no per-card Leaflet instance (that doesn't scale to a whole grid of cards).
//   2) client search areas: a dependency-free inline SVG trace of the polygon shape — no network
//      at all, so nothing to fail. No street/tile context, but the shape itself is often
//      recognizable and it can never break.
// Standard Web Mercator, global pixel space at a given zoom (256px tiles) — the same projection
// every XYZ tile server uses, so a point computed here lines up exactly with the tile images.
function lngLatToPixel(lng, lat, zoom) {
  const n = 256 * 2 ** zoom
  const x = (lng + 180) / 360 * n
  const latRad = lat * Math.PI / 180
  const y = (1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n
  return { x, y }
}
// Picks the HIGHEST zoom whose pixel bounding box still fits the target preview size — the
// search area's own real footprint decides the zoom, not a fixed guess (a tiny courtyard and a
// whole district both need to fully fit the same small thumbnail).
// The LARGEST zoom whose pixel bbox still fits WITHIN the target box — "contain", not "cover":
// the whole polygon must always stay visible (the point of this preview), even if that leaves
// some blank margin on one axis for an elongated shape. A "cover" fit (crop to fill) was tried
// first but a wide, short polygon then needs a zoom where the SHORT axis alone reaches the
// target — at that zoom the long axis wildly overshoots, and a centered square crop can land
// entirely inside the shape's interior with no boundary in view at all (worse than blank
// margin). The actual root cause of "only half present" was simpler: target was hardcoded to
// 300 while the real 'large' box (w-full, aspect-[4/3] since 2026-07-06) renders at ~300-500px on typical grid
// widths — fixed below by matching the target to that real size instead of guessing small.
function pickAreaZoom(ring, targetW, targetH) {
  const lngs = ring.map(([lng]) => lng), lats = ring.map(([, lat]) => lat)
  const minLng = Math.min(...lngs), maxLng = Math.max(...lngs)
  const minLat = Math.min(...lats), maxLat = Math.max(...lats)
  for (let z = 18; z >= 3; z--) {
    const p1 = lngLatToPixel(minLng, maxLat, z)   // NW corner (max lat = north = smaller pixel y)
    const p2 = lngLatToPixel(maxLng, minLat, z)   // SE corner
    if (p2.x - p1.x <= targetW && p2.y - p1.y <= targetH) return { zoom: z, p1, p2 }
  }
  const p1 = lngLatToPixel(minLng, maxLat, 3), p2 = lngLatToPixel(maxLng, minLat, 3)
  return { zoom: 3, p1, p2 }
}
// 'accepted' = committed-but-NOT-executed. For audit-only meeting/candidate rows that's just
// "logged" (remapped to Saved below); for a real requirement command it means the gateway
// refused and the write rolled back — genuinely NOT saved, so we say so plainly (never "Saving…").
// active/withdrawn are the real listing.status values — a scraped-listing row's item.status IS
// one of these directly, not a proposal status, so they're plain labels here.
// ⚠️ `withdrawn` WAS MISSING and `inactive` was never a real value (measured 2026-08-09 on the
// live corpus: active 44,544 / withdrawn 9,921 / inactive ZERO). The consumer is
// `t(STATUS_LABEL_KEYS[displayStatus]) || displayStatus`, and t() returns its argument when the
// key is missing — so with `undefined` in, the `||` fell through and every off-market card
// rendered the raw English word "withdrawn" on an all-Georgian screen. `inactive` is kept as a
// harmless alias in case an old row carries it; it is not evidence the value exists.
export const STATUS_LABEL_KEYS = { pending: 'desk_status_pending', accepted: 'desk_status_not_saved', executed: 'desk_status_saved', rejected: 'desk_status_rejected', edited: 'desk_status_edited', failed: 'desk_status_failed', dismissed: 'desk_status_archived', active: 'desk_status_on_market', withdrawn: 'desk_status_off_market', inactive: 'desk_status_off_market' }
// Days a platform typically keeps a listing live before it auto-expires (agent's own estimate,
// 2026-07-05 — not scraped from the platform, just experience: myhome ~30d, ss ~45d, korter ~60d).
// Used ONLY to color the age badge below; a real 'inactive' status (we actually saw it pulled)
// always wins over the estimate.
const PLATFORM_EXPIRY_DAYS = { 'myhome.ge': 30, 'ss.ge': 45, 'korter.ge': 60 }
// Replaces the flat "On market" badge with how long the ad has been up, colored by how close it
// is to that platform's typical auto-expiry: green (first week) → yellow (up to half the expiry
// window) → amber (past half, still presumed live) → red (past the platform's usual expiry —
// probably stale/expired even though we haven't re-scraped it as 'inactive' yet). listed_at is
// stamped by upsert_listing at INSERT time (first time WE saw it, `now()`, never touched again on
// re-scrape) — a proxy for "posted", not the platform's own posting date, since scrapers don't
// extract one. Returns null (caller falls back to the plain On/Off market badge) when the source
// isn't one of the three tracked platforms or listed_at is missing.
// Relative capture time for the card's provenance line (2026-07-17 card redesign) — the old
// full `toLocaleString()` (date + seconds) on every card was archaeology-grade noise; "how
// fresh" is the scanning question. The exact timestamp stays one hover away (title attr).
export function relTime(ts) {
  const d = new Date(ts).getTime()
  if (!Number.isFinite(d)) return ''
  const mins = Math.floor((Date.now() - d) / 60000)
  if (mins < 1) return 'now'
  if (mins < 60) return `${mins}m`
  if (mins < 1440) return `${Math.floor(mins / 60)}h`
  if (mins < 10080) return `${Math.floor(mins / 1440)}d`
  return new Date(ts).toLocaleDateString()
}
// ─── The owner's own words (2026-07-30) ──────────────────────────────────────
// The ad's description text, exactly as scraped. Owner request: *"the owner's listing
// description comment that was scraped from the original ad, nothing new — if there was
// no comment by the owner leave it empty."*
//
// READ-ONLY on purpose. This is an OBSERVATION of what the ad says, not a field of ours:
// it is not editable here, it carries WHEN we last saw the ad saying it, and it renders
// NOTHING at all when there is no text (no empty card, no "no description" ceremony).
//
// ONE renderer, two surfaces (property modal + the lineage/leads modal) — both already
// import from this module, and both read the same `lineage` payload, so neither re-decides
// what an ad description is. Prefer the ORIGIN's text over a reshare's: a reshare's
// description is an agent's rewrite, and the request was for the original ad's.
// ⚠️ Under representation restriction the SERVER has already stripped phones/links/emails
// from this string (redactContactChannels in listingLineagePayload). Never re-assemble it
// from another source client-side.
export function adDescriptionOf(lineage) {
  const o = lineage?.origin, l = lineage?.listing
  if (o?.ad_description) return { text: o.ad_description, at: o.ad_description_at || null, fromOrigin: true, versions: o.ad_description_versions || [] }
  if (l?.ad_description) return { text: l.ad_description, at: l.ad_description_at || null, fromOrigin: false, versions: l.ad_description_versions || [] }
  return null
}
// VERSIONS (owner 2026-10-14: "text must have versions and show all versions"): when the owner edited the ad, the
// newest wording stays on top and every earlier one is listed under it with the dates we saw it — nothing is replaced.
export function AdDescriptionCard({ text, at, fromOrigin = false, versions = [] }) {
  const [open, setOpen] = useState(false)
  const [showOld, setShowOld] = useState(false)
  if (!text) return null
  const older = (versions || []).slice(1)
  const long = text.length > 420
  return (
    <div className={card}>
      <p className="text-xs font-semibold text-muted-foreground mb-1">
        <Icon name="message-circle" size={12} /> {t('desk_ad_description_label')}
        {at && <span className="ml-1 font-normal text-xs">· {relTime(at)}</span>}
        {fromOrigin && <span className="ml-1 font-normal text-xs">· {t('desk_ad_description_from_origin')}</span>}
      </p>
      {/* Inline -webkit-line-clamp, not the Tailwind utility — same reason AITab does it:
          it must clamp regardless of whether the line-clamp plugin is in the build. */}
      <p className="text-sm text-foreground whitespace-pre-wrap"
        style={long && !open ? { display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical', overflow: 'hidden' } : undefined}>{text}</p>
      {long && (
        <button className="mt-1 text-xs text-primary hover:underline" onClick={() => setOpen(v => !v)}>
          {open ? t('desk_ad_description_less') : t('desk_ad_description_more')}
        </button>
      )}
      {older.length > 0 && (
        <div className="mt-2 border-t border-border pt-2">
          <button className="text-xs text-primary hover:underline" onClick={() => setShowOld(v => !v)}>
            {t('desk_ad_description_versions').replace('{n}', String(older.length))}
          </button>
          {showOld && older.map((v, i) => (
            <div key={i} className="mt-2">
              <p className="text-xs text-muted-foreground">
                {new Date(v.first_seen_at).toLocaleDateString()}
                {v.last_seen_at && v.last_seen_at !== v.first_seen_at ? ' – ' + new Date(v.last_seen_at).toLocaleDateString() : ''}
              </p>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{v.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function listingAgeBadge(l) {
  if (!l || l.status === 'inactive') return null
  const expiryDays = PLATFORM_EXPIRY_DAYS[l.source]
  if (!expiryDays || !l.listed_at) return null
  const days = Math.floor((Date.now() - new Date(l.listed_at).getTime()) / 86400000)
  if (!Number.isFinite(days) || days < 0) return null
  const variant = days <= 7 ? 'green' : days <= expiryDays / 2 ? 'yellow' : days < expiryDays ? 'amber' : 'red'
  return {
    label: days === 0 ? 'today' : days === 1 ? '1d' : `${days}d`,
    variant,
    title: `${days} day${days === 1 ? '' : 's'} since first seen on ${l.source} — typically expires around ${expiryDays}d`,
  }
}
// freshnessBadge (the "ნანახია X წინ" chip on last_seen_at, design skin §4) was REMOVED
// 2026-08-03: the owner's three-date card line (market · acquired · checked) carries the
// same fact as შემოწმდა, and a chip restating one of the line's dates was double vision.
// "📆 available from" card-face badge (2026-07-21). Sits beside the freshness badge because
// both answer timing questions and the eye already goes there. A date that has PASSED means
// the property is free NOW — the date has stopped being news, so it renders nothing rather
// than accumulating stale chips across the feed. Compared as YYYY-MM-DD strings against the
// local day: available_from is a plain calendar date with no timezone, so parsing it into a
// Date would shift it a day for anyone east/west of UTC (the office is UTC+4).
// ⚠️ THE OFFICE DAY, NOT THE DEVICE DAY (fixed 2026-09-02). This computed "today" from
// `new Date()` — the viewer's own clock — while the comment above correctly reasons about the
// office being UTC+4. So a laptop set to another timezone, or with a wrong date, made the badge
// appear or vanish a day early: a flat free from tomorrow reads as free NOW, which is the one
// thing this badge exists to prevent. `todayStr()` is the SERVER-corrected instant formatted in
// the office timezone — the same fix applied across the Desk today under the owner's rule that
// the system must not use the PC's time.
export function availableFromBadge(availableFrom) {
  if (!availableFrom) return null
  if (availableFrom <= todayStr()) return null
  return { label: `📆 ${availableFrom}`, variant: 'blue', title: t('desk_avail_facet_title') }
}
// 0258 — "an agent phoned the owner and was told this is gone." The card MUST say so:
// the feed already sinks reported properties (IntakeTab activeGroups sort), and a card
// that quietly moves down the page with no explanation is the kind of invisible
// behaviour this codebase treats as a bug. Amber, never red, and never a strikethrough:
// these reports are UNVERIFIED by construction (0178 refuses to let hearsay archive
// anything), so the card says "reported", not "sold".
export function marketReportBadge(reportCount) {
  if (!reportCount) return null
  return { label: tLog('desk_lead_reported_gone', { n: reportCount }), variant: 'amber',
           title: t('desk_market_report_badge_title') }
}
// Created / last-updated span for a listing card that stands in for a whole STACK (the origin
// scrape + its reshares, 0041) — the user's rule: "there are not one listing, it's scraped
// (created) listing and reshares." created = the OLDEST listed_at across the stack (the original
// ad's listing date — the origin is the oldest); updated = the freshest real change anywhere in
// the stack. listing.updated_at only moves on an actual observation change (price/status/…),
// never a no-op re-scrape (see server shapeListingRow), and a fresh reshare contributes its own
// listed_at — so max(both) is the true "last touched" for the property. Null when nothing dated.
// ⚠️ CORRECTION 2026-07-27 — the comment above used to call listed_at "the original
// ad's listing date". It never was. Measured across all 32,517 listing rows: NOT ONE
// has a listed_at that differs from created_at by more than an hour. listed_at is
// WHEN WE FIRST SAW THE AD, so this card was showing agents the crawl date while
// reading as the posting date — an ad that had sat unsold for three months looked
// exactly as fresh as one posted an hour ago, which for a leads feed is the whole
// question. core 0228 added `posted_at`: the PORTAL's own claim, present for
// ss/myhome/bog, a republish stamp for korter, and NULL for home.ge (it states none).
// Prefer it; fall back to first-seen only when the portal said nothing, and tell the
// UI which one it got so the label can be honest instead of confidently wrong.
// ⚠️ EXTENDED 2026-08-03 (owner: "we have three dates"): the line is now market ·
// acquired · checked. `created` stays the MARKET date (posted_at with first-seen
// fallback, as above); `acquired` = the oldest listed_at across the stack (when OUR
// system got it — crawler or agent); `checked` = the freshest last_seen_at anywhere
// in the stack (observe_listing bumps it on every re-crawl, changed or not — the
// "still alive" stamp the freshness chip used to carry). `modified` kept for callers
// but no longer rendered — updated_at is microsecond-identical to last_seen_at live.
function listingStackDates(items) {
  let created = null, modified = null, createdIsPosted = false, postedKind = null
  let acquired = null, checked = null
  for (const i of items || []) {
    const l = i.listing || {}
    // the ad's own posting date wins; first-seen is the fallback, never a substitute
    const posted = l.posted_at || null
    const listedAt = l.listed_at || i.created_at || null
    const candidate = posted || listedAt
    if (candidate && (!created || candidate < created)) {
      created = candidate
      createdIsPosted = !!posted
      postedKind = posted ? (l.posted_at_kind || null) : null
    }
    if (listedAt && (!acquired || listedAt < acquired)) acquired = listedAt
    const seenAt = l.last_seen_at || null
    if (seenAt && (!checked || seenAt > checked)) checked = seenAt
    const updatedAt = l.updated_at || null
    for (const d of [updatedAt, listedAt]) if (d && (!modified || d > modified)) modified = d
  }
  return created ? { created, modified: modified || created, createdIsPosted, postedKind, acquired, checked } : null
}
// Elapsed-time form for the visible date line (owner 2026-08-03: "show how many days have
// passed since that instead of showing exact dates") — exact timestamps stay in the hover
// tooltip. Same today/Nd convention the retired age chip used, extended upward so a
// 16-month-old ad reads as "16mo", not a wall of days.
// Exported 2026-08-07 for the client-lead card — the same move StateRow got on 08-05 and
// for the same reason: a category filling the state slot must speak the COLUMN's grammar,
// not re-derive its own. A client card reading `3h` beside a property card reading `14დ`
// in the same table is exactly the divergence the state column exists to prevent.
// ⚠️ Not interchangeable with relTime (:427). relTime is the FACTS-column caption format
// (now · 5m · 3h · 2d); this is the STATE-column format (დღეს · 3დ · 27დ · 3თვე) used by
// the განახლდა / ჩვენთან / შემოწმდა rows. Pick by which column you are writing into.
export function cardAge(iso) {
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)
  if (!Number.isFinite(d)) return ''
  if (d <= 0) return t('desk_age_today')
  if (d < 30) return `${d}${t('desk_age_unit_d')}`
  if (d < 365) return `${Math.round(d / 30)}${t('desk_age_unit_mo')}`
  return `${(d / 365).toFixed(1)}${t('desk_age_unit_y')}`
}
// Full date + time, for the hover tooltip (the visible line stays date-only for scannability).
export function cardDateTime(iso) { try { return new Date(iso).toLocaleString() } catch { return '' } }
// Compact one-liner for the lineage modal's header subtitle — "70მ², 2ოთ, 1საძ, 17/20ს,
// 2 სვ.წ": area, rooms, bedrooms, floor, bathrooms. Land has no rooms/floor/bathrooms, only
// area. A house's "floor" isn't a within-building position — its floors_total (how many
// stories the house itself has) stands alone instead of a floor/total pair. Bathroom count
// only shown above 1 — a single სვ.წ is assumed on every apartment, so stating it is noise;
// it's only worth calling out when there's more than one.
// ⚠️ Two rules earned 2026-08-03 (owner: "must be clear in short form too"):
// (1) ONE MEANING PER SYMBOL. Rooms/bedrooms used to share the floor notation ("2/1ოთ" next
//     to "17/20ს"), so the same slash meant "of that many" in one token and "of which" in the
//     other — unreadable at a glance. Bedrooms is now its OWN token with its OWN unit, and
//     the slash is left to mean floor-of-total and nothing else.
// (2) The units are i18n keys, not literals. This string renders inside an English UI too,
//     where hardcoded Georgian suffixes sat next to English words (same disease as the raw
//     `kind` token on the card face, fixed in the same commit).
export function formatListingSummary(m) {
  // condition is a factTokens-only token (it earns its place on the POSTED ON diff line,
  // where platforms disagree about it) — the header subtitle stays numbers-only.
  return factTokens(m).filter(tk => tk.key !== 'condition').map(tk => tk.text).join(', ') || null
}
// Same fields/order as formatListingSummary, but as separate TOKENS instead of one joined
// string — so each can be colored independently against a reference posting. Every posting in
// POSTED ON is a full independent scrape/claim (its own property_id — 0041), so a reshare's
// area/rooms/floor CAN genuinely disagree with the origin ad; the previous full-text dump made
// every row look identical at a glance even when one platform quietly listed different numbers
// (2026-07-09 — "make what's different eye-catching, not the whole fact sheet again").
function factTokens(m) {
  if (!m) return []
  const out = []
  if (m.area_sqm != null) out.push({ key: 'area_sqm', text: tLog('desk_compact_area', { area: m.area_sqm }), val: m.area_sqm })
  if (m.kind === 'land') return out
  if (m.rooms != null) out.push({ key: 'rooms', text: tLog('desk_compact_rooms', { rooms: m.rooms }), val: m.rooms })
  // Bedrooms is its own token, shown whenever recorded — including when it equals rooms.
  // The old "only when it differs" rule made a missing bedroom count and a bedrooms==rooms
  // studio pixel-identical, and bedrooms is a filterable fact (bedroomsSet), so a card that
  // silently omits it reads as "unknown" for a listing the filter will happily return.
  if (m.bedrooms != null) out.push({ key: 'bedrooms', text: tLog('desk_compact_bedrooms', { bedrooms: m.bedrooms }), val: m.bedrooms })
  if (m.kind === 'house') {
    if (m.floors_total != null) out.push({ key: 'floor', text: tLog('desk_compact_floors_total', { total: m.floors_total }), val: m.floors_total })
  } else if (m.floor != null) {
    out.push({ key: 'floor', text: m.floors_total != null ? tLog('desk_compact_floor_of', { floor: m.floor, total: m.floors_total }) : tLog('desk_compact_floor', { floor: m.floor }), val: m.floor })
  }
  if (m.bathrooms != null && m.bathrooms > 1) out.push({ key: 'bathrooms', text: tLog('desk_compact_bathrooms', { n: m.bathrooms }), val: m.bathrooms })
  if (m.condition) out.push({ key: 'condition', text: m.condition, val: m.condition })
  return out
}
const DIFF_HL = 'text-amber-600 dark:text-amber-400 font-semibold'
// One facts line per POSTED ON row, only the tokens that disagree with `ref` (the origin ad,
// or this listing's own values when it IS the origin) get highlighted — everything else stays
// plain muted text, exactly like before.
export function FactsDiffLine({ m, refM }) {
  const tokens = factTokens(m)
  if (!tokens.length) return null
  return (
    <span className="block text-xs font-normal text-muted-foreground">
      {tokens.map((tk, i) => {
        const rv = refM ? refM[tk.key === 'floor' ? 'floor' : tk.key] : undefined
        const differs = refM && rv != null && rv !== tk.val
        return <span key={tk.key}>{i > 0 ? ', ' : ''}<span className={differs ? DIFF_HL : undefined}>{tk.text}</span></span>
      })}
    </span>
  )
}
// Same idea for the address line — highlighted only when it actually differs from the
// reference posting's address (a reshare posted under a different address than its origin).
export function AddressDiffLine({ value, refValue }) {
  if (!value) return null
  const differs = !!(refValue && refValue !== value)
  return <span className={`block text-xs font-normal ${differs ? DIFF_HL : 'text-muted-foreground'}`}>{value}</span>
}
export const FIELD_LABEL_KEYS = {
  price_min: 'desk_fieldlabel_price_from', price_max: 'desk_fieldlabel_price_to', currency: 'desk_fieldlabel_currency',
  area_sqm_min: 'desk_fieldlabel_area_from', area_sqm_max: 'desk_fieldlabel_area_to',
  rooms_min: 'desk_fieldlabel_rooms_from', rooms_max: 'desk_fieldlabel_rooms_to',
  bedrooms_min: 'desk_fieldlabel_bedrooms_from', bedrooms_max: 'desk_fieldlabel_bedrooms_to',
  district_codes: 'desk_fieldlabel_districts', excluded_district_codes: 'desk_fieldlabel_excluded_districts', excluded_complex_codes: 'desk_fieldlabel_excluded_complexes', complex_codes: 'desk_fieldlabel_complexes',
  must_have_codes: 'desk_fieldlabel_must_haves', nice_to_have_codes: 'desk_fieldlabel_nice_to_haves', must_not_have_codes: 'desk_fieldlabel_must_not_haves',
  condition_codes: 'desk_fieldlabel_condition', condition_exclude_codes: 'desk_fieldlabel_condition_exclude',
  max_drive_minutes: 'desk_fieldlabel_drive_time', mortgage_ready: 'desk_fieldlabel_mortgage', has_pets: 'desk_fieldlabel_pets', has_children: 'desk_fieldlabel_children',
  needs_deeded_parking: 'desk_crit_parking_deeded',
}
// kindOf moved to ../../lib/deskFeed.js; re-exported above.
// A synthesized PROPERTY-head card (the editable cluster head) reuses a listing's card kind
// for its DESIGN (scraped_listing_readonly renders the thumb/address/facts row), but must
// filter under the Property pill and open the editable modal — so its objType is forced to
// 'property' via the __isProperty marker, ahead of the kind→objType map.
export function objectTypeOf(item) { return item?.__isProperty ? 'property' : (OBJECT_TYPE[kindOf(item)] || null) }

// The crawler's name, taken from the ROW and never from a constant in this file.
// `capture_bot` (server, via migration 0388's crawler_generation) carries the name of the
// generation that was running when the listing was captured, which is the whole point of the
// owner's 2026-08-03 ruling: replacing the crawler must rename what it finds FROM THEN ON,
// while every card already captured keeps saying Argo. A hardcoded 'Argo' here would silently
// retro-rename the entire archive on the day a successor ships.
// Locale is chosen here because the server has no business knowing the Desk's language; it
// sends both and the card picks, same rule the district names follow.
export function crawlerName(listing) {
  if (!listing || listing.capture_source !== 'crawler' || !listing.capture_bot) return null
  const bot = listing.capture_bot
  return (getLanguage() === 'ka' ? (bot.name_ka || bot.name_en) : (bot.name_en || bot.name_ka)) || null
}

// Compiles the raw command payload into the plain sentence a non-technical agent reads —
// the same fields ClientReview shows before commit, just condensed to a headline + one line.
// `money` = { display, rate } from MoneyCtx. Passed in rather than read from context
// because summarize is a plain function called during a component's render, not a hook.
export function summarize(item, districts, features, money = { display: null, rate: null }) {
  const priceOf = (amount, currency, suffix = '') =>
    formatMoney(amount, currency, money.display, money.rate, { suffix })
  const districtName = code => { const d = districts.find(d => d.code === code); return d ? (d.name_en || d.name_ka || code) : code }
  const k = kindOf(item), p = item.proposed || {}

  if (k === 'extract_requirement') {
    const person = p.person || {}, req = p.requirement || {}, crit = p.criteria || {}
    const bits = [req.intent, req.property_category].filter(Boolean)
    const reviewDistricts = [...(crit.district_must_codes || []), ...(crit.district_codes || [])]
    if (reviewDistricts.length) bits.push(reviewDistricts.slice(0, 2).map(districtName).join(', '))
    if (crit.price_max != null) bits.push(tLog('desk_person_up_to_price', { price: crit.price_max, currency: crit.currency || '' }).trim())
    return { title: person.display_name || person.phone || t('desk_entry_new_client'), subtitle: bits.join(' · ') || null }
  }
  if (k === 'amend_requirement') {
    const fields = (p.ops || []).map(o => t(FIELD_LABEL_KEYS[o.field]) || o.field)
    return { title: t('desk_entry_client_update'), subtitle: fields.length ? tLog('desk_entry_changed_fields', { fields: fields.join(', ') }) : null }
  }
  // `reason` is free text an agent typed, but internal callers also stamp snake_case codes
  // there (data_desk_manual, probe_cleanup, …). Show prose; never show a code — matching a
  // hardcoded sentinel list kept leaking every new code someone added.
  if (k === 'close_requirement') {
    const prose = p.reason && !/^[a-z0-9]+(_[a-z0-9]+)+$/.test(p.reason) ? p.reason : null
    return { title: t('desk_entry_client_closed'), subtitle: prose || t('desk_entry_closed_from_desk') }
  }
  if (k === 'reopen_requirement') return { title: t('desk_entry_client_reopened'), subtitle: t('desk_entry_back_on_active_list') }
  if (k === 'schedule_meeting') {
    const m = p.meeting || {}
    return { title: m.clientName || m.clientPhone || t('desk_entry_meeting_fallback'), subtitle: [m.date, m.time].filter(Boolean).join(' ') || null }
  }
  if (k === 'add_candidate') return { title: p.name || p.phone || t('desk_entry_new_candidate'), subtitle: p.phone && p.name ? p.phone : (p.position || null) }
  if (k === 'extract_listing') {
    const inp = p.input || {}, pr = inp.property || {}, lst = inp.listing || {}
    // ⚠️ A WhatsApp owner offer (core 4151 fb_offer_draft) has no address yet, and its raw_text starts with a machine
    // header — the card read "[whatsapp · owner offer · sale] 59890307" and agents could not tell what it was
    // (owner 2026-11-03). Its title is what the owner said (the reader's own summary, `notes`); the phone goes below.
    const wa = item.intake?.channel === 'whatsapp'
    const said = wa ? String(p.notes || inp.notes || '').trim() : ''
    const title = pr.address || pr.cadastral_code || (said ? (said.length > 90 ? said.slice(0, 89) + '…' : said) : '')
      || item.intake?.raw_text?.slice(0, 40) || t('desk_entry_property_fallback')
    const bits = []
    // Same bold-price header treatment as scraped listing cards (2026-07-17 card redesign).
    const price = priceOf(lst.price, lst.currency)
    // the kind in the Desk's language (it printed the raw code: "house", "land")
    if (pr.kind) bits.push(kindLabel(pr.kind) || pr.kind)
    if (wa && lst.intent) bits.push(intentLabel(lst.intent) || lst.intent)
    if (wa && inp.owner?.phone) bits.push(inp.owner.phone)
    return { title, price, subtitle: bits.join(' · ') || null }
  }
  if (k === 'listing_readonly' || k === 'scraped_listing_readonly') {
    const l = item.listing || {}
    // The city is the address's own first comma segment. The HOME city stays stripped (it is
    // the same word on ~91% of cards); an out-of-town one is KEPT and leads the title — see
    // cityForDisplay for why the source is the ad's own text and not the resolved
    // district.city, and for the bare-district first segment it protects.
    // Owner-reported 2026-07-27: Rustavi leads read as if they were Tbilisi.
    const { city: cityLabel, rest: addressRest } = cityForDisplay(l.address, districts)
    // District ALWAYS leads the address (2026-07-18 user rule): scraped address_texts are
    // inconsistent — some carry a district/raion segment ("ვაკე-საბურთალო, ყაზბეგის..."),
    // some are bare street+number. Prepend the registry name (locale-aware; the server's
    // l.district is English-only) unless the address already names it in either alphabet.
    const distRow = l.district_code ? districts.find(d => d.code === l.district_code) : null
    const distName = distRow
      ? (getLanguage() === 'ka' ? (distRow.name_ka || distRow.name_en) : (distRow.name_en || distRow.name_ka))
      : (l.district || null)
    const street = addressRest || l.address || ''
    const streetLc = street.toLowerCase()
    const distAlreadyNamed = (distRow ? [distRow.name_ka, distRow.name_en] : [distName])
      .filter(Boolean).some(n => streetLc.includes(n.toLowerCase()))
    // City → district → street, general to specific. The city is folded INTO `title` (not
    // handed out as a separate field for each surface to place) so every consumer of
    // summarize().title gets it for free and none of them can drift: the map tooltip, the
    // lineage modal header, the compact list row, the match rows. `titleCity`/`titleRest`
    // are the same string pre-split, for the card face that wants to weight the city.
    // ⚠️ The city leads on purpose. Three of those surfaces render the title with `truncate`
    // (single line, no wrap), so on a narrow column the LAST segment is what gets eaten. For
    // an out-of-town listing, losing the street tail is survivable; silently reading as a
    // local one is not.
    const streetPart = street
      ? (distName && !distAlreadyNamed ? `${distName}, ${street}` : street)
      : (distName || t('desk_entry_listing_fallback'))
    const title = cityLabel ? `${cityLabel}, ${streetPart}` : streetPart
    // Inherited-value muting (2026-07-05): a reshare with no posted_address of its own shows
    // the ORIGIN's address (server coalesce), and a bare-created reshare's price came from the
    // origin (meta.price_inherited_from_origin). Both render muted so the agent can tell "set
    // at posting" from "inherited from the source". address_inherited alone isn't enough —
    // a plain scraped source's property address IS its own, so gate on is_reshare.
    const titleMuted = !!(l.is_reshare && l.address_inherited && (addressRest || l.address))
    const bits = []
    if (l.intent) bits.push(intentLabel(l.intent))
    // Price is the card's dominant element (2026-07-17 card redesign — eBay-style hierarchy:
    // the agent's first scanning question is "in range?"), so it leaves the facts line and
    // renders bold in the header via `price` below. "/mo" ("/d") doubles as the rent-vs-sale
    // signal that survives even when the intent word is skimmed past.
    const price = (() => {
      const m = priceOf(l.price, l.currency,
        l.intent === 'rent' ? '/mo' : l.intent === 'daily_rent' ? '/d' : '')
      // "ფასი შეთანხმებით" — the AD SAID so (server flag), which is a different fact from
      // "we have no price". Before this the two were pixel-identical: formatMoney(null)
      // returns null, so slotPrice rendered NOTHING and an ss listing that deliberately
      // offers price-on-request looked like a broken capture (2026-07-24, owner-caught —
      // ss is the portal with this mode, and it states it by publishing no price at all).
      // ⚠️ Gate on price_on_request ONLY, never on `!l.price`: a genuine capture failure
      // must keep rendering blank, or every gap becomes a claim about the owner's intent.
      // Same words BusinessSection already uses for its own price_on_request listings.
      if (!m && l.price_on_request) return { text: t('desk_price_on_request'), approx: false, observed: null }
      // Developer card (core 1663): one card per floor stack — the face states the price RANGE across its floors.
      const dc = l.dev_card
      if (dc && dc.price_min != null && dc.price_max != null && +dc.price_max > +dc.price_min) {
        const a = priceOf(dc.price_min, dc.price_currency), b = priceOf(dc.price_max, dc.price_currency)
        if (a && b) {
          const unit = b.text.match(/\s\S+$/)?.[0], lo = unit && a.text.endsWith(unit) ? a.text.slice(0, -unit.length) : a.text
          return { ...b, text: `${lo} – ${b.text}`, approx: false, observed: null }
        }
      }
      // price per m² (owner 2026-10-24: "add per square metre price on desk cards"): the sale price over the building's
      // area (land: the plot, as core 3872's filter divides). Through priceOf, so it follows the agent's display currency
      // like the headline. Sale only — a monthly rent per m² is not a figure anyone compares here.
      const ppmArea = Number(l.kind === 'land' ? (l.land_area_sqm ?? l.area_sqm) : l.area_sqm)
      const ppm = m && l.intent !== 'rent' && l.intent !== 'daily_rent' && +l.price > 0 && ppmArea > 0
        ? priceOf(+l.price / ppmArea, l.currency, '/მ²')?.text ?? null : null
      return m && {
        ...m,
        muted: !!l.price_inherited,
        history: l.price_history || null,   // crawler-observed moves → hover badge (2026-07-20)
        ppm,
      }
    })()
    // kindLabel, never the raw token (labels.js is THE display dictionary, 2026-07-23):
    // the card face pushed `l.kind` verbatim, so a Georgian facts line read
    // "ქირავდება · apartment · 2 ოთ." — the ONE English word in it, and the platform's
    // normalized enum leaking to screen. Owner-caught 2026-08-03.
    if (l.kind) bits.push(kindLabel(l.kind))
    if (l.rooms != null) bits.push(tLog('desk_entry_rooms_abbrev', { rooms: l.rooms }))
    // Bedrooms on the face (owner 2026-08-03): it is what a client actually asks for and
    // what bedroomsSet filters on, so the card must state it rather than make the agent
    // open the listing. Always when recorded — see the factTokens note on why "only when
    // it differs from rooms" is the wrong rule.
    if (l.bedrooms != null) bits.push(tLog('desk_entry_bedrooms_abbrev', { bedrooms: l.bedrooms }))
    const dcard = l.dev_card
    if (dcard && dcard.area_min != null && +dcard.area_max > +dcard.area_min) bits.push(tLog('desk_entry_area_abbrev', { area: `${+dcard.area_min}–${+dcard.area_max}` }))
    else if (l.area_sqm != null) bits.push(tLog('desk_entry_area_abbrev', { area: l.area_sqm }))
    // The PLOT (0226 fact, filterable since v29). Shown only when it says something the
    // building figure does not: for land the two are the same scraped number, so printing
    // both reads as a duplicate. Measured live 2026-07-31 — every one of 119 houses
    // carrying both had DIFFERENT values (311 m² on a 1,000 m² plot), which is exactly the
    // case a card must not hide. ⚠️ A filter the card cannot show reads as broken: the
    // land-area control ships in the same commit as this line, deliberately.
    if (l.land_area_sqm != null && l.land_area_sqm !== l.area_sqm) {
      bits.push(tLog('desk_entry_land_area_abbrev', { area: l.land_area_sqm }))
    }
    // floors of the stack, and — like a resale card's "14/17" — how tall the building is when the developer shows it
    if (dcard && dcard.floor_min != null && dcard.floor_max > dcard.floor_min) bits.push(l.floors_total != null
      ? tLog('desk_entry_floor_range_of', { min: dcard.floor_min, max: dcard.floor_max, total: l.floors_total })
      : tLog('desk_entry_floor_range', { min: dcard.floor_min, max: dcard.floor_max }))
    else if (l.floor != null) bits.push(l.floors_total != null ? tLog('desk_entry_floor_position', { floor: l.floor, total: l.floors_total }) : tLog('desk_entry_floor_only', { floor: l.floor }))
    // "N left of M": the developer's stock at this position (available + reserved over every floor of the stack)
    if (dcard && dcard.total > 1) bits.push(tLog('desk_dev_left_of', { left: (dcard.available || 0) + (dcard.reserved || 0), total: dcard.total }))
    // total 0 = the developer lists only what is still on offer (Archi): the stack's size is unknown, so no "of M"
    else if (dcard && !dcard.total && (dcard.available || 0) + (dcard.reserved || 0) > 1) bits.push(tLog('desk_dev_on_sale', { n: (dcard.available || 0) + (dcard.reserved || 0) }))
    if (l.condition) bits.push(l.condition)
    // People ride on their OWN line, not in `bits` (owner ruling 2026-07-25): two names
    // ("დასკრაპა: X · განცხ. ავტორი: Y") are longer than every fact combined, and appended to
    // the facts they pushed a phone card's facts line to 6–7 wrapped rows. Kept on the face —
    // whose ad this is and who captured it is exactly what a poaching check needs — but as one
    // clamped muted line the card can budget for. Plain strings (tLog returns a string here),
    // so the caller can also hand the joined text to a title tooltip.
    const people = []
    if (k === 'listing_readonly') {
      // Reshare — the agent's own posted link; author IS our agent.
      if (l.author_name) people.push(tLog('desk_entry_by_agent', { agent: l.author_name }))
    } else {
      // Scraped source — author is whoever posted the ad on the platform (usually another
      // agency/owner, honest provenance).
      //
      // "Who put this in the system" LEFT this line on 2026-08-03 (owner: "we don't need two
      // same stating") — it now lives in the provenance caption below, where the generic word
      // "saved" used to sit saying nothing. Two consequences, both deliberate:
      //   • no `desk_entry_scraped_by` token here — one fact, one place;
      //   • when the ad's author IS the capturer (an agent's own posting saved back in, the
      //     "Luka Bigvava · Luka Bigvava" card that prompted this), the caption already names
      //     them and this token is dropped too. Compared by PARTY ID, never by name: 18 live
      //     parties are called "irakli" and a name match would silently merge them.
      const authorIsCapturer = !!(l.author_party_id && l.scraped_by_party_id
        && l.author_party_id === l.scraped_by_party_id)
      if (l.author_name && !authorIsCapturer) people.push(tLog('desk_entry_ad_by', { agent: l.author_name }))
    }
    // bits may contain JSX — interleave separators instead of join().
    return {
      title, titleCity: cityLabel, titleRest: streetPart, titleMuted, price,
      subtitle: bits.length ? bits.flatMap((b, i) => i ? [' · ', b] : [b]) : null,
      people: people.length ? people.join(' · ') : null,
    }
  }
  return { title: item.intake?.raw_text?.slice(0, 60) || '—', subtitle: null }
}

// human_edited comes from the proposal's own JSONB payload (set by this file) — no
// server/schema change needed for the badge. glow: 'amber' (AI's confident pick this
// paste) | 'blue' (a possible match — hint only) | null — visual only now; the actual
// click routing (continue draft vs. open edit modal) lives in IntakeTab's handleCardClick,
// one level up, since it needs state (aiCreateCmd, candidates, viewings) this component
// doesn't have. `onOpen` is that one router, passed down untouched.
// `thread`: { count, expanded, onToggle } when this item heads a group of >1 events on the
// same demand — the toggle reveals the rest (see groupHistory / HistoryGroup below). Nothing
// here merges data; it only decides what's visible.

// ListingGallery + splitCityFromAddress + osm tile/approx-location helpers —
// moved to ./desk/ListingGallery.jsx (plan §3 decomposition, verbatim).

// ─── Map view (2026-07-06) — one pin per entry, same precise-lat/lng-only rule ListingGallery
// uses for its own map slide (no on-the-fly approximate geocoding here — that's a per-card
// network call, fine for one card's trailing slide, way too many requests for a whole page of
// pins at once). Entries with no lat/lng just don't get a pin; nothing else changes their status.
// entryCoords moved to ../../lib/deskFeed.js (with its comments); re-exported above.
// A group's pin is whichever member actually has coords — the head is usually right (newest),
// but a cascade's origin ad or an older amend may be the one carrying lat/lng.
function groupCoords(group) {
  for (const item of group.items) {
    const c = entryCoords(item)
    if (c) return c
  }
  return null
}
// Cached per color so repeated markers of the same platform share one divIcon instance.
const pinColorIcons = new Map()
function coloredPinIcon(hex) {
  if (pinColorIcons.has(hex)) return pinColorIcons.get(hex)
  // drop-shadow + a thick white outline so a pin never disappears into the OSM tile palette —
  // a mid-gray agent pin (see AGENT_PIN_COLOR) was invisible against gray streets/buildings
  // before this (2026-07-07: "coords are there but the pin emoji is missing"). SVG rendered
  // fine; it just blended in.
  const icon = L.divIcon({
    className: '',
    html: `<svg width="26" height="42" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg" style="filter:drop-shadow(0 2px 2px rgba(0,0,0,.45));display:block">
      <path d="M12.5 0C5.6 0 0 5.6 0 12.5c0 9.4 12.5 28.5 12.5 28.5S25 21.9 25 12.5C25 5.6 19.4 0 12.5 0z" fill="${hex}" stroke="#fff" stroke-width="2.5"/>
      <circle cx="12.5" cy="12.5" r="4.5" fill="#fff"/>
    </svg>`,
    iconSize: [26, 42], iconAnchor: [13, 42], popupAnchor: [0, -36],
  })
  pinColorIcons.set(hex, icon)
  return icon
}
// Deep indigo (not gray) so an agent's own, not-yet-on-a-platform property stands out AND stays
// visually distinct from the three platform colors (orange/blue/green). The old #6b7280 gray blended
// straight into the map (see coloredPinIcon comment).
const AGENT_PIN_COLOR = '#4338ca'
function pinIconFor(source) {
  const key = source ? platformKey(source) : null
  return coloredPinIcon(key ? PIN_COLOR[key] : AGENT_PIN_COLOR)
}
// The whole-corpus dot (B307): deliberately NOT the teardrop — loaded groups keep their
// rich colored pins with tooltips and full card handlers; corpus dots are the book's
// background layer, and drawing 3,400 of them in the same costume as the 130 loaded ones
// would erase the difference between "fetched, card ready" and "known, card on click".
const CORPUS_DOT = L.divIcon({
  className: '',
  html: `<div style="width:12px;height:12px;border-radius:50%;background:#4338ca;border:2px solid #fff;box-shadow:0 1px 2px rgba(0,0,0,.4)"></div>`,
  iconSize: [12, 12], iconAnchor: [6, 6],
})
// ── Corpus-dot hover briefs (B307c, 2026-08-05) ──────────────────────────────────────
// A corpus dot ships [id, lat, lng] and NOTHING else — that is precisely what makes 29,303
// of them affordable (card data for the same set is ~60 MB). The cost is that a dot is
// anonymous: until this, the only way to learn what one was, was to open its card. So the
// hover asks the server for the few ids a mouse actually crosses, batched.
//
// MODULE-scoped, like the pin pool itself: a session's answers survive closing and reopening
// the map, and a re-render must not throw away what was already paid for.
// ⚠️ A SUCCESSFUL miss is cached (null = "core has no such listing", a permanent answer);
// a FAILED request is not (a network blip must not poison that dot for the whole session).
const briefMem = new Map()       // listing_id → brief | null
// TWO structures, and the split is load-bearing. `briefWaiting` is every id that has been
// asked for and not yet answered — QUEUED OR IN FLIGHT — and is what stops a second hover
// re-asking. `briefQueue` is only the not-yet-sent ones, and is what a flush drains.
// ⚠️ One map for both was the first version, and it re-sent the whole in-flight set on every
// subsequent flush: measured in the harness, sweeping 15 dots sent 45 ids across 5 batches,
// because ids stay in `briefWaiting` until their response lands 400ms later. Correct output,
// triple the network — the kind of bug that only shows up under a fast mouse.
const briefWaiting = new Map()   // listing_id → [cb]   (queued OR in flight)
const briefQueue = new Set()     // listing_id          (not yet sent)
let briefTimer = null
const BRIEF_BATCH = 40           // must match BRIEF_MAX server-side (nginx 8KB request line)
function flushBriefs() {
    briefTimer = null
    const ids = [...briefQueue].slice(0, BRIEF_BATCH)
    if (!ids.length) return
    for (const id of ids) briefQueue.delete(id)
    const settle = (id, brief, remember) => {
        if (remember) briefMem.set(id, brief)
        const cbs = briefWaiting.get(id) || []
        briefWaiting.delete(id)
        cbs.forEach(cb => { try { cb(brief) } catch { /* a dead marker's callback */ } })
    }
    getListingBriefs(ids)
        .then(list => {
            const got = new Map((list || []).map(b => [b.id, b]))
            for (const id of ids) settle(id, got.get(id) || null, true)
        })
        .catch(() => { for (const id of ids) settle(id, null, false) })
        // Ids past the cap, or hovered while this batch was in flight, go out immediately —
        // a hover that arrived 5ms late must not wait for the next mouse move to be sent.
        .finally(() => { if (briefQueue.size && !briefTimer) briefTimer = setTimeout(flushBriefs, 0) })
}
// The translated half of briefLabel, resolved at call time so a language switch takes effect
// on the next hover without rebuilding a single marker.
const briefLabels = () => ({
    unknown: t('desk_map_pin_unknown'),
    area: area => tLog('desk_entry_area_abbrev', { area }),
    rooms: rooms => tLog('desk_entry_rooms_abbrev', { rooms }),
})
const pinTooltip = (brief, districts, money) => briefLabel(brief, districts, money, formatMoney, briefLabels())
function requestBrief(id, cb) {
    if (briefMem.has(id)) { cb(briefMem.get(id)); return }
    const q = briefWaiting.get(id)
    if (q) { q.push(cb); return }
    briefWaiting.set(id, [cb])
    briefQueue.add(id)
    // Coalescing window: crossing a cluster fires dozens of mouseovers in under a second, and
    // one request for forty of them is the difference between this being free and being a
    // request storm. 90ms is below the ~150ms a deliberate hover dwells before it wants an answer.
    if (!briefTimer) briefTimer = setTimeout(flushBriefs, 90)
}
// 🗺️ THE MAP CAN NOW BE ASKED A QUESTION, not only answer one (2026-08-29). Until today every
// pin on it was OUTPUT: the map drew where the results were and could not narrow them. Two
// filters the server has always been able to answer had no way in.
//   • PROXIMITY had exactly one door — clicking a named place in the search box — so an agent
//     could search "near Vazisubani metro" but not near the corner they were standing on, or
//     near a client's office, or near anywhere without a gazetteer entry.
//   • AREA had none at all: a client's drawn search polygon has existed since 0041 on the DEMAND
//     side, and AreaPreview below has been RENDERING one for a year, while listing search had no
//     way to ask "inside this shape".
// ⚠️ DRAWING IS A COMMIT, and the commit belongs to the caller. This component owns the geometry
// and the gestures; it never decides what a shape MEANS. onNear/onPolygon hand the value up to
// the same committed-filter state the panel and the URL already share, so a shape drawn here is
// indistinguishable from one restored from a link.
const EMPTY_MAP_POLYGONS = []
export function EntriesMapView({ groups, districts, features, onCardClick, corpusPins, corpusPinsFailed = false, onPinClick, expanded, onToggleExpand,
                                 near = null, onNear = null, polygons = EMPTY_MAP_POLYGONS, polygon = null, onPolygon = null, onRemovePolygon = null }) {
  // null | 'point' | 'polygon'. Held here, not lifted: a half-drawn shape is not a filter, and
  // publishing it upward would make every vertex click a committed search.
  const [drawMode, setDrawMode] = useState(null)
  const [draft, setDraft] = useState([])
  // `polygon` keeps the render harness and any older embed compatible while the Desk now owns
  // the multi-area `polygons` contract.
  const committedPolygons = useMemo(() => polygons.length ? polygons : (Array.isArray(polygon) ? [polygon] : EMPTY_MAP_POLYGONS),
    [polygons, polygon])
  const drawModeRef = useRef(drawMode); drawModeRef.current = drawMode
  const draftRef = useRef(draft); draftRef.current = draft
  const strokeRef = useRef(null)
  const strokeLayerRef = useRef(null)
  const suppressMapClickRef = useRef(0)
  const mapEl = useRef(null), mapRef = useRef(null), layerRef = useRef(null)
  const markersRef = useRef(new Map())   // marker key → { marker, spec } (src/lib/mapMarkers.js)
  // fitBounds ONCE per mount (owner 2026-08-05, map-view pass): the feed loads in chunks,
  // `pins` re-derives per chunk, and re-fitting on each arrival yanked the viewport every
  // few seconds for the whole load — panning or zooming mid-load was fighting the map.
  // Markers still update per chunk (cheap, clustered); only the CAMERA is claimed once.
  const fitDoneRef = useRef(false)
  // Display currency for hover briefs, read through a REF at hover time rather than closed
  // over by the marker effect: a currency toggle must not rebuild 29k markers, and a tooltip
  // lives for the length of one hover, so the next one already speaks the new unit.
  const money = useMoney()
  const moneyRef = useRef(money)
  moneyRef.current = money
  const pins = useMemo(() => groups
    .map(group => ({ group, coords: groupCoords(group) }))
    .filter(p => p.coords),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groups])
  // Corpus dots minus anything a loaded group already draws (by listing id): a loaded row's
  // teardrop outranks its own background dot, or every fetched property renders twice at
  // one coordinate and the cluster counts double-speak.
  const bgPins = useMemo(() => {
    if (!Array.isArray(corpusPins) || !corpusPins.length) return []
    const loadedIds = new Set()
    for (const g of groups) for (const it of g.items) {
      const lid = it.listing?.listing_id || it.result?.listing_id
      if (lid) loadedIds.add(lid)
    }
    return corpusPins.filter(p => Array.isArray(p) && p.length >= 3 && !loadedIds.has(p[0]))
  }, [corpusPins, groups])

  useEffect(() => {
    if (mapRef.current || !mapEl.current) return
    const map = L.map(mapEl.current).setView([41.715, 44.79], 12)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '© OpenStreetMap' }).addTo(map)
    mapRef.current = map
    // Leaflet sizes its canvas once at init and never notices the container changing size on
    // its own (e.g. the ⤢ enlarge toggle hiding the side panels) — without this it keeps
    // rendering at the old width, leaving the newly-freed space blank.
    const ro = new ResizeObserver(() => map.invalidateSize())
    ro.observe(mapEl.current)
    // map.remove() takes the cluster layer with it; forget it so a remount builds a fresh one.
    return () => { ro.disconnect(); map.remove(); mapRef.current = null; layerRef.current = null; markersRef.current = new Map() }
  }, [])

  // 2026-10-23 (audit, Desk map performance): markers are kept by IDENTITY — see src/lib/mapMarkers.js.
  // The layer was removed and rebuilt on every change of pins/bgPins, so one landed page (its groups
  // promoted from corpus dot to teardrop) re-created every remaining dot. Now only the markers whose key
  // or facts changed are touched. Handlers read refs at event time because a marker outlives the render
  // that created it: a click opens the CURRENT group, a hover speaks the current districts and currency.
  const onCardClickRef = useRef(onCardClick); onCardClickRef.current = onCardClick
  const onPinClickRef = useRef(onPinClick); onPinClickRef.current = onPinClick
  const districtsRef = useRef(districts); districtsRef.current = districts
  const markerOps = useMemo(() => leafletMarkerOps({
    L, pinIcon: pinIconFor, dotIcon: CORPUS_DOT,
    onPin: group => onCardClickRef.current?.(group),
    // A dot's click opens the card by id (the row isn't loaded, so there is no group to hand over).
    onDot: id => onPinClickRef.current?.(id),
    // Hover = "what is this?" (B307c). The tooltip is bound on FIRST hover, never at creation: 29k
    // pre-bound tooltip objects is real memory for something the agent will point at a dozen of. A
    // cached brief renders on the first frame; an uncached one shows '…' and swaps in place when the
    // batch lands, so the hover never blocks on the network.
    onDotHover: (marker, id) => {
      const known = briefMem.has(id)
      if (!marker.getTooltip()) {
        marker.bindTooltip(known ? pinTooltip(briefMem.get(id), districtsRef.current, moneyRef.current) : '…',
          { direction: 'top', offset: [0, -8] })
      }
      marker.openTooltip()
      if (known) return
      requestBrief(id, brief => {
        // The pointer may have left, or the marker may have been removed, before this landed —
        // setting content on a detached marker is harmless, showing it is not.
        if (!marker.getTooltip()) return
        marker.setTooltipContent(pinTooltip(brief, districtsRef.current, moneyRef.current))
      })
    },
  }), [])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    // Corpus dots join the SAME cluster layer — two layers would draw two overlapping cluster
    // bubbles with two half-counts at every zoom. One layer for the life of the map.
    if (!layerRef.current) {
      layerRef.current = L.markerClusterGroup({ maxClusterRadius: 60 })
      layerRef.current.addTo(map)
      markersRef.current = new Map()
    }
    const specs = markerSpecs(pins, bgPins, group => summarize(group.items[0], districts, features).title)
    syncMarkerLayer(layerRef.current, markersRef.current, specs, markerOps)
    if ((pins.length || bgPins.length) && !fitDoneRef.current) {
      fitDoneRef.current = true
      const all = [...pins.map(p => [p.coords.lat, p.coords.lng]), ...bgPins.map(([, lat, lng]) => [lat, lng])]
      map.fitBounds(L.latLngBounds(all), { padding: [40, 40], maxZoom: 16 })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pins, bgPins])

  // ── the drawing gesture ────────────────────────────────────────────────────────────────
  // ⚠️ ONE handler bound ONCE, reading mode through refs. Re-binding a Leaflet listener whenever
  // the mode changes leaks handlers unless every path removes the old one, and a duplicated
  // click handler on a polygon draft silently doubles every vertex.
  useEffect(() => {
    const map = mapRef.current
    if (!map || (!onNear && !onPolygon)) return
    const onClick = e => {
      if (Date.now() < suppressMapClickRef.current) return
      const mode = drawModeRef.current
      if (mode === 'point' && onNear) {
        // The radius is CARRIED OVER from whatever is committed, so moving the point does not
        // silently reset a distance the agent chose. A first-ever point takes the 1000 m the
        // transport already defaults to, so the map and the URL agree from the first click.
        onNear({ id: null, lat: +e.latlng.lat.toFixed(6), lng: +e.latlng.lng.toFixed(6),
                 radiusM: near?.radiusM || 1000 })
        setDrawMode(null)
      } else if (mode === 'polygon') {
        setDraft(d => [...d, [+e.latlng.lat.toFixed(6), +e.latlng.lng.toFixed(6)]])
      }
    }
    // Finishing a polygon on double-click is Leaflet's own idiom, and the zoom it would
    // otherwise trigger has to go with it or the shape closes AND the map jumps.
    const onDbl = () => {
      if (drawModeRef.current !== 'polygon') return
      const ring = draftRef.current
      if (ring.length >= 3 && onPolygon) onPolygon(ring)
      setDrawMode(null); setDraft([])
    }
    map.on('click', onClick); map.on('dblclick', onDbl)
    return () => { map.off('click', onClick); map.off('dblclick', onDbl) }
  }, [onNear, onPolygon, near?.radiusM])

  // Double-click zoom is disabled ONLY while drawing a polygon — outside that it is the
  // ordinary way to zoom a map and taking it away permanently would be a worse trade.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (drawMode === 'polygon') {
      map.doubleClickZoom.disable()
      map.dragging.disable()
      if (map.touchZoom) map.touchZoom.disable()
    } else {
      map.doubleClickZoom.enable()
      map.dragging.enable()
      if (map.touchZoom) map.touchZoom.enable()
    }
    if (map.getContainer()) map.getContainer().style.cursor = drawMode ? 'crosshair' : ''
  }, [drawMode])

  // Zillow-style mobile trace: while Draw is active, one continuous finger stroke becomes the
  // draft boundary. It is drawn directly into Leaflet during movement (no React render per
  // pointer event), sampled every ~6 screen pixels, then capped below the server's 200-corner
  // guard. Mouse users retain precise click-to-place corners and double-click-to-finish.
  useEffect(() => {
    const map = mapRef.current, el = map?.getContainer()
    if (!map || !el || drawMode !== 'polygon') return
    el.style.touchAction = 'none'
    const point = e => map.mouseEventToContainerPoint(e)
    const latlng = e => map.mouseEventToLatLng(e)
    const down = e => {
      if (e.pointerType === 'mouse' || !e.isPrimary) return
      e.preventDefault(); e.stopImmediatePropagation()
      el.setPointerCapture?.(e.pointerId)
      const px = point(e), ll = latlng(e)
      strokeRef.current = [{ px, ll }]
      strokeLayerRef.current?.remove()
      strokeLayerRef.current = L.polyline([ll], { color: '#38bdf8', weight: 4, opacity: 0.95 }).addTo(map)
      setDraft([])
    }
    const move = e => {
      const stroke = strokeRef.current
      if (!stroke || e.pointerType === 'mouse') return
      e.preventDefault(); e.stopImmediatePropagation()
      const px = point(e), last = stroke[stroke.length - 1].px
      if (px.distanceTo(last) < 6) return
      const ll = latlng(e)
      stroke.push({ px, ll })
      strokeLayerRef.current?.addLatLng(ll)
    }
    const finish = e => {
      const stroke = strokeRef.current
      if (!stroke || e.pointerType === 'mouse') return
      e.preventDefault(); e.stopImmediatePropagation()
      suppressMapClickRef.current = Date.now() + 500
      const step = Math.max(1, Math.ceil(stroke.length / 80))
      const sampled = stroke.filter((_, i) => i % step === 0 || i === stroke.length - 1)
        .map(({ ll }) => [+ll.lat.toFixed(6), +ll.lng.toFixed(6)])
      setDraft(sampled.length >= 3 ? sampled : [])
      strokeRef.current = null
      strokeLayerRef.current?.remove(); strokeLayerRef.current = null
    }
    el.addEventListener('pointerdown', down, { capture: true, passive: false })
    el.addEventListener('pointermove', move, { capture: true, passive: false })
    el.addEventListener('pointerup', finish, { capture: true, passive: false })
    el.addEventListener('pointercancel', finish, { capture: true, passive: false })
    return () => {
      el.style.touchAction = ''
      el.removeEventListener('pointerdown', down, true)
      el.removeEventListener('pointermove', move, true)
      el.removeEventListener('pointerup', finish, true)
      el.removeEventListener('pointercancel', finish, true)
      strokeRef.current = null
      strokeLayerRef.current?.remove(); strokeLayerRef.current = null
    }
  }, [drawMode])

  // ── what is committed, drawn ───────────────────────────────────────────────────────────
  // ⚠️ A COMMITTED SHAPE IS ALWAYS VISIBLE, whether it was drawn here or restored from a URL.
  // A filter narrowing the results with nothing on the map to show for it reads as the map
  // being broken — the same defect class as a facet whose number does not match its click.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const shapes = []
    if (near?.lat != null && near?.lng != null) {
      shapes.push(L.circle([near.lat, near.lng], { radius: near.radiusM || 1000,
        color: '#2563eb', weight: 1, fillOpacity: 0.08 }).addTo(map))
      shapes.push(L.circleMarker([near.lat, near.lng], { radius: 4, color: '#2563eb',
        fillColor: '#2563eb', fillOpacity: 1 }).addTo(map))
    }
    committedPolygons.forEach((polygon, index) => {
      if (!Array.isArray(polygon) || polygon.length < 3) return
      const shape = L.polygon(polygon, { color: '#7c3aed', weight: 2, fillOpacity: 0.08 }).addTo(map)
      shape.bindTooltip(`${t('desk_facet_map_area')} ${index + 1}`, { sticky: true })
      shapes.push(shape)
    })
    if (draft.length) {
      shapes.push(L[draft.length >= 3 ? 'polygon' : 'polyline'](draft,
        { color: '#7c3aed', weight: 2, dashArray: '4 3', fillOpacity: 0.05 }).addTo(map))
      draft.forEach(pt => shapes.push(L.circleMarker(pt, { radius: 3, color: '#7c3aed',
        fillColor: '#fff', fillOpacity: 1 }).addTo(map)))
    }
    return () => shapes.forEach(x => x.remove())
  }, [near?.lat, near?.lng, near?.radiusM, committedPolygons, draft])

  return (
    // ⚠️ `isolate` HERE, not only on the map div — owner-reported 2026-08-15: opening a listing
    // from a map pin drew the ⤢ enlarge button ON TOP of the modal, overlapping its ✕.
    // The inner `isolate` below boxes LEAFLET's panes. The enlarge button is a SIBLING of that
    // div, so its `z-[1000]` was never boxed by anything and competed in the ROOT stacking
    // context — where the card modals live at z-50, in a portal to <body>. A z-1000 element
    // anywhere on the page beats them, and no amount of z-index on the modal is the answer:
    // whoever raises theirs next wins, and z-index wars are how this ends up unfixable.
    // Measured in a browser rather than reasoned about: with a z-1000 button in a plain
    // `relative` wrapper, elementFromPoint over the button returns the BUTTON through a z-50
    // fixed overlay; add `isolation: isolate` to that wrapper and the same point returns the
    // OVERLAY. The button still paints above the map, because inside this new stacking context
    // z-1000 still beats the map div's auto.
    <div className="relative isolate">
      {/* isolate: Leaflet's own panes carry z-index up to 700 (marker/popup panes) — without a
          stacking context of their own, that z-index is compared against the WHOLE page's, so
          a pin/tooltip painted straight through the Modal's z-50 overlay when a card was opened
          from a map pin. `isolate` boxes the map's z-index scale so nothing above it can escape. */}
      <div ref={mapEl} className="relative isolate w-full h-[60dvh] min-h-[360px] md:h-[70vh] rounded-xl border border-border" />
      {/* Enlarge/collapse (2026-07-09): a map control in the map's own top-right corner, not a
          toolbar button — Leaflet's own zoom control sits top-left and attribution bottom-right,
          so this corner is free. z-[1000] to clear Leaflet's own panes (up to z-700, boxed by
          the `isolate` above) and its control container (~z-1000). */}
      {onToggleExpand && (
        <button onClick={onToggleExpand}
          title={t(expanded ? 'desk_map_collapse_tooltip' : 'desk_map_expand_tooltip')}
          className="absolute top-3 right-3 z-[1000] w-11 h-11 md:w-8 md:h-8 flex items-center justify-center rounded-lg border border-border bg-card text-foreground shadow hover:bg-accent transition-colors">
          {expanded ? '⤡' : '⤢'}
        </button>
      )}
      {/* Drawing tools, stacked UNDER the enlarge button in the same free corner. Same z-[1000]
          and the same reason: Leaflet's own panes reach z-700 and its control container ~z-1000,
          and the `isolate` above boxes the whole scale so none of it escapes to the page.
          ⚠️ Rendered only when the caller passes a handler — on a surface with no filter state to
          write to, a drawing tool is a control that cannot do anything. */}
      {(onNear || onPolygon) && (
        <div className="absolute top-[4.25rem] md:top-14 right-3 z-[1000] flex flex-col gap-2 md:gap-1">
          {onNear && (
            <button onClick={() => { setDraft([]); setDrawMode(m => m === 'point' ? null : 'point') }}
              title={t('desk_map_point_tool')} aria-pressed={drawMode === 'point'}
              className={`w-11 h-11 md:w-8 md:h-8 flex items-center justify-center rounded-lg border shadow transition-colors ${
                drawMode === 'point' ? 'border-primary bg-primary/15 text-primary'
                                     : 'border-border bg-card text-foreground hover:bg-accent'}`}>📍</button>
          )}
          {onPolygon && (
            <button disabled={committedPolygons.length >= 10}
              onClick={() => { setDraft([]); setDrawMode(m => m === 'polygon' ? null : 'polygon') }}
              title={t(committedPolygons.length >= 10 ? 'desk_map_area_limit' : 'desk_map_area_tool')} aria-pressed={drawMode === 'polygon'}
              className={`w-11 h-11 md:w-8 md:h-8 flex items-center justify-center rounded-lg border shadow transition-colors ${
                drawMode === 'polygon' ? 'border-primary bg-primary/15 text-primary'
                                       : 'border-border bg-card text-foreground hover:bg-accent'} disabled:cursor-not-allowed disabled:opacity-40`}>⬠</button>
          )}
          {(near || committedPolygons.length > 0) && (
            <button onClick={() => { setDraft([]); setDrawMode(null); onNear && onNear(null); onRemovePolygon && onRemovePolygon(null) }}
              title={t('desk_map_clear_shapes')}
              className="w-11 h-11 md:w-8 md:h-8 flex items-center justify-center rounded-lg border border-border bg-card text-muted-foreground shadow hover:bg-accent transition-colors">✕</button>
          )}
        </div>
      )}
      {/* The instruction appears only while drawing. A permanent hint would be noise on a map
          that is usually just being read. */}
      {drawMode === 'point' && (
        <p className="absolute bottom-3 left-3 right-3 md:left-1/2 md:right-auto md:-translate-x-1/2 z-[1000] px-3 py-2 md:py-1 rounded-xl md:rounded-full border border-border bg-card/95 text-xs text-center text-foreground shadow">
          {t('desk_map_point_hint')}
        </p>
      )}
      {drawMode === 'polygon' && (
        <div className="absolute bottom-3 left-3 right-3 md:left-1/2 md:right-auto md:-translate-x-1/2 z-[1000] rounded-xl border border-border bg-card/95 p-2 shadow space-y-2 md:min-w-[360px]">
          <p className="text-xs text-center text-foreground">
            {t('desk_map_area_hint')}{draft.length > 0 && ` · ${draft.length}`}
          </p>
          <div className="grid grid-cols-3 gap-2">
            <button type="button" className="min-h-11 md:min-h-9 rounded-lg border border-border px-3 text-xs font-medium hover:bg-accent"
              onClick={() => { setDraft([]); setDrawMode(null) }}>{t('desk_map_draw_cancel')}</button>
            <button type="button" disabled={!draft.length}
              className="min-h-11 md:min-h-9 rounded-lg border border-border px-3 text-xs font-medium hover:bg-accent disabled:opacity-40"
              onClick={() => setDraft([])}>{t('desk_map_draw_clear')}</button>
            <button type="button" disabled={draft.length < 3}
              className="min-h-11 md:min-h-9 rounded-lg bg-primary px-3 text-xs font-semibold text-primary-foreground disabled:opacity-40"
              onClick={() => { if (draft.length >= 3 && onPolygon) onPolygon(draft); setDraft([]); setDrawMode(null) }}>
              {t('desk_map_draw_apply')}
            </button>
          </div>
        </div>
      )}
      {/* 2026-10-23 audit (map section): "no locations" read the LOADED pins only and showed over
          visible background dots; and a failed pin request left a partial map that said nothing. */}
      {(() => {
        const note = mapCoverageNote({ loadedPins: pins.length, backgroundPins: bgPins.length, pinsFailed: corpusPinsFailed })
        return note && (
          <p className="text-sm text-muted-foreground mt-2">
            {note === 'pins_failed' ? tLog('desk_map_pins_failed', { n: pins.length }) : t('desk_entry_no_locations')}
          </p>
        )
      })()}
    </div>
  )
}

// Mini preview of a client's drawn search area (requirement_criteria.search_polygon) — only
// rendered when one actually exists (most clients don't have one yet). Was a bare SVG shape with
// no map underneath it (the bug: "polygon seen but no map") — now a real OSM tile mosaic sized
// to the polygon's own footprint, with the shape overlaid in the SAME pixel space so it lines up
// exactly on the streets it actually covers, same tile server the pin thumbnails already use.
export function AreaPreview({ polygon, size = 'thumb' }) {
  const ring = polygon?.coordinates?.[0]
  // 4:3 to match ListingGallery 'large' (see its comment — 2026-07-06 restructure).
  const sizeClass = size === 'large' ? 'w-full aspect-[4/3]' : 'w-24 h-16'
  // The tile mosaic must fill the WHOLE preview box, not just the polygon's own bounding box —
  // the old version sized the map window to the bbox and centered it, which left everything
  // around a small polygon as blank gray (the bug: "polygon shows but not the full map").
  // The box's real pixel size is measured once (useLayoutEffect, no ResizeObserver — a slightly
  // stale size after a window resize only shifts which tiles are fetched, never breaks layout).
  const boxRef = useRef(null)
  const [box, setBox] = useState(null)
  useLayoutEffect(() => {
    const el = boxRef.current
    if (el) setBox({ w: el.offsetWidth || 96, h: el.offsetHeight || 64 })
  }, [size])
  const geo = useMemo(() => {
    if (!ring?.length || !box) return null
    // Zoom: the polygon should occupy ~85% of the box at most — visible with map context around it.
    const { zoom } = pickAreaZoom(ring, box.w * 0.85, box.h * 0.85)
    // Window: the FULL box, centered on the polygon bbox's center in pixel space.
    const lngs = ring.map(([lng]) => lng), lats = ring.map(([, lat]) => lat)
    const pNW = lngLatToPixel(Math.min(...lngs), Math.max(...lats), zoom)
    const pSE = lngLatToPixel(Math.max(...lngs), Math.min(...lats), zoom)
    const ox = (pNW.x + pSE.x) / 2 - box.w / 2   // window origin (top-left) in global pixel space
    const oy = (pNW.y + pSE.y) / 2 - box.h / 2
    const tiles = []
    for (let tx = Math.floor(ox / 256); tx * 256 < ox + box.w; tx++)
      for (let ty = Math.floor(oy / 256); ty * 256 < oy + box.h; ty++)
        tiles.push({ x: tx, y: ty, left: tx * 256 - ox, top: ty * 256 - oy })
    const points = ring.map(([lng, lat]) => {
      const px = lngLatToPixel(lng, lat, zoom)
      return `${(px.x - ox).toFixed(1)},${(px.y - oy).toFixed(1)}`
    }).join(' ')
    return { zoom, tiles, points, w: box.w, h: box.h }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(ring), box])
  if (!ring?.length) return null
  return (
    <div ref={boxRef} className={`relative ${sizeClass} rounded mb-2 overflow-hidden bg-muted shrink-0`}>
      {geo && (
        <div className="absolute inset-0">
          {geo.tiles.map(t => (
            <img key={`${t.x}-${t.y}`} src={`https://a.tile.openstreetmap.org/${geo.zoom}/${t.x}/${t.y}.png`} alt=""
              style={{ position: 'absolute', left: t.left, top: t.top, width: 256, height: 256 }} />
          ))}
          <svg width={geo.w} height={geo.h} className="absolute inset-0">
            <polygon points={geo.points} fill="#f59e0b" fillOpacity="0.25" stroke="#f59e0b" strokeWidth="2" />
          </svg>
        </div>
      )}
    </div>
  )
}
// B259 (owner ruling 2026-07-31): "Display the cluster truth first. Explain the matching
// evidence second." — the card keeps its own price; this line says which sibling matched.
// 🚫 Deliberately NOT the headline: promoting the matched price makes the card's primary
// number change with the query, which the ruling rejected outright as trust-destroying.
function MatchedEvidenceLine({ note }) {
  const money = useMoney()
  const l = note.listing
  // Same formatter, same display-currency conversion the card face uses — a matched price
  // shown in a different unit from the headline it sits under would be worse than silent.
  const m = formatMoney(l.price, l.currency, money.display, money.rate,
    { suffix: l.intent === 'rent' ? '/mo' : l.intent === 'daily_rent' ? '/d' : '' })
  const facts = [
    m?.text || null,
    l.area_sqm != null ? tLog('desk_entry_area_abbrev', { area: l.area_sqm }) : null,
    l.rooms != null ? tLog('desk_entry_rooms_abbrev', { rooms: l.rooms }) : null,
  ].filter(Boolean).join(' · ')
  if (!facts) return null
  return (
    <p className="text-xs text-muted-foreground/80 mt-0.5 truncate"
      title={t('desk_matched_member_title', { n: note.count, total: note.total })}>
      <span className="text-emerald-600 dark:text-emerald-400">✓</span>{' '}
      {note.count > 1
        ? tLog('desk_matched_member_multi', { n: note.count, facts })
        : tLog('desk_matched_member_one', { facts })}
    </p>
  )
}

// Corner chip, POSITION-FREE (2026-08-03). Same shape as OVERLAY_BADGE minus its `absolute`:
// the chip now sits inline in the footer's rail row on most cards, and only the fallback path
// wraps it in an absolutely-positioned span. Keeping the position out of the chip is what lets
// ONE node serve both placements — a chip that carries `absolute bottom-2 right-2` in its own
// class list silently ignores whatever container it is handed to.
const CORNER_CHIP = 'inline-flex items-center rounded-full px-2 py-0.5 text-xs whitespace-nowrap'

// ── The aligned state column (design 1c, chosen base) ───────────────────────
// Designer rule 2: "name left / value right / hairline between — every card puts the same
// field in the same place so a feed reads as a table." That is the whole point of moving the
// dates off the face: as chips they sat in a wrapping row whose order changed with content,
// so the same fact landed somewhere different on every card and the eye had to read instead
// of scan.
//
// Rule 1 (colour marks exceptions only) is enforced here by omission: every row below is
// muted, and the ONLY coloured value is a stale check. ⚠️ Amber at 7 days paints 57.5% of the
// live corpus today (25,845 of 44,978 active listings, measured 2026-08-04) — that is the
// crawler's debt showing through the card, deliberately, not a threshold to tune away. The
// fix is core/LISTING_FRESHNESS.md, not a repaint.
//
// Zero rows VANISH (designer, 6b) — never `0`, never `—`. A row that exists to say nothing
// happened is noise on every card in the feed.
// ⚠️ THE VALUE NEVER TRUNCATES — the LABEL does (owner-caught 2026-08-04: on a 224px column
// the long Georgian label pushed the owner's-ads dots out of the row, so myhome showed and
// korter and ss.ge were silently clipped. A value that quietly loses a platform is worse than
// a label that reads "მესაკუთრის განც…", because nothing on screen says a dot is missing).
// Exported (2026-08-05) so a category can fill the state slot with ITS OWN rows and still
// speak this grammar — name left, value right, hairline between. The alignment is the whole
// point of the column, so a surface that hand-rolls its own rows breaks the feed's one table.
// ⚠️ `wrap` exists because `shrink-0` is what makes the default value box overflow rather
// than fold (2026-08-05). A value that never shrinks sizes itself to its content, so any
// flex-wrap INSIDE it can never trigger — the meeting card's "when" row (a date plus a
// countdown, 201px) hung 26px past the edge of a 162px compact column until this. `wrap`
// swaps the no-shrink/no-break pair for `min-w-0`, which lets the box narrow and its content
// fold onto a second line. Wrapping is NOT truncating: the rule that the value never loses
// information still holds, it just spends a line instead of a pixel. Use it for prose-ish
// values; leave it off for anything that must stay on one line, like the platform dots.
export function StateRow({ label, children, title, wrap = false }) {
  return (
    <div className="flex items-center justify-between gap-2 py-1 border-t border-border/40 first:border-t-0" title={title}>
      <span className="text-xs text-muted-foreground min-w-0 truncate">{label}</span>
      <span className={`text-xs text-right tabular-nums ${wrap ? 'min-w-0' : 'shrink-0 whitespace-nowrap'}`}>{children}</span>
    </div>
  )
}
function StateBlock({ item, stackDates, platformChips, views, chain, chainOpen, onToggleChain }) {
  // ⚠️ A chip is the OWNER's ad only when it is the scraped source (`origin`). The rest are our
  // own postings — the chain's subject, and counting them here would say the owner advertised
  // on three portals when we did.
  const ownerChips = (platformChips || []).filter(c => c.origin)
  // ⚠️ ACTIVE AND ENDED ARE SPLIT BECAUSE THE FILTER SPLITS THEM (owner ruling 2026-09-02: "let it
  // show only active ads and show dead listings as ended along active / filter only active
  // listings for only 1 platform").
  //
  // `p_owner_platforms_*` counts `count(distinct l.source) ... where l.status = 'active'`. This row
  // counted every owner platform regardless of status, so the two answered different questions and
  // the card read as a contradiction: ticking "1 პორტალზე" returned cards showing two owner dots.
  // Measured live: of 26,730 clusters the filter calls one platform, 2,030 (7.6%) drew more dots
  // than that. Isolated by cause — 2,029 were STATUS and exactly 1 was authorship, which is why
  // this touches status alone and leaves the `origin` test (B652) exactly as it is.
  //
  // 📌 The ended ones are still SHOWN, per the ruling: a platform the owner has left is real
  // history. What changed is that they no longer wear the same shape as the live ones — they are
  // grouped, dimmed and NAMED. The dot grammar already greyed them and that was not enough,
  // because greyness reads as styling rather than as a claim about the world.
  const liveOwnerChips = ownerChips.filter(c => c.status === 'active')
  const endedOwnerChips = ownerChips.filter(c => c.status !== 'active')
  const days = ts => Math.floor((Date.now() - new Date(ts).getTime()) / 86400000)
  const checkedDays = stackDates?.checked ? days(stackDates.checked) : null
  const live = item.listing?.status === 'active'
  const postedKey = !stackDates?.createdIsPosted ? 'desk_entry_first_seen'
    : stackDates.postedKind === 'republished' || stackDates.postedKind === 'current_run' ? 'desk_entry_bumped'
    : 'desk_entry_posted'
  return (
    <div className="flex flex-col min-w-0">
      {item.listing?.status && (
        <div className="flex items-center gap-1.5 pb-1 text-xs">
          <span className={`w-1.5 h-1.5 rounded-full ${live ? 'bg-emerald-500' : 'bg-muted-foreground/50'}`} />
          <span className={live ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}>
            {t(live ? 'desk_state_active' : 'desk_state_closed')}
          </span>
        </div>
      )}
      {/* THE OWNER'S ads only — the label says whose they are, so our reposts must not be in
          it (owner-caught 2026-08-04: "it says owners ads but also shows relistings; relist
          already shown in chain"). `origin` is the scraped source ad; every non-origin chip is
          one of OUR postings, and those are the chain's subject. The row vanishes when the
          owner has none (a repost-only card), per the zero-rows rule.
          Each dot is a full PlatformChip in `dot` form (owner 2026-08-04: "on hover on those
          listings dots show what hover on pills did") — same popover, same ad list, same
          links; the pill row it replaced is gone from the face, not duplicated by it. */}
      {ownerChips.length > 0 && (
        <StateRow label={t('desk_state_owner_ads')}>
          <span className="inline-flex items-center gap-2 flex-wrap">
            {liveOwnerChips.map(c => {
              const pk = platformKey(c.source)
              return <PlatformChip key={c.id} dot c={c} pk={pk} label={pk ? PLATFORM_LABEL[pk] : c.source}
                propertyId={item.listing?.property_id || null}
                caption={c.origin ? t('desk_entry_scraped_listing_caption') : null} />
            })}
            {/* The ended group. It carries its own name so the row cannot be read as "the owner is
                on N platforms" when some of those platforms are finished — that reading is exactly
                what made the card contradict the "1 platform" filter.
                ⚠️ `flex-wrap` on the parent is load-bearing: this row lives in a 224px column that
                has overflowed before ("700 USD/n", harness 2026-08-04), and a word plus two dots is
                more than the old content. Wrapping is the honest failure; clipping is not. */}
            {endedOwnerChips.length > 0 && (
              <span className="inline-flex items-center gap-2 opacity-70" title={t('desk_state_closed')}>
                {liveOwnerChips.length > 0 && <span aria-hidden className="text-muted-foreground/40">·</span>}
                {endedOwnerChips.map(c => {
                  const pk = platformKey(c.source)
                  return <PlatformChip key={c.id} dot c={c} pk={pk} label={pk ? PLATFORM_LABEL[pk] : c.source}
                    propertyId={item.listing?.property_id || null}
                    caption={c.origin ? t('desk_entry_scraped_listing_caption') : null} />
                })}
                <span className="text-[10px] leading-none text-muted-foreground">
                  {t('desk_state_owner_ads_ended')}
                </span>
              </span>
            )}
            {/* The BARE number, not the formatted price: the headline three centimetres to the
                left already carries the currency and the /mo, and repeating them here overflowed
                a 224px column into "700 USD/n" (seen in the harness, 2026-08-04). */}
            {/* ⚠️ NO PRICE HERE (owner 2026-08-04). The headline carries it, with its currency
                and its /mo, three centimetres to the left — and the chain panel carries each
                posting's own. A bare repeat next to the dots read as a number soup ("1 1 ·
                102000") and stated nothing the card had not already said. */}
          </span>
        </StateRow>
      )}
      {stackDates?.created && (
        <StateRow label={t(`${postedKey}_label`)} title={cardDateTime(stackDates.created)}>{cardAge(stackDates.created)}</StateRow>
      )}
      {stackDates?.acquired && (
        <StateRow label={t('desk_entry_acquired_label')} title={cardDateTime(stackDates.acquired)}>{cardAge(stackDates.acquired)}</StateRow>
      )}
      {stackDates?.checked && (
        <StateRow label={t('desk_entry_checked_label')} title={cardDateTime(stackDates.checked)}>
          <span className={checkedDays > 7 ? 'text-amber-600 dark:text-amber-400 inline-flex items-center gap-1' : ''}>
            {checkedDays > 7 && <Icon name="clock" size={11} />}
            {cardAge(stackDates.checked)}
          </span>
        </StateRow>
      )}
      {views?.total > 0 && (
        /* Same tooltip the eye pill carried — who, when, how often. The pill is gone from the
           face on these cards, so this row is where that hover lives now. */
        <StateRow label={t('desk_state_seen')}
          title={(views.viewers || []).map(v => `${v.name || '?'} — ${v.last_at ? new Date(v.last_at).toLocaleString() : '?'}${v.count > 1 ? ` (×${v.count})` : ''}`).join('\n') || undefined}>
          {views.total}{views.mine?.last_at ? ` · ${t('desk_state_seen_me')}` : ''}
        </StateRow>
      )}
      {/* The chain's door, at the foot of the column (design 2b) — full width, so it reads as
          "there is more under this card" rather than as one more chip in a row. */}
      {chain && (
        <button onClick={e => { e.stopPropagation(); onToggleChain() }}
          className={`mt-2 flex items-center justify-between gap-2 w-full rounded-md border px-2 py-1.5 text-xs transition-colors ${
            chainOpen ? 'border-primary/50 bg-primary/5 text-foreground' : 'border-border text-muted-foreground hover:border-primary/40'}`}>
          <span className="inline-flex items-center gap-1.5">
            <Icon name="layers" size={12} />
            {t(chain.known ? 'desk_chain_title' : 'desk_chain_dupes_title')}
            {' · '}{chain.sources}{chain.reposts ? ` + ${chain.reposts}` : ''}
            {/* Whole-family reach (0084) rides the chain door, where the owner put it — it
                answers "how many agents are on this", which is the same question the door
                opens. ⚠️ NOT the chain's own agent count: a scope=mine feed loads only this
                agent's rows, so the server can count four while the panel can name one. */}
            {item.listing?.family_agent_count > 1 && (
              <span className="inline-flex items-center gap-0.5 text-muted-foreground"
                title={tLog('desk_entry_family_reach_tooltip', { agents: item.listing.family_agent_count, total: item.listing.family_listing_count ?? '?' })}>
                <Icon name="users" size={11} />×{item.listing.family_agent_count}
              </span>
            )}
          </span>
          <span>{chainOpen ? '▲' : '▼'}</span>
        </button>
      )}
    </div>
  )
}

function ChainRow({ chain, row, propertyId = null }) {
  // A facebook row is a whole posting SPREE (owner 2026-08-20) — its inline counts say how
  // far it reached. Fetched only when the panel is actually open (this component mounts on
  // expand), cached per property in fbStatsCache, so a page of cards costs nothing.
  const isFb = platformKey(row.source) === 'facebook'
  const [fbStats, setFbStats] = useState(null)
  useEffect(() => {
    if (!isFb || !propertyId) return
    let gone = false
    fbStatsFor(propertyId).then(rows => {
      if (gone) return
      const own = rows.find(r => r.listing_id === row.id) || (rows.length === 1 ? rows[0] : null)
      if (own) setFbStats(own)
    })
    return () => { gone = true }
  }, [isFb, propertyId, row.id])
  return (
    <div className="flex items-center gap-2 text-xs"
      style={{ paddingLeft: chain.known ? row.depth * 14 : 0 }}>
      {/* The connector belongs to the KNOWN state only — see the note above buildChain.
          In the flat band there is nothing to connect and drawing it would claim an order
          the data does not hold. */}
      {chain.known && row.depth > 0 && <span className="text-muted-foreground/50 -ml-2">└</span>}
      <span className={`shrink-0 rounded px-1 font-mono text-[10.5px] ${row.ours
        ? 'bg-sky-500/15 text-sky-600 dark:text-sky-300'
        : 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'}`}>{row.handle}</span>
      {/* წყარო tag on the KNOWN root only (mock 2b) — in the flat band nothing may claim it. */}
      {chain.known && !row.ours && row.depth === 0 && (
        <span className="shrink-0 rounded px-1 text-[10px] font-medium bg-emerald-500/20 text-emerald-700 dark:text-emerald-300">
          {t('desk_chain_source_tag')}
        </span>
      )}
      <span className={`shrink-0 font-medium ${row.closed ? 'text-muted-foreground line-through' : ''}`}>
        {(row.source ? PLATFORM_LABEL[platformKey(row.source)] : null) || row.source || '—'}
      </span>
      <span className="min-w-0 truncate text-muted-foreground">
        {[row.who, row.at ? cardAge(row.at) : null,
          fbStats?.post_count > 0
            ? `${fbStats.post_count} ${t('desk_lineage_fb_posts')}${fbStats.group_count > 0 ? ` · ${fbStats.group_count} ${t('desk_lineage_fb_groups')}` : ''}`
            : null,
        ].filter(Boolean).join(' · ')}
      </span>
      <span className="ml-auto shrink-0 tabular-nums">
        {row.price != null ? `${row.price}${row.currency ? ' ' + row.currency : ''}` : ''}
        {row.delta != null && (
          <span className={`ml-1 ${row.delta > 0 ? 'text-emerald-600 dark:text-emerald-400'
            : row.delta < 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
            {row.delta > 0 ? `+${row.delta}%` : row.delta < 0 ? `${row.delta}%` : '='}
          </span>
        )}
      </span>
      {row.url && (
        <a href={row.url} target="_blank" rel="noreferrer"
          className="shrink-0 text-muted-foreground hover:text-foreground"><Icon name="external-link" size={11} /></a>
      )}
    </div>
  )
}

function ChainPanel({ chain, propertyId = null }) {
  // Closed listings collapse into one muted line (mock 2b: "+ 2 დახუрული განცხადება") that
  // expands in place. They keep their handles — the GAP in the numbering is information
  // (designer rule 3) — so collapsing may hide a row but never renumbers one.
  const [showClosed, setShowClosed] = useState(false)
  if (!chain) return null
  const open = chain.rows.filter(r => !r.closed)
  const closed = chain.rows.filter(r => r.closed)
  return (
    <div className="mt-2 rounded-lg border border-border bg-background/40 px-3 py-2 pointer-events-auto"
      onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between gap-2 pb-1.5 mb-1 border-b border-border/60">
        <span className="text-xs font-medium">
          {t(chain.known ? 'desk_chain_title' : 'desk_chain_dupes_title')}
        </span>
        <span className="text-[11px] text-muted-foreground">
          {[chain.sources ? tLog('desk_chain_n_sources', { n: chain.sources }) : null,
            chain.reposts ? tLog('desk_chain_n_reposts', { n: chain.reposts }) : null,
            chain.platforms ? tLog('desk_chain_n_platforms', { n: chain.platforms }) : null,
          ].filter(Boolean).join(' · ')}
        </span>
      </div>
      <div className="space-y-0.5">
        {(showClosed ? chain.rows : open).map(row => <ChainRow key={row.id} chain={chain} row={row} propertyId={propertyId} />)}
        {closed.length > 0 && (
          <button className="text-[11px] text-muted-foreground hover:text-foreground pt-0.5"
            onClick={() => setShowClosed(v => !v)}>
            {showClosed ? '▴ ' : '+ '}{tLog('desk_chain_closed_n', { n: closed.length })}
          </button>
        )}
      </div>
      {/* The agents behind the reposts (mock 2b's "აგენტების ჯაჭვი") — one card per person,
          their handles, their newest asking price. ONLY what the rows already state: no phone,
          no negotiation, no cooperation figures — those stay behind their own gates
          (click-to-reveal, blind-first) and must not leak in through a summary band. */}
      {(() => {
        const byWho = new Map()
        for (const r of chain.rows) {
          if (!r.who || !r.ours) continue
          const a = byWho.get(r.who) || { who: r.who, rows: [] }
          a.rows.push(r); byWho.set(r.who, a)
        }
        const agents = [...byWho.values()]
        if (!agents.length) return null
        return (
          <div className="mt-2 pt-1.5 border-t border-border/60">
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="text-[10.5px] tracking-[.06em] uppercase text-muted-foreground">{t('desk_chain_agents_title')}</span>
              <span className="text-[11px] text-muted-foreground">{tLog('desk_chain_n_agents', { n: agents.length })}</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {agents.map(a => {
                const latest = a.rows[a.rows.length - 1]
                return (
                  <div key={a.who} className="rounded-md border border-border bg-background/60 px-2 py-1 text-xs min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium truncate">{a.who}</span>
                      {a.rows.map(r => (
                        <span key={r.id} className="rounded px-1 font-mono text-[10px] bg-sky-500/15 text-sky-600 dark:text-sky-300">{r.handle}</span>
                      ))}
                    </div>
                    <div className="text-muted-foreground tabular-nums">
                      {[(latest.source ? PLATFORM_LABEL[platformKey(latest.source)] : null) || latest.source,
                        latest.at ? cardAge(latest.at) : null,
                        latest.price != null ? `${latest.price}${latest.delta != null && latest.delta !== 0 ? ` ${latest.delta > 0 ? '+' : ''}${latest.delta}%` : ''}` : null,
                      ].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })()}
      {/* Stated ONCE, at the foot of the band — not per row (designer, turn 6). */}
      {!chain.known && (
        <p className="mt-1.5 pt-1.5 border-t border-border/60 text-[11px] text-muted-foreground">
          {t('desk_chain_unknown_first')}
        </p>
      )}
    </div>
  )
}

// B613 attribution probe. Mounting one card measures ~88 ms and that is now the whole
// remaining cost of a Desk browse click (B611: of 3,266 ms blocked, the data pipeline was
// 4.4 ms). Nothing could say WHICH part, so this times the card's own render body.
//
// Off unless `window.__PERF_PROBE = true`, and then it costs one boolean read plus two
// clock reads per card. It records TIMINGS ONLY — never row content.
//
// ⚠️ `HistoryItemBody` is CALLED, not rendered as <HistoryItemBody/>. That is deliberate:
// a plain call keeps its hooks in THIS component's hook list, so hook order is unchanged
// and the probe cannot alter behaviour. Rendering it as an element would insert a new
// component boundary and change what React memoises and re-renders — i.e. it would change
// the thing being measured.
export function HistoryItem(props) {
  const on = typeof window !== 'undefined' && window.__PERF_PROBE === true
  const t0 = on ? performance.now() : 0
  const out = HistoryItemBody(props)
  if (on) {
    const d = performance.now() - t0
    const p = (window.__cardPerf = window.__cardPerf || { renders: 0, totalMs: 0, worstMs: 0 })
    p.renders++; p.totalMs = +(p.totalMs + d).toFixed(1); p.worstMs = Math.max(p.worstMs, +d.toFixed(2))
  }
  return out
}

function HistoryItemBody({ item, matchNote = null, availableFrom = null, reportedGone = 0, districts, features, glow, glowClickable, onOpen, thread, sub, titleOverride, searchPolygons, gallerySize = 'thumb', platformChips, chain = null, cooperations, coopOffer = null, restrictions, payTerms = null, restrictionsCleared = null, views, shares = null, onOpenCooperation, onAddMeeting, onCreateContract, onShare, onRepost = null, onShareOthers = null, onOpenTerms, meetingsIndex, onOpenMeeting, clientMeetings = null, privileged = false, stackDates = null, favorites = null, onToggleFavorite = null, archived = null, onToggleArchive = null, onReportGone = null, authorMark = null, strip = null, contactKnown = null, cornerSlot = null, onDistrictVote = null, stateSlot = null, defects = null, agentFacts = null, distinct = null, devCard = null, seller = null }) {
  const money = useMoney()
  // DEV render counter (B122 attribution probe, pairs with window.__feedPerf): tells a
  // measurement whether blocking time is cards re-rendering or non-card tree work.
  if (import.meta.env.DEV) window.__cardRenders = (window.__cardRenders || 0) + 1
  // Meetings touching THIS card: matched by the same keys the lineage modal uses (ad URL
  // or snapshot core_listing_id), across this posting AND its platform siblings, deduped.
  const cardMeetings = (() => {
    if (!meetingsIndex) return null
    const urls = [item.listing?.source_url, ...(platformChips || []).map(c => c.source_url)].filter(Boolean)
    const coreIds = [item.listing?.listing_id, ...(platformChips || []).map(c => c.id)].filter(Boolean)
    const seen = new Set(), out = []
    for (const k of urls) for (const v of meetingsIndex.byUrl[k] || []) if (!seen.has(v.id)) { seen.add(v.id); out.push(v) }
    for (const k of coreIds) for (const v of meetingsIndex.byCore[k] || []) if (!seen.has(v.id)) { seen.add(v.id); out.push(v) }
    // Core meetings are anchored on the PROPERTY, not on an ad — matched last so
    // the url/listing hits keep their order, and deduped by id so a legacy viewing
    // that carries both a listing ref and a property id is not counted twice.
    const propId = item.listing?.property_id || item.result?.property_id
    if (propId) for (const v of meetingsIndex.byProperty?.[propId] || []) if (!seen.has(v.id)) { seen.add(v.id); out.push(v) }
    return out
  })()
  // The chain opens IN PLACE, under the card (design 2b) — deliberately not a modal: the
  // question it answers ("who posted this first, and who do I call") is asked while scanning
  // the feed, and a modal makes you leave the row you were reading.
  const [chainOpen, setChainOpen] = useState(false)
  const objType = objectTypeOf(item)
  // Meeting/candidate entries are audit-only logs (POST /api/core/proposal, never routed
  // through execute_proposal) — their status sits at 'accepted' forever, which for the
  // client/requirement pipeline means "stuck, not executed" but here just means "logged
  // successfully" (the real write already happened elsewhere: ViewingForm / useCandidates).
  // Without this split, these cards would show a permanent, misleading "Saving…".
  const isAuditOnly = objType === 'meeting' || objType === 'candidate'
  const displayStatus = isAuditOnly && item.status === 'accepted' ? 'executed' : item.status
  // 'accepted' is yellow, not green: it means committed-but-NOT-executed. A gateway rejection
  // rolls the whole transaction back (incl. the status='failed' write — see 0017 INVARIANT 5),
  // so a rejected proposal freezes at 'accepted'. Green is reserved for actually-executed.
  const statusColor = { pending: 'yellow', accepted: 'red', executed: 'green', rejected: 'red', edited: 'blue', failed: 'red', dismissed: 'default', active: 'green', inactive: 'default' }[displayStatus] || 'default'
  const humanEdited = !!item.proposed?.human_edited
  // listing_readonly/scraped_listing_readonly rows are scraped/agent-declared facts, not
  // AI-parsed proposals — the "🤖 AI-created"/"✏️ you edited" line doesn't apply. Keyed off
  // the two specific read-only listing KINDs (both are objType 'listing' as of 2026-07-08).
  const isExternalListing = kindOf(item) === 'listing_readonly' || kindOf(item) === 'scraped_listing_readonly'
  // The "today"/"21d" age chip and the "ნანახია X წინ" freshness chip were BOTH retired
  // 2026-08-03 (owner: "why do we need this?" / "we need one line dates"): every date fact
  // now lives in the card's single relative date line — one surface, four ages. The badge
  // slot falls back to the plain status badge. listingAgeBadge stays exported for
  // compactSummary (IntakeTab), which has no date line of its own.
  // Rides ALONGSIDE the age/status badge rather than replacing it — "how long has it been on
  // the market" and "when does it free up" are different questions and an agent needs both.
  const availBadge = availableFromBadge(availableFrom)
  const goneBadge = marketReportBadge(reportedGone)
  // A plain AI/manual property draft (extract_listing) has no item.listing at all — that field
  // only exists on the synthetic scraped/reshare item shapes. Without this, ListingGallery got
  // `listing={undefined}` for a draft: no address to geocode, so its map-slide fell straight to
  // the empty placeholder no matter what. Pull the draft's own address out of its proposal
  // payload instead, so the SAME geocode cascade has something to work with.
  const galleryListing = isExternalListing
    ? item.listing
    // listing_images = manually-uploaded photos, attached server-side for executed drafts
    // (/api/core/proposals enrichment) — with photos the card shows them like a scraped listing.
    : (objType === 'property' ? { address: item.proposed?.input?.property?.address || null, images: item.listing_images || [] } : null)
  const summary = summarize(devCard && item.listing ? { ...item, listing: { ...item.listing, dev_card: devCard } } : item, districts, features, money)
  // B274. "This card is only a CANDIDATE for the district it is filed under."
  // ⚠️ The gate is `district_status === 'disputed'` EXACTLY — it must mirror the vote
  // endpoint's own precondition, which 409s on anything else. Two statuses look votable and
  // are not: `resolved` is already settled (three votes one way, 0101) and asking again
  // re-opens a closed question; `unconfirmed_sub` ALSO carries a declared code that differs
  // from the pin, but per core_ingest.flagDistrictDispute it means the declared district sits
  // INSIDE the pin's — one place described at two zoom levels, with no rival to choose
  // between. A "which is it?" chip there asks an unanswerable question and every tap would
  // return 409. Undisputed rows carry no dispute fields at all (the server omits them
  // per-row), so this is false on nearly every card.
  const districtDisputed = !!onDistrictVote && !!item.listing?.declared_district_code
    && item.listing.declared_district_code !== item.listing.district_code
    && item.listing.district_status === 'disputed'
  // On a thread head, the card is anchored by the client's IDENTITY (the create's name,
  // passed as titleOverride) so an agent scanning the list sees "Salome", not "Client
  // update" — the head's OWN action ("changed: price, districts" / "Client demand closed")
  // becomes the detail line so the freshest true thing is still visible at a glance.
  // titleOverride is {title, city, rest} (HistoryGroup's anchor), not a string.
  const title    = titleOverride?.title || summary.title
  const subtitle = titleOverride ? (summary.subtitle || summary.title) : summary.subtitle
  // Same string as `title`, with the out-of-town city weighted on the card FACE — it is a
  // scope marker, not one more address word, and it changes the meaning of everything after
  // it. The parts are split at SOURCE (summarize's titleCity/titleRest, or the anchor's) and
  // never re-parsed from the composed string here, so the weighting and the text cannot drift.
  // No city — the home city, or a client-name override — just renders the plain string.
  const titleCity = titleOverride ? titleOverride.city : summary.titleCity
  const titleRest = titleOverride ? titleOverride.rest : summary.titleRest
  const titleNode = titleCity
    ? <><span className="font-semibold">{titleCity}</span>{`, ${titleRest}`}</>
    : title
  // Gallery/map-pin and the client area preview are both skipped on thread sub-rows (`sub`) —
  // those are dense history entries, not the main card, so keep them text-only.
  const clientPolygon = !sub && objType === 'client' ? searchPolygons?.[item.result?.requirement_id] : null
  // A plain AI/manual property draft (extract_listing/extract_requirement's property twin) has
  // an address but no scraped photos — it still deserves a map thumb, same as a scraped listing
  // gets its photo/pin carousel. Previously ONLY isExternalListing got a thumb-mode gallery, so
  // every pending draft card (the common "review this" case) sat bare next to full-size listing
  // cards. galleryListing already carries the draft's address (see above) when there's no photo.
  const hasGalleryListing = !!(galleryListing?.address || galleryListing?.district || galleryListing?.images?.length)

  // Urgency accent (2026-07-16 card redesign): a left border stripe separates "act now"
  // from "FYI" at a glance — mapped from data we ALREADY have (urgency + move_in_date +
  // missing phone), no new queries. Client thread-heads only; sub rows stay flat.
  const crit = item.proposed?.criteria || {}
  const moveInDays = crit.move_in_date ? (new Date(crit.move_in_date).getTime() - Date.now()) / 86400000 : null
  const isHotLead = objType === 'client' && !sub &&
    (item.proposed?.requirement?.urgency === 'immediate' || (moveInDays !== null && moveInDays >= 0 && moveInDays <= 14))
  const missingPhone = objType === 'client' && !sub && kindOf(item) === 'extract_requirement' && !item.proposed?.person?.phone
  // Sparse criteria = huge, meaningless match counts (a demand with no districts/budget/rooms
  // "matched" 1107 listings on 2026-07-16). The fix is upstream of display: TELL the agent
  // which core fields to fill, so the count becomes an honest signal. A drawn search area
  // (clientPolygon) counts as a location, so it doesn't nag when the agent drew a polygon.
  const missingCrit = objType === 'client' && !sub && kindOf(item) === 'extract_requirement' ? [
    ...(!(crit.district_codes || []).length && !(crit.district_must_codes || []).length && !clientPolygon ? [t('desk_crit_districts')] : []),
    ...(crit.price_max == null ? [t('desk_crit_budget')] : []),
    ...(crit.rooms_min == null && crit.rooms_max == null && crit.bedrooms_min == null ? [t('desk_crit_rooms')] : []),
  ] : []
  const urgencyAccent = isHotLead ? 'border-l-4 border-l-destructive' : (missingPhone || missingCrit.length) ? 'border-l-4 border-l-amber-400' : ''

  // ── What HAPPENED with this client (owner 2026-08-01) ──────────────────────
  // "This client was added to the meeting that was closed — shouldn't we have that
  // registered here?" The two records existed and nothing on the card said so: a client
  // with a viewing booked, or a closed deal, looked identical to one nobody had called.
  //
  // Read from the meetings the Desk has ALREADY loaded (the feed's own list, indexed by
  // requirement and by party in IntakeTab) — no new query, and it inherits that feed's
  // viewer scoping rather than inventing its own. Keyed by requirement FIRST: a party may
  // hold several demands, and a viewing belongs to the demand it was booked for. The party
  // fallback catches meetings attached to a client who had no requirement at the time.
  const demandMeetings = objType === 'client' && !sub && clientMeetings
    ? (clientMeetings.byRequirement?.[item.result?.requirement_id]
       || clientMeetings.byParty?.[item.result?.person_id] || [])
    : []
  // The 🏁 chip must open the CLOSED meeting it announces — it used to click through to
  // `nextMeeting`, whose selection never picks a closed one, so the chip could land on a
  // failed or cancelled row (fixed 2026-08-15). "Next" is the earliest upcoming by
  // appointment time, not whatever sits first in array order.
  const closedMeeting = demandMeetings.find(m => m.status === 'closed') || null
  const dealClosed = !!closedMeeting
  // ⚠️ EVERY ACTIVE STATUS, not a hand-written pair. This read `scheduled || pending`, which
  // silently excluded `binder` — the most live state a meeting has, earnest money already down —
  // and `rescheduled`. A client whose only live meeting was a binder therefore fell through to
  // the `demandMeetings[0]` fallback below, i.e. ARRAY ORDER, which is exactly the defect the
  // comment above says was fixed on 2026-08-15. It was fixed for closed/failed/cancelled and
  // left open for the two active statuses nobody listed. 3 live binder meetings exist.
  const nextMeeting = demandMeetings
    .filter(m => MEETING_ACTIVE.includes(m.status))
    .sort((a, b) => (a.scheduled_at || a.created_at || '').localeCompare(b.scheduled_at || b.created_at || ''))[0]
    || demandMeetings[0] || null
  // What the 🤝 chip may speak for: a cancelled meeting never happened, so it is neither
  // something this client "has" nor something worth opening from a summary chip.
  const meetingsThatCount = demandMeetings.filter(m => m.status !== 'cancelled')
  const chipMeeting = nextMeeting && nextMeeting.status !== 'cancelled'
    ? nextMeeting
    : meetingsThatCount
        .slice()
        .sort((a, b) => (b.scheduled_at || b.created_at || '').localeCompare(a.scheduled_at || a.created_at || ''))[0]
      || null

  // ── C-skeleton slots (A→C increment 2, owner 2026-07-23: "Properties first") ──
  // The card face is FIVE named slots in fixed order: media → identity → provenance →
  // signals → strip. Extracted as named renderers (behavior-preserving) so the next
  // increments can hand other categories the same skeleton with per-category slot
  // content instead of forked card components. Slots close over this component's
  // computed state on purpose — they are ORDER contracts, not portable widgets yet.
  const slotMediaLarge = () => (<>
    {(gallerySize === 'large' || gallerySize === 'home') && !sub && !clientPolygon && (objType === 'property' || objType === 'listing') && (
      <ListingGallery listing={galleryListing} size="large" />
    )}
    {(gallerySize === 'large' || gallerySize === 'home') && clientPolygon && <AreaPreview polygon={clientPolygon} size="large" />}
  </>)
  const slotMediaThumb = () => (<>
    {gallerySize === 'thumb' && !sub && hasGalleryListing && <ListingGallery listing={galleryListing} size="card" />}
    {gallerySize === 'thumb' && clientPolygon && <AreaPreview polygon={clientPolygon} size="thumb" />}
  </>)
  // The four date facts as CHIPS in the badge row (owner 2026-08-03: "make one line from
  // this two with age chips and titles what they denote") — the separate text date line is
  // gone; status, dates and the other badges are ONE line. Each chip = the label of what
  // its date denotes + the elapsed-time form; the exact timestamp lives in its tooltip.
  // The market chip keeps the old age chip's platform-expiry coloring; the checked chip
  // keeps the freshness ladder (≤7d green / ≤21d amber).
  // Does this card get the aligned state column (design 1c)? Listing faces only: it is a
  // column of listing state, and a client/draft card has none of those rows, so it would
  // render an empty rail. ⚠️ When it IS shown the date CHIPS must not also render — the same
  // four facts in two places is exactly the "second copy" disease, and the column is the one
  // that keeps them aligned across the feed.
  // ⚠️ EVERY listing card face, not just the row form (owner 2026-08-04: "match გალერიის
  // view"). The gallery/home cards were still showing the pre-redesign pile — five date
  // chips, the platform pills, a chain chip and a separate negotiation band — beside row-form
  // cards that had already lost all of it. The state block is the card's ONE state area; a
  // card that opts out of it is a second design, which is what this whole pass removed.
  // ⚠️ `stateSlot` OVERRIDES the whole block, it does not add to it (2026-08-05). The column
  // is one card's answer to "what is the state of this thing", and a category that supplies
  // its own has decided the listing rows are the wrong answer. Meetings did: `ვნახეთ` is our
  // crawl date and `შემოწმდა` is the re-checker's debt — leads-hunting facts on a card whose
  // job is "you are standing outside a building at 3:30". Note the listing's own status dot
  // lives inside StateBlock, so a slotted card loses that too — deliberately, since on a
  // meeting card a green აქტიური is the LISTING's status and the meeting's own status is a
  // different word three centimetres away.
  const showState = !sub && (isExternalListing || !!stateSlot)
  // …but only the ROW form has room beside the facts. Gallery and home are photo-on-top,
  // single-column cards — i.e. already 2c, so they take the stacked block at every width.
  // ⚠️ This says the card is ELIGIBLE for the column, not that it gets it: whether there is
  // actually room is a question about the CARD's width, which only a container query can
  // ask (`.desk-state-beside`, index.css 2026-08-05). It shipped as a `lg:` viewport query
  // and the Meetings feed — the one surface that renders the row form inside a 340px grid —
  // collapsed the facts column to zero and pushed the state column 89px outside the card.
  const stateBeside = showState && gallerySize === 'thumb'
  // Does this card take the [photo | body] row form? Hoisted out of the JSX (2026-08-09)
  // because THREE nested wrappers now key off it — the row, the body column and the inner
  // facts row — and three copies of one condition is three chances for them to disagree.
  const rowForm = !sub && gallerySize === 'thumb' && (hasGalleryListing || clientPolygon || stateBeside)
  // The cluster's observed price band (mock: "700–750") — what the same flat is ASKING across
  // its postings. Same currency only; a USD/GEL band would be a made-up spread.
  const priceBand = (() => {
    if (!showState || !chain) return null
    const cur = item.listing?.currency
    const ps = chain.rows.filter(r => r.price != null && r.currency === cur).map(r => r.price)
    if (ps.length < 2) return null
    const lo = Math.min(...ps), hi = Math.max(...ps)
    return lo === hi ? null : `${lo}–${hi}`
  })()
  const dateChips = (!sub && !showState && stackDates?.created) ? (() => {
    const marketKey = !stackDates.createdIsPosted ? 'desk_entry_first_seen'
      : stackDates.postedKind === 'republished' || stackDates.postedKind === 'current_run' ? 'desk_entry_bumped'
      : 'desk_entry_posted'
    const days = ts => Math.floor((Date.now() - new Date(ts).getTime()) / 86400000)
    const expiry = PLATFORM_EXPIRY_DAYS[item.listing?.source]
    const md = days(stackDates.created)
    // ⚠️ `=== 'inactive'` here was DEAD (found 2026-08-09). `listing.status` in core holds
    // `active` | `withdrawn` and nothing else — measured: 44,544 / 9,921, zero 'inactive'. So
    // the guard never fired and a withdrawn ad kept being coloured by its AGE, i.e. a green
    // "fresh" chip on something that is off the market. Off-market beats any freshness estimate,
    // which is exactly what the comment above PLATFORM_EXPIRY_DAYS already claimed happened.
    const marketVariant = !expiry || (item.listing?.status && item.listing.status !== 'active') ? 'default'
      : md <= 7 ? 'green' : md <= expiry / 2 ? 'yellow' : md < expiry ? 'amber' : 'red'
    const cd = stackDates.checked ? days(stackDates.checked) : null
    return [
      { key: marketKey, ts: stackDates.created, variant: marketVariant,
        hint: !stackDates.createdIsPosted ? t('desk_entry_first_seen_hint') : null },
      stackDates.acquired && { key: 'desk_entry_acquired', ts: stackDates.acquired, variant: 'default', hint: t('desk_entry_acquired_hint') },
      stackDates.checked && { key: 'desk_entry_checked', ts: stackDates.checked,
        variant: cd <= 7 ? 'green' : cd <= 21 ? 'amber' : 'default', hint: t('desk_entry_checked_hint') },
      stackDates.modified && { key: 'desk_entry_updated', ts: stackDates.modified, variant: 'default', hint: null },
    ].filter(Boolean).map(c => (
      <Badge key={c.key} label={tLog(c.key, { date: cardAge(c.ts) })} variant={c.variant}
        title={[tLog(c.key, { date: cardDateTime(c.ts) }), c.hint].filter(Boolean).join(' — ')} />
    ))
  })() : null
  const slotBadges = () => (<>
    {/* The status badge yields to the state column's own status line (owner 2026-08-04 mock
        review: "აქტიური" twice on one face was the first thing circled). */}
    {!showState && <Badge label={t(STATUS_LABEL_KEYS[displayStatus]) || displayStatus} variant={statusColor} />}
    {dateChips}
    {availBadge && <Badge label={availBadge.label} variant={availBadge.variant} title={availBadge.title} />}
    {goneBadge && <Badge label={goneBadge.label} variant={goneBadge.variant} title={goneBadge.title} />}
  </>)
  const slotPrice = (cls = 'text-lg') => summary.price && (
    <p className={`${cls} font-bold leading-6 tabular-nums mt-0.5 ${summary.price.muted ? 'text-muted-foreground/60' : ''}`}
      title={priceTitle(summary.price)}>
      {summary.price.text}
      {summary.price.ppm && <span className="ml-1.5 text-xs font-normal text-muted-foreground">{summary.price.ppm}</span>}
      <ObservedPriceHistoryBadge history={summary.price.history} />
    </p>
  )

  // ─── THE CORNER CHIP: one node, one placement decision ──────────────────────────────────
  // Stack count ("3 ×", handed down by HistoryGroup) or this card's own thread toggle. Whoever
  // supplies it, it renders ONCE — inline at the end of the footer's rail row when this card
  // has a footer, and only as a bottom-right overlay when it does not.
  // ⚠️ History (2026-07-24): the chips were overlay-only, so the card reserved a `pb-8` strip
  // under the footer to stop them landing ON the ＋ affordances. That reservation cost every
  // stacked card a full empty band for one chip (owner 2026-08-03: "it takes unnecessary
  // space"). A flex child cannot overlap its siblings, so the fix removes the collision AND
  // the reservation — the strip is now reserved only on the fallback path, where the overlay
  // is still an overlay. Don't re-add pb-8 unconditionally.
  // Same rule as HistoryGroup's cornerSlot (owner 2026-08-04): on a card with a chain, the
  // chain door is the ONE control for "there are N postings here" — a second count is the
  // redundancy the mock review circled.
  const cornerNode = cornerSlot || ((chain && thread?.noun === 'listings') ? null : thread && thread.count > 1 ? (
    !thread.onToggle
      /* ⚠️ A COUNT IS NOT ALWAYS A CONTROL (owner-caught 2026-07-25). The Leads feed renders
         this same card but never implemented stack expansion — it passed `onToggle={() => {}}`,
         so the chip looked and hovered exactly like the property feed's working one and did
         nothing when pressed. A dead affordance is worse than no affordance: it teaches the
         agent the app is broken. Feeds without an expander pass onToggle={null} and get the
         count as plain text. */
      ? <span className={`${CORNER_CHIP} bg-muted text-muted-foreground`}>{`${thread.count} ×`}</span>
      : thread.noun === 'listings'
        /* Expanded cascade member: the card body now OPENS the member (see HistoryGroup), so
           this slot (where the collapsed stack shows its "N ×" count) carries the collapse
           toggle — the same anchor point thread actions always live in. */
        ? <button className={`${CORNER_CHIP} bg-primary/90 text-primary-foreground hover:bg-primary`}
            onClick={e => { e.stopPropagation(); thread.onToggle() }}>{`${thread.count} × ▲`}</button>
        : <button className={`${CORNER_CHIP} bg-primary/90 text-primary-foreground hover:bg-primary`}
            onClick={e => { e.stopPropagation(); thread.onToggle() }}>
            {`${tLog('desk_entry_thread_updates', { n: thread.count })} ${thread.expanded ? '▲' : '▼'}`}
          </button>
  ) : null)
  // The footer's own gate, evaluated here so the two placements are mutually exclusive by
  // construction. CooperationChips/CardFooter render even when endSlot is their only content,
  // so "footer shown" is enough — there is no case where both fire and none where neither does.
  // `strip` earns the footer too (2026-09-06, see the rail gate below), so it must count HERE as
  // well — or the strip renders standalone AND inside the footer, the double pill the owner saw.
  const footerShown = !!(onOpenCooperation || onAddMeeting || onShare || onOpenTerms || strip) && !sub
  const cornerInFooter = !!cornerNode && footerShown

  return (
    <div
      /* pb-8 reserves a strip for the corner chip ONLY on the overlay path (see above): the
         chips are `absolute bottom-2 right-2`, so they occupy 8–29px above the card's bottom
         edge while `p-4` ends the content at 16px, and they would land on whatever the card's
         last row is. Reserving VERTICAL space beats shrinking a row horizontally: the badges
         differ in width ("4 ×" vs "N updates ▼"), so a horizontal pad would be wrong for one.
         ⚠️ pb-8 must come AFTER `card`'s p-4 — Tailwind emits pb-* after p-*, so it wins. */
      /* `desk-cq` makes THIS card the query container for every width decision inside it —
         the row form, the photo and the state column (index.css, 2026-08-05). They must size
         themselves against the card, never the window: a card three columns wide on a 4K
         monitor is still 340px, and only the card knows that. */
      className={`relative desk-cq ${sub ? 'bg-muted/30 rounded-lg p-2 opacity-70' : card} ${cornerNode && !cornerInFooter ? 'pb-8' : ''} ${urgencyAccent} transition-all ${onOpen ? 'cursor-pointer hover:border-primary/40' : ''} ${glow === 'amber' ? glowAmber : glow === 'blue' ? glowBlue : ''}`}
      onClick={onOpen}
    >
      {/* ⚠️ the card's KEYBOARD door (Desk home audit 2026-10-21): the whole-card click is mouse-only, and wrapping a card
          full of tools in one button is invalid. A real button, first in tab order, hidden until focused; the
          click-anywhere convenience stays as it was. */}
      {onOpen && (
        <button type="button" onClick={e => { e.stopPropagation(); onOpen(e) }} className="sr-only focus:not-sr-only focus:absolute focus:top-1 focus:left-1 focus:z-20 focus:rounded focus:bg-primary focus:px-2 focus:py-1 focus:text-xs focus:text-primary-foreground">
          {t('desk_card_open')}
        </button>
      )}
      {/* ── MY SHELVES: 🗄 archive + ♥ favorite, ONE cluster ────────────────────────────
          🗄 (0375) sits LEFT of ♥ so the corner stays "my shelves" and never grows into a
          toolbar. Both carry the same gate, the same optimistic-parent pattern and the same
          null-means-unavailable rule (favorites/archived === null → API hasn't answered →
          no dead buttons); it is the same KIND of thing (mine, reversible, private), which
          is exactly why they earn a place next to each other.
          ⚠️ 🗄 hides the card from THIS agent's feed only. It is NOT the leads Archive
          (company-wide, sets lead_status, permanent for some reasons) and NOT a market
          report (0178: a report never archives, so one agent cannot remove another's
          property). If this button ever starts doing either, the label lies.

          🚨 THEY OVERLAPPED ON MOBILE (owner screenshot, 2026-08-19) — and the cause was
          that "next to each other" was expressed as TWO independently positioned elements,
          `right-2` and `right-9`, with the 36px gap between them hand-computed from the ♥
          button's assumed width. ♥/♡ is not a glyph of assumed width: Android substitutes an
          EMOJI font for U+2665, which renders far wider than the desktop text glyph the 36px
          was measured against, so the heart grew leftwards under the eye. A hardcoded offset
          can only ever be right for the font that was on screen when it was written.
          One flex row lays them out instead — the gap is now a fact of the layout rather than
          an arithmetic guess, and it survives any glyph, any font fallback, any size change.
          ⚠️ Keep the two conditions SEPARATE inside the row (not one combined guard): either
          shelf can be unavailable on its own, and the survivor must still render. */}
      {/* The two shelf verbs now come from ShelfToggles (CooperationChips.jsx) so the card and
          the lineage modal's footer cannot drift — owner asked for them in the modal too on
          2026-09-10. The ABSOLUTE POSITIONING stays here: where they sit is the card's own
          layout decision, and the modal places the same buttons in its action row instead.
          ⚠️ item.listing is passed WHOLE, not {property_id}: the toggles snapshot address,
          price and currency onto the shelf entry when adding. */}
      {!sub && item.listing?.property_id && (
        <ShelfToggles row={item.listing} favorites={favorites} archived={archived}
          onToggleFavorite={onToggleFavorite} onToggleArchive={onToggleArchive}
          onReportGone={onReportGone} reportedGone={reportedGone}
          compact className="absolute z-10 top-2 right-2" />
      )}
      {/* 'large' (the dedicated Gallery view): image/map fills the top, text below — same as
          before. 'thumb' (normal card view): a small thumbnail sits LEFT of the text instead,
          so it doesn't dominate a compact card.
          Gallery view gives EVERY property-ish card a media slot, not just read-only scraped
          listings — a plain AI/manual draft (extract_listing) has no item.listing at all, so
          ListingGallery just falls through to its placeholder box instead of leaving a blank
          hole in the grid ("if anything has no image must show map"). */}
      {/* SLOT: media (large/home form) */}
      {slotMediaLarge()}
      {/* eBay-style body (2026-07-17 redesign v2): the image is a BIGGER left column spanning
          title/price/facts, and everything reads top-down beside it — title, then price bold
          in the reading flow (not a corner the eye has to travel to), then the facts line. */}
      {/* items-stretch so the media column can fill the face's height (owner request
          2026-07-30). ListingGallery's 'card' size carries the matching self-stretch. */}
      {/* showState joins the flex condition: the state column is a COLUMN, and a card with no
          thumbnail would otherwise leave the container block-level and stack it underneath. */}
      {/* `desk-card-row` replaces `flex flex-col sm:flex-row gap-3 sm:items-stretch` — the
          same decision, moved off the viewport onto the card (index.css §1). The photo's own
          `sm:w-56` was the other half of it and moved with it; they are one rung, so they
          must always change together. The card no longer stacks at any width: the narrow rung
          SHRINKS the photo instead, which measured half the height of photo-on-top at a
          meetings column while giving the facts nearly double the room. */}
      {/* ⚠️ THE PHOTO'S SIBLING IS THE WHOLE CARD BODY, NOT JUST THE FACTS (owner 2026-08-09,
          annotated screenshot: "stretch image from top to bottom then align bottom pills left
          to image"). Until then the row held [photo | facts] and the footer pill rail was a
          later sibling at card level, so the photo stopped where the facts stopped and the
          rail ran the full card width underneath it — the empty band the screenshot circles.
          Now the row holds [photo | column], where the column is facts-row ON TOP of the tail
          (feed strips + the pill rail). Two consequences, both wanted: the photo stretches past
          the pills to the card's bottom edge (index.css §1 carries the stretch), and the rail
          starts at the photo's right edge because it is INSIDE the column.
          ⚠️ ChainPanel deliberately stayed OUTSIDE (it moved below the footer): it is a
          full-width expansion, and inside the column it would both lose the photo's width and
          drag the stretched photo to double height whenever an agent opened it.
          ⚠️ `contents` on the two new wrappers when the row form is off — a gallery/home/sub
          card is photo-on-top and must render byte-for-byte as before, so the wrappers vanish
          from layout entirely rather than adding two block boxes to it. */}
      <div className={rowForm ? 'desk-card-row' : ''}>
        {/* SLOT: media (thumb form, left column) */}
        {slotMediaThumb()}
        <div className={rowForm ? 'flex-1 min-w-0 flex flex-col' : 'contents'}>
        {/* ⚠️ `flex-1` on the FACTS row, which is what makes the rail a real footer (owner
            2026-08-09: "this is a footer so align this to the bottom"). The facts row takes the
            slack, so the rail is pushed to the column's bottom edge — and since the column is
            the photo's flex sibling, that edge IS the photo's bottom. Without it the rail sits
            immediately under the last line of text and any leftover height (the photo's 11rem
            floor, or a taller neighbour in an equal-height grid) opens as dead space BELOW the
            footer, which reads as a card that failed to fill itself. */}
        <div className={rowForm ? 'flex gap-3 items-start min-w-0 flex-1' : 'contents'}>
        <div className="flex-1 min-w-0">
          {sub ? (
          <>
          {/* Dense thread sub-row — a history entry, not a card face. Keeps the old one-line
              shape (truncated title, badges inline) on purpose: the identity block below is
              for the FACE, and giving a sub-row four stacked lines would bury the head card. */}
          <div className="flex items-start gap-2 min-w-0">
            {objType && <Icon name={OBJECT_ICON[objType]} size={16} className="shrink-0 mt-1 text-muted-foreground" />}
            <p className={`font-medium text-sm truncate ${!titleOverride && summary.titleMuted ? 'text-muted-foreground/70' : ''}`}
              title={!titleOverride && summary.titleMuted ? t('desk_entry_address_inherited') : undefined}>{title}</p>
            {slotBadges()}
          </div>
          {slotPrice()}
          {/* People stay INLINE on a sub-row (they left the facts line only for the card face,
              which is the surface with a height budget) — verbatim pre-2026-07-25 content. */}
          {subtitle && <p className="text-sm text-muted-foreground mt-0.5">{subtitle}{summary.people ? ` · ${summary.people}` : ''}</p>}
          {/* B259. Secondary by construction: the cluster's own price stays the headline and
              this explains why the stack surfaced — Google's snippet, not a new title. Only
              rendered when some-but-not-all members matched, so it never repeats the face. */}
          {matchNote && !sub && <MatchedEvidenceLine note={matchNote} />}
          </>
          ) : (
          <>
          {/* ── IDENTITY BLOCK — ONE order for every card face (owner rulings 2026-07-25
              mobile review), replacing the home-vs-rest fork that used to live here:
                price → ADDRESS → facts → badges → provenance
              Two things this fixes, both caused by the old fork, both visible on a phone:
              1. ⚠️ BADGES GET THEIR OWN ROW — they must never again be a flex sibling of the
                 address. `Badge` is inline-block and does not shrink, so on the ~184px text
                 column of a 400px viewport the age + freshness pair ("5d" + "ნანახი 3d") ate
                 ~135px and squeezed the address down to its min-content: a card whose title
                 read "საბ / ქ…" instead of "საბურთალო, ყაზბეგის 24".
              2. ⚠️ THE ADDRESS IS text-sm AND CLAMPS TO 2 LINES, above the facts — on the
                 front page it was a text-xs muted line-clamp-1 footnote BELOW the facts, i.e.
                 the least legible element on the card. District + street is what the agent
                 navigates by (summarize() builds it deliberately); it ranks second, under
                 price, and is normal-color while the facts stay muted.
              A card with no price (client, draft, meeting) promotes the title back to the
              heading — the one variation point, same as the old home branch had. */}
          <div className="flex items-start gap-2 min-w-0">
            {objType && <Icon name={OBJECT_ICON[objType]} size={16} className="shrink-0 mt-1 text-muted-foreground" />}
            {summary.price
              /* SLOT: price — biggest thing on the card. Muted = inherited from the origin ad.
                 On state-column cards the mock's price ROW rides beside it: ლიდი inline (its
                 chip left the band below) and the cluster's observed price band ("700–750") —
                 the range the same flat is asking across its postings, which one number alone
                 cannot say. */
              ? <span className="flex flex-wrap items-baseline gap-x-2 min-w-0">
                  {slotPrice('text-xl')}
                  {showState && ['open', 'working', 'engaged'].includes(item.listing?.lead_status) && (
                    <span className="text-xs font-medium text-amber-600 dark:text-amber-400 inline-flex items-center gap-1"
                      title={t('desk_lead_badge_hint')}><Icon name="target" size={12} /> {t('desk_lead_badge')}</span>
                  )}
                  {/* 🔁 reshare pool (owner 2026-10-27, on the price line next to ლიდი) - ReshareMark.jsx */}
                  <ReshareMark propertyId={item.listing?.property_id} />
                  {priceBand && <span className="text-xs text-muted-foreground tabular-nums">{priceBand}</span>}
                </span>
              /* Muted title = the address is INHERITED from the source listing (reshare with no
                 posted_address of its own) — see summarize's titleMuted. */
              : <p className={`font-medium line-clamp-2 ${!titleOverride && summary.titleMuted ? 'text-muted-foreground/70' : ''}`}
                  title={!titleOverride && summary.titleMuted ? t('desk_entry_address_inherited') : undefined}>{titleNode}</p>}
            {/* Q2 (owner ruling 2026-09-05): an UNRESOLVED street conflict is surfaced, never chosen away.
                The claims travel from the server's address_state through displayAddress.js; nothing here
                picks a side, it names the disagreement until the resolver settles it. */}
            {Array.isArray(item.listing?.address_conflict) && item.listing.address_conflict.length > 1 && (
              <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-0.5"
                 title={item.listing.address_conflict.map(c => c.name_ka).filter(Boolean).join(' / ')}>
                ⚠ {t('desk_address_conflict')} · {item.listing.address_conflict.map(c => c.name_ka).filter(Boolean).join(' / ')}
              </p>
            )}
          </div>
          {summary.price && (
            /* Hover the address → the nearby places this pin actually sits among. Gated on
               is_representative AND on the pin surviving the server's degradation; see
               NearbyHover. The existing title (inherited-address provenance, else the
               untruncated address) is preserved verbatim — the popover adds, never replaces. */
            <NearbyHover
              propertyId={item.listing?.property_id}
              lat={item.listing?.lat} lng={item.listing?.lng}
              isRep={item.listing?.is_representative}
              className={`text-sm mt-0.5 line-clamp-2 ${!titleOverride && summary.titleMuted ? 'text-muted-foreground/60' : ''}`}
              title={!titleOverride && summary.titleMuted ? t('desk_entry_address_inherited') : title}>
              {titleNode}
            </NearbyHover>
          )}
          {subtitle && <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">{subtitle}</p>}
          {/* Who captured it / whose ad it is — one clamped line, never allowed to wrap into
              the facts (see summarize's `people`). Full text stays in the tooltip and the
              lineage modal. */}
          {summary.people && (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1" title={summary.people}>{summary.people}</p>
          )}
          {/* 1685 — whose property and who handles it; "likely" where it is inferred (desk/SellerFacets.jsx) */}
          {sellerLine(seller, t) && (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1" title={sellerLine(seller, t)}>{sellerLine(seller, t)}</p>
          )}
          {/* SLOT: identity badges — own row, always populated (slotBadges falls back to the
              status badge), so this never renders as an empty gap. A live scraped/reshare
              listing shows its age (color-coded toward the platform's typical auto-expiry)
              instead of a flat "On market". */}
          <div className="flex flex-wrap items-center gap-1 mt-1">{slotBadges()}</div>
          </>
          )}
            {/* SLOT: provenance + signals row (platform pill, source caption, LEAD/DRAFT
                badges, match pill) — stays inline until a later increment needs to vary it
                per category; the SLOT ordering contract is what increments build on. */}
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1.5">
              {/* Cascade member cards still show their platform pill (the face card's colored
                  chips carry it otherwise). The "source ad / agent repost" role text was removed
                  (2026-07-05 UI cleanup) — the source card is now identified by being the ONLY
                  card that shows the 🌐 scraped-listing line below. */}
              {isExternalListing && !platformChips?.length && item.listing?.source && (
                <span className="text-xs">
                  <PlatformPill source={item.listing.source} />
                </span>
              )}
              {/* 🌐 line marks the source ad (scraped_listing_readonly). Property-cluster heads
                  get the editable "✎ my property" badge instead. Both carry the capture time. */}
              {(!isExternalListing || kindOf(item) === 'scraped_listing_readonly') && (
                <span className="text-xs text-muted-foreground">
                  {/* PROVENANCE, NOT A SHRUG (owner ruling 2026-08-03). This slot used to read
                      a bare "შენახული"/"saved listing" — a word that says an event happened and
                      nothing about who caused it, while the capturer's name was duplicated on
                      the line above. It now answers the actual question: which of our people
                      put this in the system, or whether the crawler found it.
                      THREE states, because the data has three (see capture_source on the
                      server): a name, the crawler, or genuinely unknown — 22,130 active
                      listings carry raws stamped with neither, from before the crawler
                      recorded its session. Unknown keeps the old generic wording rather than
                      guessing "crawler", which would be wrong on half the corpus. */}
                  {item.__isProperty
                    ? (item.__source === 'manual' ? t('desk_entry_my_property_manual')
                      : item.__source === 'ai' ? t('desk_entry_my_property_ai')
                      : item.listing?.scraped_by_name ? tLog('desk_entry_my_property_added_by', { agent: item.listing.scraped_by_name })
                      : crawlerName(item.listing) ? tLog('desk_entry_my_property_crawler', { bot: crawlerName(item.listing) })
                      : item.listing?.capture_source === 'form' ? t('desk_entry_my_property_form')
                      : t('desk_entry_my_property_scraped'))
                    : isExternalListing
                      ? (item.listing?.scraped_by_name ? tLog('desk_entry_scraped_listing_added_by', { agent: item.listing.scraped_by_name })
                        : crawlerName(item.listing) ? tLog('desk_entry_scraped_listing_crawler', { bot: crawlerName(item.listing) })
                        // core 2006: filled in by a person on a sentavus fill form — never "found by Argo"
                        : item.listing?.capture_source === 'form' ? t('desk_entry_scraped_listing_form')
                        : t('desk_entry_scraped_listing_caption'))
                    // A client/property filled through a sent fill-link (core_fill.js stamps
                    // channel='client_form') is NOT an AI paste — the client filled the form
                    // themselves. The channel already rides on the history item (server select
                    // intake(...,channel)), so this is label-only; humanEdited stays a no-op here.
                    : item.intake?.channel === 'client_form' ? t('desk_entry_filled_via_link')
                    // an owner offered it in a WhatsApp chat (core 4151) — not something the agent pasted
                    : item.intake?.channel === 'whatsapp' ? t('desk_entry_from_whatsapp')
                    : humanEdited ? t('desk_entry_you_edited') : t('desk_entry_ai_created')}
                  {/* Creator attribution (2026-07-16): created_by_agent_name is resolved
                      server-side (/api/core/proposals) from created_by_agent_id — for a
                      fill-link card this is the agent who MINTED and sent the link, for an
                      edited card the agent who saved. Admin/manager feed had no name at all. */}
                  {item.created_by_agent_name && <> · <Icon name="user" size={11} /> {item.created_by_agent_name}</>}
                  {/* Listing cards get the explicit დაემატა/განახლდა line below instead — keep this
                      relative stamp only where there's no stack-dates line (client/property cards). */}
                  {item.created_at && !stackDates?.created && <> · <span title={new Date(item.created_at).toLocaleString()}>{relTime(item.created_at)}</span></>}
                </span>
              )}
              {/* Agent-repost cards (listing_readonly) still show WHEN the reshare link was
                  captured to the DB (listed_at) — every listing card gets a time, not just scrapes. */}
              {isExternalListing && kindOf(item) === 'listing_readonly' && item.created_at && (
                <span className="text-xs text-muted-foreground" title={new Date(item.created_at).toLocaleString()}>{tLog('desk_entry_reposted', { datetime: relTime(item.created_at) })}</span>
              )}
              {glow === 'blue' && <Badge label={glowClickable ? t('desk_entry_possible_match') : t('desk_entry_matched_contact')} variant="yellow" />}
              {/* LEAD badge (designer board 2026-07-23): a lead card must SAY it's a lead
                  wherever it renders outside the Leads category (home cockpit, future mixed
                  results) — set by the producing feed (LeadsFeed / homeLeads), never derived
                  here. ⚠️ Deduplicated 2026-08-03 (owner: "it says lead twice, one is
                  redundant"): when the chips band below will already render the 🎯 LEAD
                  pill (lead_status open/working/engaged, B105 — the one with the "unworked
                  supply" hint), this badge yields. It still renders for lead cards whose
                  lead_status doesn't drive the pill (e.g. archived leads). */}
              {item.lead_badge && !['open', 'working', 'engaged'].includes(item.listing?.lead_status) &&
                <Badge label={`LEAD · ${t(item.lead_badge === 'archive' ? 'desk_lead_archive' : 'desk_lead_active')}`} variant="amber" />}
              {/* DRAFT badge (owner ruling 2026-07-23, same vocabulary as LEAD): a manual/AI
                  property draft must SAY it's a draft next to scraped listings — the two card
                  species looking accidental violated "inherited must look distinct". ✎ manual /
                  🤖 AI-created, the same provenance split the caption line already draws. */}
              {!sub && objType === 'property' && !isExternalListing && !item.lead_badge && (
                <Badge label={`${humanEdited || !item.intake?.raw_text ? '✎' : '🤖'} ${t('desk_draft_badge')}`} variant="blue" />
              )}
              {/* Client ACTION signals (match pill, hot lead, fill-these nags) moved to the
                  Client card's constant strip below (A→C increment 3) — this row is
                  provenance only now. */}
            </div>
            {/* The stack's date facts render as CHIPS in the badge row above (dateChips,
                owner 2026-08-03) — the separate text date line this slot used to hold is
                gone: badges and dates are ONE line. */}
          {/* ⚠️ INSIDE THE TEXT COLUMN, deliberately (owner request 2026-07-30). This row
              used to be a full-width sibling BELOW the image+text flex row, which is what
              capped the image height: the media column could only stretch to the bottom of
              the text, never through the chips band. Living in the text column it indents to
              the text’s left edge — right of the image, but not flush right — and frees that
              whole band for the image to fill. Moving it back out re-caps the image. */}
          {/* One card per property posting, platforms on its face — the same ad goes to all three
              platforms as separate listing rows (0041), and burying them in the modal's ALSO POSTED
              ON meant the card face showed only whichever platform happened to be posted last.
              pointer-events-auto is belt-and-braces since 2026-07-31 (the stack wrapper no
              longer blankets the front card — see the ⚠️ in HistoryGroup); these are real
              external links and must stay clickable on a stack regardless. */}
          {/* views?.mine is in this list for a reason: on a card whose ONLY chip is the
              "seen by me" marker the band would otherwise not render at all, and the marker
              would be invisible on exactly the plain cards it is most useful on. */}
          {/* ⚠️ REDUNDANCY GATE (owner, 2026-08-04 mock review: "make sure you remove redundant
              things"). When the state column is shown, every fact it carries LEAVES this band:
              platform chips (the owner's-ads dot row + the chain panel say where it is posted),
              the seen ✓ and the eye count (the column's "Seen by 4 · me ✓" is both), and LEAD
              (inline on the price row, where the mock puts it). What stays here are the
              exceptions the column does not state: lifecycle DONE, a disputed district, the
              ×N-agents reach. The same fact must never render twice on one face. */}
          {((!showState && (platformChips?.length > 0 || views?.total > 0 || views?.mine?.last_at || ['open', 'working', 'engaged'].includes(item.listing?.lead_status)))
            || ((!showState || !chain) && item.listing?.family_agent_count > 1)
            || item.listing?.lifecycle_status || districtDisputed) && (
            /* LEFT-aligned. This row went right on 2026-07-30 by request and came back left the
               same day, once the enlarged image changed how the face reads. Left is the settled
               answer — do not move it right again without being asked. */
            <div className="flex flex-wrap gap-1.5 mt-2 pointer-events-auto">
              {/* B105 (owner ruling 2026-07-22): agents SHOULD see leads but must tell them
                  apart — an unpromoted crawler capture is NOT worked supply; nobody has
                  spoken to this owner through us yet. promoted renders as normal supply
                  (it IS supply); dead never reaches hunting surfaces. */}
              {!showState && ['open', 'working', 'engaged'].includes(item.listing?.lead_status) && (
                <span className={pill('warn', { extra: 'font-medium' })}
                  title={t('desk_lead_badge_hint')}>
                  <Icon name="target" /> {t('desk_lead_badge')}
                </span>
              )}
              {/* B274: leads the rest of the band on purpose. Every other chip states
                  something we KNOW about this card; this one says the card may be filed in
                  the wrong place, which qualifies all of them. */}
              {districtDisputed && (
                <DistrictDisputeChip listing={item.listing} districts={districts} onVote={onDistrictVote} />
              )}
              {/* Deal closed (0086): agent-sourced sold/rented lifecycle. Leads the row —
                  "this one is DONE" outranks where it was posted. */}
              {item.listing?.lifecycle_status === 'status_sold' && (
                <span className={pill('ok', { extra: 'font-medium' })}>
                  <Icon name="flag" /> {t('desk_entry_closed_sold')}
                </span>
              )}
              {item.listing?.lifecycle_status === 'status_rented_out' && (
                <span className={pill('ok', { extra: 'font-medium' })}>
                  <Icon name="flag" /> {t('desk_entry_closed_rented')}
                </span>
              )}
              {/* Chip colored by platform — same palette as the Listings tab tallies, so
                  "which site is this posting on" reads the same everywhere. The ORIGIN ad's
                  chip is a separate fact from "our posting on that platform" (see the
                  platformChips builder) — until 2026-07-17 both rendered identically, so a
                  card could show what looked like a duplicate "SS.ge · SS.ge"; the origin chip
                  is dashed + captioned so the distinction is visible on the face.
                  Rendering + hover live in PlatformChip (above) — hover needs a hook and these
                  are inside a .map(). */}
              {/* Gone when the state column shows (owner 2026-08-04): the owner's-ads dots ARE
                  these chips — same builder, same hover, dot form. */}
              {!showState && (platformChips || []).map(c => {
                const pk = platformKey(c.source)
                return <PlatformChip key={c.id} c={c} pk={pk} label={pk ? PLATFORM_LABEL[pk] : c.source}
                  propertyId={item.listing?.property_id || null}
                  caption={c.origin ? t('desk_entry_scraped_listing_caption') : null} />
              })}
              {/* The chain's door lives in the state column (design 2b) when there is one; this
                  chip is the fallback for cards that get no column — a narrow/sub layout must
                  not lose the door entirely. */}
              {chain && !showState && (
                <button onClick={e => { e.stopPropagation(); setChainOpen(v => !v) }}
                  className={pill('neutral', { door: true, extra: `bg-background ${chainOpen ? 'text-foreground border-primary/40' : 'text-muted-foreground'}` })}>
                  <Icon name="layers" /> {t(chain.known ? 'desk_chain_title' : 'desk_chain_dupes_title')}
                  {' · '}{chain.rows.length}
                </button>
              )}
              {/* Whole-family reach (0084): how many agents have this property reposted, plus the
                  total posting count — a scope=mine feed only loads the agent's OWN rows, so this
                  server-shipped count is the only way the face can say "4 other agents also repost
                  this" without opening the lineage modal. Shown to agent AND admin. Not a link —
                  clicking the card opens the full family in the modal. Gated at ≥2 agents: a single
                  agent cross-posting to 3 platforms (the common case) is already told by the platform
                  ×N chips; the badge earns its space only when OTHER agents also repost. */}
              {/* The chain door carries this when there IS one; without a chain there is no
                  door, so the chip is the only place left for it. */}
              {(!showState || !chain) && item.listing?.family_agent_count > 1 && (
                <span title={tLog('desk_entry_family_reach_tooltip', { agents: item.listing.family_agent_count, total: item.listing.family_listing_count ?? '?' })}
                  className={pill('neutral', { extra: 'bg-background text-muted-foreground' })}>
                  <Icon name="users" /> ×{item.listing.family_agent_count}
                </span>
              )}
              {/* ✓ I HAVE SEEN THIS (owner request 2026-07-31: "mark on card and date when
                  agent has seen it on hover"). Deliberately a separate chip from the eye
                  count beside it, not a recolour of it: they answer different questions —
                  "have I covered this" vs "how much attention has it had" — and an agent
                  scanning for their own unworked cards must not have to read a number to
                  find out. Muted, so a seen card reads as quieter without the whole card
                  changing colour (dimming the card was the alternative and was rejected:
                  muted styling on these cards already means gone/expired and inherited
                  address, and a third meaning for the same signal would make all three
                  ambiguous). Hover carries the date, which is the part that cannot be
                  glanced. */}
              {!showState && views?.mine?.last_at && (
                <span title={[
                  tLog('desk_entry_seen_by_me_at', { datetime: new Date(views.mine.last_at).toLocaleString() }),
                  views.mine.count > 1 ? tLog('desk_entry_seen_by_me_times', { n: views.mine.count }) : null,
                ].filter(Boolean).join('\n')}
                  className={pill('neutral', { extra: 'bg-background text-muted-foreground' })}>
                  <Icon name="check" /> {t('desk_entry_seen_by_me')}
                </span>
              )}
              {/* Who else saw it — hover lists the colleagues and when.
                  ⚠️ NOT rep-gated any more (owner ruling 2026-07-31, server side in
                  core_match.js /listing-views): until then this only rendered on cards you
                  represent. It now renders on every card, so the same chip carries a wider
                  disclosure than its 2026-07-15 design intended — read that server comment,
                  including the B111 tension, before treating this count as a ranking signal.
                  Your OWN opens are excluded server-side; they are the chip above. */}
              {!showState && views?.total > 0 && (
                <span title={(views.viewers || []).map(v => `${v.name || '?'} — ${v.last_at ? new Date(v.last_at).toLocaleString() : '?'}${v.count > 1 ? ` (×${v.count})` : ''}`).join('\n')}
                  className={pill('info')}>
                  <Icon name="eye" /> {views.total}
                </span>
              )}
            </div>
          )}
          {/* 2c — the NARROW variant. Below the column's breakpoint there is no room beside the
              facts, so the same rows become a full-width key/value block: same grammar, stacked
              instead of beside (designer, turn 6). ONE component renders both, so the two
              layouts cannot drift into two different answers about what a card states. */}
          {showState && (
            <div className={`${stateBeside ? 'desk-state-stacked ' : ''}mt-2 pt-1.5 border-t border-border/50 pointer-events-auto`}
              onClick={e => e.stopPropagation()}>
              {stateSlot || <StateBlock item={item} stackDates={stackDates} platformChips={platformChips} views={views}
                chain={chain} chainOpen={chainOpen} onToggleChain={() => setChainOpen(v => !v)} />}
            </div>
          )}
        </div>
        {/* SLOT: the aligned state column (design 1c). Fixed width and a hairline on its left —
            the alignment IS the feature, so it must not flex with the facts beside it. */}
        {stateBeside && (
          /* ⚠️ pt-7 (owner 2026-10-23 "sold rented selection is overflowing"): the absolute shelf corner (sold/rented + 🗄 + ♥,
             top-2 right-2) sat ON the column's first row ("აქტიური"); the column starts below that corner instead */
          <div className={`desk-state-beside w-56 shrink-0 border-l border-border/50 pl-3 flex-col pointer-events-auto ${!sub && item.listing?.property_id ? 'pt-10' : ''}`}
            onClick={e => e.stopPropagation()}>
            {stateSlot || <StateBlock item={item} stackDates={stackDates} platformChips={platformChips} views={views}
              chain={chain} chainOpen={chainOpen} onToggleChain={() => setChainOpen(v => !v)} />}
          </div>
        )}
        {/* ← closes the inner facts row; the tail below is its sibling INSIDE the body column,
            which is what puts the pill rail beside the photo rather than under it. */}
        </div>
      {/* Cooperation state on the card face, same row style as the platform chips — "is there
          a deal on this property and at what %" is a property-face fact (0046: identity =
          property + contact + intent), not something to bury behind a person lookup. Only on
          listing/property cards where the Desk resolved this card's property's cooperations. */}
      {/* SLOT: strip — FEED-SUPPLIED content (A→C increment 4): Meeting/Lead feeds hand
          their strip content in as a node; the CARD owns the slot position so every
          category's strip lives inside the card frame, not floating under it. The node
          brings its own hairline (MeetingStrip does) or plain chips (leads density). */}
      {/* ⚠️ Only when there is NO footer to ride (owner 2026-08-04: "this extra space is
          useless"). A lead card's counters used to open their own hairline band right above
          the footer rail — a whole row for two small numbers. When a footer exists the strip
          is handed to it as `startSlot` instead (see below); this branch keeps feeds whose
          cards have no footer from losing their strip entirely. */}
      {strip && !sub && !footerShown && <div className="pointer-events-auto">{strip}</div>}
      {/* SLOT: strip — CLIENT card (A→C increment 3): the same constant hairline row the
          Properties family got in increment 1, carrying the demand's ACTION signals —
          matches to work (match pill, can't-rule-out count from fill_match_count, client
          demands only — the enrichment keys on requirement_id), urgency, and the
          fill-these gaps. Moved here from the provenance row so every category's card
          ends in "what do I do with this" under one hairline. */}
      {!sub && objType === 'client' && (isHotLead || missingPhone || missingCrit.length > 0
        || demandMeetings.length > 0
        || (typeof item.match_count === 'number' && item.match_count > 0)) && (
        <div className="border-t border-border/50 mt-1.5 pt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
          {/* WHAT HAPPENED comes first — a closed deal or a booked viewing outranks every
              "fill this in" nag on the same row. The chip opens the meeting (stopPropagation:
              the card's own click opens the CLIENT, and both are wanted). */}
          {dealClosed && (
            <button type="button" className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 hover:underline"
              onClick={e => { e.stopPropagation(); onOpenMeeting?.(closedMeeting) }}>
              🏁 {t('desk_entry_deal_closed')}
            </button>
          )}
          {/* ⚠️ COUNTS WHAT HAPPENED, NOT EVERY ROW (fixed 2026-08-15 — the other half of the
              🏁 fix above, which had the identical defect). This counted `demandMeetings`
              unfiltered, so a client whose only two viewings were CANCELLED read "2 meetings"
              in the primary "what happened" slot — the slot whose own comment says a booked
              viewing outranks every fill-this-in nag — and the click fell through to
              whichever cancelled row sat first in array order. A cancelled meeting is a
              NON-outcome: the hour was given back. */}
          {!dealClosed && meetingsThatCount.length > 0 && (
            <button type="button" className="text-xs font-medium text-primary hover:underline"
              onClick={e => { e.stopPropagation(); chipMeeting && onOpenMeeting?.(chipMeeting) }}>
              🤝 {meetingsThatCount.length > 1
                ? tLog('desk_entry_meetings_n', { count: meetingsThatCount.length })
                : t('desk_entry_has_meeting')}
            </button>
          )}
          {typeof item.match_count === 'number' && item.match_count > 0 && !missingCrit.length && (
            <Badge label={tLog('desk_entry_match_pill', { count: item.match_count > 99 ? '99+' : item.match_count })} variant="blue" />
          )}
          {isHotLead && <Badge label={t('desk_entry_hot_lead')} variant="red" />}
          {missingPhone && <span className="text-xs text-amber-500"><IconText>{t('desk_entry_no_phone')}</IconText></span>}
          {missingCrit.length > 0 && (
            <span className="text-xs text-amber-500"><IconText>{tLog('desk_entry_fill_criteria', { fields: missingCrit.join(', ') })}</IconText></span>
          )}
        </div>
      )}
      {/* ── Defects read from the photographs (B1021, 2026-08-27) ────────────────────────
          Owner request, review 40: "there is NO FIELD for it need to add it in field". This
          is the only fact on the card face that comes from LOOKING at the flat rather than
          from what the advert says about itself, which is why it gets its own line rather
          than a slot in the footer's three-group rail (whose line budget is documented at
          CooperationChips and is already spent).

          ⚠️ IT IS A SIGHTING, NOT A VERDICT. Each chip names a surface someone can point at —
          `where_in_frame` and the photo numbers travel with the claim and land in the title —
          and says nothing about the building. The wording follows that: the chip is the
          defect's name, and the tooltip is where it was seen. Never "poor condition".

          ⚠️ ABSENCE DRAWS NOTHING, DELIBERATELY. There is no "no defects" state and there
          must not be: the promotion registry refuses to write `present=false` for these codes
          (0745), because a photograph showing no damp is evidence about a wall. A green tick
          here would be a claim nobody made — the mirror of the withheld-lock defect the
          diagnosis rules already name.

          📌 A cluster's defect can come from a SIBLING advert's photographs (the server
          expands through property_cluster_map). `own` is false then, and the chip renders
          muted with a tooltip saying so — the same "inherited value" convention the price and
          address already use, rather than presenting a neighbour advert's frame as this
          card's own. */}
      {/* ── What an agent found, and who (B1021 + the 2026-08-27 attribution ruling) ─────
          One renderer for the card face and the property modal — AgentFacts.jsx carries the
          two registers (defect warnings, and facts the advert never stated) and the ✦/✎ mark
          that names who filled each one. Nothing about "who said this" is decided here: a
          second copy of that on the card is exactly what the ruling ended.

          Placed above the constant action row rather than inside the footer's three-group
          rail, whose line budget is documented at CooperationChips and is already spent. */}
      {/* 0894 (owner 2026-09-06, pointing at this exact row: "instead of this we need that comment
          that describes this particular property with its specialties"). When the statistics
          found a differentiator AND the writer has rendered it, the note takes this slot; the
          agent-filled chips stay on the modal. A card with no note (ordinary flat, or not yet
          written) keeps the chips, so the transition never blanks a row that had information. */}
      {!sub && (distinct?.text_ka || distinct?.text_en)
        ? <DistinctNote row={distinct} className="border-t border-border/50 mt-1.5 pt-1.5" />
        : !sub && (
        <AgentFacts defects={defects} agentFacts={agentFacts} features={features}
          className="border-t border-border/50 mt-1.5 pt-1.5" />
      )}
      {/* developer card (core 1725): the developer's own floor plan, the card's flats highlighted — on click only */}
      {!sub && devCard && <DevFloorPlan propertyId={item.listing?.property_id} className="mt-1" />}
      {/* SLOT: strip (constant action row — C's fifth slot, increment 1) */}
      {/* `strip` alone also earns the rail (2026-09-06): the Meetings feed wires none of the four
          actions — a meeting card's doors are its own pills — and until now that meant NO rail at
          all, so its pills sat as state rows while every listing card kept its bottom rail. A feed
          that supplies rail content and no actions gets exactly its content: the rail's built-in
          contact pill is withheld (see contactListing below), or the card would draw two phones. */}
      {(onOpenCooperation || onAddMeeting || onCreateContract || onShare || onOpenTerms || strip) && !sub && (
        /* A→C increment 1 → 2.2 (owner ruling 2026-07-23 "Properties first", rolled to every
           category 2026-07-24): the hairline action row is CONSTANT across ALL card faces,
           not just the Properties family. Both the separator and the one-line contract now
           ride on CooperationChips itself — it is the only place that knows whether the row
           has any content, and the category gate that used to live here (`item.listing ||
           objType === 'property'`) would have drawn a hairline over an empty row for every
           client/meeting card the moment it was widened. Category-specific SIGNALS still
           differ (client match pill / hot-lead stripe above, feed strips); the action row's
           SHAPE no longer does — which is the whole point of C. */
        <CooperationChips
          cardFooter hairline
          /* The stack count / thread toggle rides the rail row instead of overlaying the
             card's bottom-right corner — see the cornerNode block above for why. */
          endSlot={cornerInFooter ? cornerNode : null}
          /* The feed's counters, on the footer's own row rather than a band above it. */
          startSlot={strip || null}
          /* 🔗 client links minted for THIS property (owner 2026-08-03). Batched one
             fetch per page by the feed, exactly like cooperations — never per card. */
          shares={shares}
          /* `wide` = the desktop single-column list card (`max-w-4xl`, ~900px), which is the
             only form with room to put the footer's three groups on ONE line. gallerySize is
             the width fact the card already has: 'thumb' is the row form, and since the
             entries view derives away from it under 768px it can only mean desktop-list now.
             'large'/'home' are the ~355px stack (mobile, and the desktop 3-up grid) and keep
             the stacked line budget. CardFooter re-gates on `md:` regardless. */
          wide={gallerySize === 'thumb'}
          contactListing={strip && !(onOpenCooperation || onAddMeeting || onShare || onOpenTerms) ? null : item.listing}
          // Whether a number is on file — supplied by the feed that knows (Leads reads the
          // payload's has_phone). null on every other feed = "not asked", which keeps the
          // pill solid; only an explicit false draws it dashed.
          contactKnown={contactKnown}
          /* 0374: whether this property's author phone has been reported as an agent's.
             Rides the 📞 pill (which turns `info`) instead of adding a fourth chip — the
             footer's line budget is why the rail has three groups and not four. */
          authorMark={authorMark}
          cooperations={cooperations}
          /* B868 (a): the OWNER'S OWN stated split from the ad, shown as a dashed chip when
             no cooperation exists yet — dashed is the reserved "AI-extracted, unconfirmed"
             meaning. A live cooperation silences it (human wins by absence, B893). */
          offer={coopOffer}
          // Restriction subjects arrive as pol_* feature codes — resolve to the feature's
          // display name here (features is already a card prop). AI-extracted subjects can
          // carry NOVEL codes with no feature row (pol_rus_ukr_bel — the extractor mints
          // them); the fallback humanizes the code (strip pol_, underscores → spaces) so a
          // vocabulary gap reads like words, not an identifier (owner-caught 2026-07-23).
          /* B237: `cleared` rides alongside `items` rather than replacing them — a listing
             can legitimately be BOTH ("no restrictions apart from the one I recorded"), and
             collapsing that to one state would hide the recorded stance. Passed even when
             items is empty, which is the whole point: that combination is the third state
             (asked, and there are none) and must not render as the bare add-door. */
          restrictions={(restrictions?.subjects?.length || restrictionsCleared?.cleared) ? {
            items: (restrictions?.subjects || []).map(s => {
              const f = (features || []).find(f => f.code === s.code)
              return {
                label: f?.name_ka || f?.name_en || s.code.replace(/^pol_/, '').replace(/_/g, ' '),
                stance: s.stance, ai: s.src !== 'agent',
              }
            }),
            cleared: !!restrictionsCleared?.cleared,
            clearedHidden: !!restrictionsCleared?.hidden,
          } : null}
          /* 2026-09-12: what the tenant must produce before the keys. Rent + currency ride
             along because a deposit stated as „ნახევარი თვე" is only money once there is a
             rent to halve — the projection returns the requirement either way and the pill
             shows the figure only when it has one. */
          payTerms={payTerms}
          payRent={item.listing?.price_usd ?? item.listing?.price ?? null}
          payRentCurrency={item.listing?.currency || null}
          /* `compact` dropped 2026-07-25 — the prop was removed from CooperationChips when
             the modal path stopped collapsing to the best deal; passing it did nothing. */
          privileged={privileged}
          meetings={cardMeetings?.length ? cardMeetings : null}
          onOpenMeeting={onOpenMeeting}
          onOpenTerms={onOpenTerms && item.listing?.listing_id
            && ['rent', 'daily_rent', 'sale'].includes(item.listing.intent)
            ? () => onOpenTerms(item.listing) : null}
          onOpen={onOpenCooperation ? c => onOpenCooperation({
            cooperationId: c.id, propertyId: c.property_id, contactPartyId: c.contact_party_id,
            // Card listing context rides along (2026-07-30): the combined DealEditor
            // seeds its TERMS tab from it — without listingId the terms half opened
            // empty when entered via an existing cooperation (owner-reported).
            propertyLabel: item.listing?.address || null,
            listingId: item.listing?.listing_id || null, listingIntent: item.listing?.intent || null,
            price: item.listing?.price ?? null, currency: item.listing?.currency || null,
          }) : () => {}}
          onAdd={onOpenCooperation && item.listing?.property_id && item.listing?.author_party_id
            ? () => onOpenCooperation({
                propertyId: item.listing.property_id,
                propertyLabel: item.listing.address || null,
                contactPartyId: item.listing.author_party_id,
                contactName: item.listing.author_name || null,
                // Deal type is a fact the listing already states (2026-07-17 prefill fix).
                seedIntent: coopIntentOf(item.listing.intent),
                seedCurrency: item.listing.currency || null,
                seedPrice: item.listing.price ?? null,
                // B868 (b): the owner's stated figure prefills the commission — a prefill the
                // agent confirms by saving, never a written fact. Its TYPE follows core 1200's basis
                // (owner ruling 2026-09-16: unless the advert says an AGENCY wrote it, the percent is
                // the owner's fee to the agency, not a split between agents):
                //   commission_share (agency author) → coop_split
                //   price_percent (under 20)          → percent of the price
                //   owner_fee on a RENT               → percent: the rent cooperation's price is the
                //                                       monthly rent, so 50 = half a month
                //   owner_fee on a SALE               → NOT seeded: 50% of a sale price is no fee, and
                //                                       what an owner's „50/50" on a sale means is open
                seedCommission: coopOffer && coopOffer.percent != null && (
                  coopOffer.basis === 'commission_share' || coopOffer.basis === 'price_percent'
                  || (coopOffer.basis === 'owner_fee' && coopIntentOf(item.listing.intent) === 'rent'))
                  ? coopOffer.percent : null,
                seedCommissionType: coopOffer?.basis === 'commission_share' ? 'coop_split' : 'percent',
                // WHICH AD this cooperation was opened from (2026-07-28). open_cooperation
                // has always written cooperation_listing when given a listing id, and the
                // server route has always forwarded it — but no caller ever sent one, so
                // the table held 0 rows against 1,360 cooperations. Evidence, never
                // identity (0046: "discovery is not identity"); it just means the tie is a
                // recorded fact instead of a property_id join guessed at read time.
                listingId: item.listing.listing_id || null,
              })
            : null}
          onAddMeeting={onAddMeeting && (item.listing || item.result?.property_id) ? async () => {
            // Same core-pick seed shape the lineage modal's "Add meeting" builds. The ranked
            // contact resolver is fetched here on click (the card face never loads it) so this
            // path gets the SAME treatment: owner pre-filled from the resolver's default (never
            // an agent), and a bridged-agent contact row (is_agent + agent_id) lets ViewingForm
            // auto-pick that agent — see commit c6245df. Resolver unreachable → plain row
            // fallback, contact blank on reshares (row author = our own agent; blank beats wrong).
            const DEAL = { sale: 'trade', rent: 'lease' }
            const l = item.listing
            if (l) {
              const activeCoop = (cooperations || []).find(c => c.status !== 'rejected' && c.status !== 'ended') || null
              const pc = l.property_id ? await getPropertyContacts(l.property_id).catch(() => null) : null
              const dflt = pc?.contacts?.find(c => c.party_id === pc.default_party_id) || null
              onAddMeeting({
                __source: 'core', id: l.listing_id, property_id: l.property_id || null,
                address: l.address, rooms_count: l.rooms, apt_floor: l.floor,
                total_floors: l.floors_total, area_sq_m: l.area_sqm,
                deal_type: DEAL[l.intent] || l.intent, price: l.price, price_unit: l.currency,
                source_url: l.source_url,
                contact_name: dflt ? dflt.name : (l.is_reshare ? null : (l.author_name || null)),
                contact_phone: dflt ? dflt.phone : null,
                contacts_ranked: pc?.contacts?.length ? pc.contacts : null,
                default_contact_party_id: pc?.default_party_id || null,
                cooperation: coopMeetingSeed(activeCoop),
              })
              return
            }
            // Property card (extract_listing draft): the PROPERTY is the primary link — a viewing
            // is about showing a property, which persists even when its ad expires. The draft's
            // executed listing_id rides along as the optional secondary ref. Facts come off the
            // proposal's own input; contacts from the same ranked property resolver as above.
            const propId = item.result?.property_id
            const inp = item.proposed?.input || {}, pr = inp.property || {}, lst = inp.listing || {}
            const pc = await getPropertyContacts(propId).catch(() => null)
            const dflt = pc?.contacts?.find(c => c.party_id === pc.default_party_id) || null
            onAddMeeting({
              __source: 'core', id: item.result?.listing_id || null, property_id: propId,
              address: pr.address || null, rooms_count: pr.rooms ?? null, apt_floor: pr.floor ?? null,
              total_floors: pr.floors_total ?? null, area_sq_m: pr.area_sqm ?? null,
              deal_type: DEAL[lst.intent] || lst.intent || null, price: lst.price ?? null, price_unit: lst.currency || null,
              source_url: null,
              contact_name: dflt?.name || null, contact_phone: dflt?.phone || null,
              contacts_ranked: pc?.contacts?.length ? pc.contacts : null,
              default_contact_party_id: pc?.default_party_id || null,
              cooperation: null,
            })
          } : null}
          onCreateContract={onCreateContract && (item.listing?.property_id || item.result?.property_id)
            ? () => onCreateContract(item.listing?.property_id || item.result?.property_id) : null}
          // Client share link (MEETINGS_MODEL §5). Property-anchored like the
          // meeting chip: a share is about the PROPERTY, and the listing it was
          // minted from may expire while the share stays valid.
          onShare={onShare && (item.listing?.property_id || item.result?.property_id) ? () => onShare({
            propertyId: item.listing?.property_id || item.result?.property_id,
            propertyLabel: item.listing?.address || null,
            // Seeded from the listing purely for convenience — the value the agent
            // confirms is FROZEN on the share, and never re-read afterwards.
            seedPrice: item.listing?.price ?? null,
            seedCurrency: item.listing?.currency || null,
          }) : null}
          // Repost to portals (B244) — CardFooter derives the listing id itself from
          // contactListing, so this is a straight pass-through.
          onRepost={onRepost} onShareOthers={onShareOthers}
        />
      )}
        {/* ← closes the body column, then the desk-card-row itself. */}
        </div>
      </div>
      {/* Opens across the card's FULL WIDTH, under the whole face (design 2b) — the tree needs
          the room, and the face above it stays exactly as it reads when closed. Outside the
          flex row on purpose: inside the body column it would be squeezed into two thirds AND
          would drag the now-stretched photo (index.css §1) to the panel's full height.
          ⚠️ It sits BELOW the pill rail since 2026-08-09 (it used to open above it). The rail
          moved into the body column and the panel could not follow, so this is the only order
          left in which both keep the width they need. */}
      {chain && chainOpen && <ChainPanel chain={chain} propertyId={item.listing?.property_id || null} />}
      {/* Fallback placement: a card with no action row has nothing to sit ON, so the chip
          keeps its old bottom-right overlay (and the card keeps the pb-8 strip that reserves
          room for it). Expanded members and the collapsed front card still share one anchor
          point for "thread state lives here" — it just moved onto the rail. */}
      {cornerNode && !cornerInFooter && (
        <span className="absolute z-10 bottom-2 right-2">{cornerNode}</span>
      )}
    </div>
  )
}

// Groups the flat, append-only event list into one visual thread per demand — purely a
// display convenience, nothing is merged or overwritten in the data. `history` is newest-first
// (server order), so within each group items stay newest-first too; the group's position in
// the overall list is set by its most recent event.
export function groupHistory(history) {
  const groups = []
  const indexByReq = new Map()
  for (const item of history) {
    const reqId = item.result?.requirement_id
    if (reqId && indexByReq.has(reqId)) {
      groups[indexByReq.get(reqId)].items.push(item)
    } else {
      groups.push({ key: reqId || item.id, items: [item] })
      if (reqId) indexByReq.set(reqId, groups.length - 1)
    }
  }
  return groups
}

// React.memo (B122 Phase 2 step 2, handoff §5.1): a feed state landing must re-render only
// the cards whose group actually changed. Safe BECAUSE of step 1 — group objects keep
// identity when content is unchanged (reconcileGroups), and every callback prop the feed
// passes is identity-stable (latest-ref wrappers / per-key toggle cache / setState fns in
// IntakeTab). Currency display still updates on toggle: HistoryItem reads MoneyCtx, and
// context re-renders bypass memo by design. ⚠️ If you add a prop, keep it identity-stable
// or the memo silently degrades back to render-everything (no error — re-measure with
// window.__feedPerf if the Desk slows again).
export const HistoryGroup = memo(function HistoryGroup({ group, availableFrom = null, reportedGone = 0, districts, features, glow, glowClickable, onCardClick, expanded, onToggle, searchPolygons, gallerySize, shade = 0, coopsByProperty, defectsByProperty = null, agentFactsByProperty = null, distinctByProperty = null, devCardsByProperty = null, sellersByProperty = null, coopOffersByProperty = null, sharesByProperty = null, restrByListing, termsByListing = null, clearByListing = null, viewsByProperty, onOpenCooperation, onAddMeeting, onCreateContract, onShare, onRepost = null, onShareOthers = null, onOpenTerms, meetingsIndex, onOpenMeeting, clientMeetings = null, privileged = false, favorites = null, onToggleFavorite = null, archived = null, onToggleArchive = null, onReportGone = null, authorMark = null, strip = null, contactKnown = null, onDistrictVote = null, stateSlot = null }) {
  const [head, ...rest] = group.items
  // A thread's identity lives on its CREATE event (the client's name), not on the latest
  // amend. Anchor the head card to that so the list stays scannable by who, not by what-changed.
  // Fall back to the oldest item if the create scrolled past the 20-row window.
  const isListingCascade = kindOf(head) === 'listing_readonly' || kindOf(head) === 'scraped_listing_readonly'
  // F-033 (2026-09-05): a LISTING cascade is NOT anchored on its last member any more. The head
  // of a cascade is a CHOSEN face (IntakeTab.unifiedGroups.pickRep / deskFeed's promotion: the
  // member that names the matched street, else the origin, else the oldest), and overriding
  // its title with the last member's address put an agent's ss.ge reshare ("ბენდელიანის ქ.")
  // over the owner's own Chavchavadze-avenue ad on the owner's screen -- the FOURTH place that
  // decided a card's address. The head's own summary titles the card; client threads keep
  // their create-event anchor exactly as before.
  const anchor = rest.length && !isListingCascade
    ? (group.items.find(i => kindOf(i) === 'extract_requirement') || group.items[group.items.length - 1])
    : null
  // The anchor's title travels as {title, city, rest}, not a bare string: a LISTING cascade
  // (same property posted to myhome/ss/korter) anchors on its oldest listing, so the override
  // is an ADDRESS, and a flattened string would strip the city back out of the one place the
  // card can weight it. Caught 2026-07-27 — stacked Rustavi cards printed their city unweighted
  // while single-listing cards next to them weighted it.
  const headAnchor = anchor ? summarize(anchor, districts, features) : null
  const headTitle = headAnchor && { title: headAnchor.title, city: headAnchor.titleCity, rest: headAnchor.titleRest }
  const noun = isListingCascade ? 'listings' : 'updates'
  // "All the agent's postings of this property in one place": the same ad goes to myhome/ss/
  // korter as SEPARATE listing rows (own natural keys, 0041) — chips on the card face, each
  // with live status + direct link, the origin ad marked as such. AGGREGATED one chip per
  // platform (×N when several postings share it — ten reshares must not become ten chips):
  // the chip links to the newest posting on that platform and its dot is green if ANY of them
  // is still on market; the full per-posting list stays one click away (expand / modal). Only
  // on the group's face card — an expanded member IS one posting, chips there would repeat.
  const adTexts = useAdText()
  const platformChips = useMemo(() => {
    if (!isListingCascade) return null
    const bySource = new Map()   // Map keeps first-seen order; items are newest-first, so a chip's url = that platform's newest posting
    for (const i of group.items) {
      const l = i.listing
      if (!l?.source) continue
      // ⚠️ ORIGIN IS A LINEAGE FACT, NOT A ROW KIND (B652, owner-caught 2026-08-12: "card face
      // says მესაკუთრის განცხადება and quantities but it also counts agents reshares… this way
      // card face lies"). `kindOf(i) === 'scraped_listing_readonly'` asks "is this a scraped
      // listing row?", which is true of EVERY listing on this card — our reposts are scraped back
      // into core too. So every posting keyed to `#origin`, and the owner's-ads row printed the
      // whole cascade: the reported card read 4 · 4 · 3 = 11 postings under a label naming 3.
      // The chain panel beside it was right all along because `chainModel.js` asks the lineage
      // (`ours: !!l.is_reshare`). This now asks the same question, so the two agree by
      // construction rather than by coincidence — the ONE definition rule.
      // A repost falls through to the plain `l.source` key below, where it is still a chip and
      // still linkable; it simply stops claiming to be the owner's ad.
      const origin = kindOf(i) === 'scraped_listing_readonly' && !l.is_reshare
      // The origin ad keeps its own chip even on a shared platform — "posted on ss.ge" and
      // "the source ad lives on ss.ge" are different facts.
      const key = origin ? `${l.source}#origin` : l.source
      const c = bySource.get(key)
      // The ad's own words, per PLATFORM (2026-07-30). A chip aggregates xN postings of one
      // source, so the FIRST one's text wins — items are newest-first, i.e. what that
      // platform's ad says now. `??=` so a later, older posting cannot overwrite it.
      // ⚠️ THIS LISTING'S OWN TEXT ONLY — never the origin's (owner ruling 2026-08-02:
      // "non-source chips hover shouldn't show comment"). A repost of ours legitimately has no
      // ad text: register_reshare bare-creates it and the owner's words belong to the SOURCE
      // ad. Borrowing them onto a repost chip would attribute the owner's words to our posting,
      // which is the same mistake the reshare rule forbids elsewhere (a repost's content is
      // ours — the extension rewrites area and price for marketing — so it must never be read
      // back as the owner's). A blank hover on a repost chip is the correct answer.
      // (An origin fallback was briefly shipped here and reverted the same day.)
      const adText = adTexts[l.listing_id]?.text || null
      // Every ad the chip stands for, not just the first-seen one (2026-08-03). A ×N chip used
      // to keep ONE url and discard the rest, so the link you got was whichever posting the loop
      // happened to see first — and on a cascade spanning two property rows (one identity
      // cluster, which is the normal case here) that could be the SIBLING property's ad, opened
      // from a chip captioned as this card's saved original. Owner-reported as "the pills are
      // mismatched reposts and original links". Recency also made the destination unstable: the
      // same chip pointed somewhere else once a newer repost landed, with nothing on the card
      // changing. A chip that stands for N ads must offer N links, never guess one.
      // price/currency ride along (owner 2026-08-04: "show price in hover popup — each listing
      // has its own price"). Per-AD, never the chip's: two postings of one flat routinely ask
      // different money, and that difference is the reason to open this list at all.
      const ad = l.source_url ? { id: l.listing_id, url: l.source_url, at: l.listed_at, status: l.status, adId: l.source_listing_id || null, price: l.price ?? null, currency: l.currency || l.price_unit || null } : null
      if (c) {
        c.count++
        if (l.status === 'active') c.status = 'active'
        if (adText) c.adText ??= adText
        if (ad) c.ads.push(ad)
      } else {
        bySource.set(key, { id: key, source: l.source, status: l.status, source_url: l.source_url, origin, count: 1, adText, ads: ad ? [ad] : [] })
      }
    }
    return [...bySource.values()]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.items, isListingCascade, adTexts])

  // The chain / duplicates model (design 2b + 6a) — built ONCE per group, from the members the
  // card already holds. useMemo because HistoryItem is memoised on reference identity: a fresh
  // object every render would silently un-do that (see the render-identity note in this file).
  const chain = useMemo(
    () => (isListingCascade ? buildChain(group.items) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [group.items, isListingCascade])
  // Cooperations for THIS group's property — a cascade's members share one property (reshares
  // of one origin ad), but collect across all items anyway so a mixed group can't hide a row.
  // Deduped by cooperation id since several listings resolve to the same property.
  const groupCoops = useMemo(() => {
    if (!isListingCascade || !coopsByProperty) return null
    const seen = new Map()
    for (const i of group.items) {
      for (const c of coopsByProperty[i.listing?.property_id] || []) seen.set(c.id, c)
    }
    return [...seen.values()]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.items, isListingCascade, coopsByProperty])
  // Restrictions on the STACK face merge across the cascade (a stance on the origin ad
  // must show on the collapsed stack, same rule as groupCoops above). Agent-recorded
  // wins over scrape per subject — mirrors the overlay's own merge in IntakeTab.
  const groupRestrictions = (() => {
    if (!isListingCascade || !restrByListing) return null
    const bySubject = new Map()
    for (const i of group.items) for (const s of (restrByListing[i.listing?.listing_id]?.subjects || [])) {
      const prev = bySubject.get(s.code)
      if (!prev || (prev.src === 'scrape' && s.src === 'agent')) bySubject.set(s.code, s)
    }
    return bySubject.size ? { subjects: [...bySubject.values()] } : null
  })()
  // B237: a cascade is one property's listings, and "asked, none apply" is an answer about
  // the OWNER — so any member carrying a live clear answers for the stack. Same direction as
  // groupRestrictions above, which already pools stances across the cascade.
  const groupCleared = isListingCascade && clearByListing
    ? (group.items.find(i => clearByListing[i.listing?.listing_id]?.cleared)
        ? clearByListing[group.items.find(i => clearByListing[i.listing?.listing_id]?.cleared).listing.listing_id]
        : null)
    : null
  // Client links for the STACK face — pooled across the cascade for the same reason
  // groupCoops is: a stack's members are one property's ads, and a link minted against that
  // property must show on the collapsed front card, not only after expanding it. Deduped by
  // share id, and left in the server's newest-first order.
  const groupShares = useMemo(() => {
    if (!isListingCascade || !sharesByProperty) return null
    const seen = new Map()
    for (const i of group.items) {
      for (const sh of sharesByProperty[i.listing?.property_id] || []) seen.set(sh.id, sh)
    }
    return [...seen.values()]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.items, isListingCascade, sharesByProperty])
  const coopProps = isListingCascade
    ? { cooperations: groupCoops, defects: defectsByProperty?.[head.listing?.property_id] || null, agentFacts: agentFactsByProperty?.[head.listing?.property_id] || null, distinct: pickDistinct(distinctByProperty?.[head.listing?.property_id], head.listing?.intent), devCard: devCardsByProperty?.[head.listing?.property_id] || null, seller: sellersByProperty?.[head.listing?.property_id] || null, coopOffer: coopOffersByProperty?.[head.listing?.property_id] || null, shares: groupShares, restrictions: groupRestrictions, restrictionsCleared: groupCleared, views: viewsByProperty?.[head.listing?.property_id], onOpenCooperation, onAddMeeting, onCreateContract, onShare, onRepost, onShareOthers, onOpenTerms, meetingsIndex, onOpenMeeting, privileged, favorites, onToggleFavorite, archived, onToggleArchive, onReportGone, authorMark, contactKnown, onDistrictVote }
    : {}

  // ── B259: which member matched (owner ruling 2026-07-31) ────────────────────────────────
  // "Display the cluster truth first. Explain the matching evidence second." A cluster passes
  // a filter if ANY member does (`bool_or` in the RPC's status_of), so a stack can surface for
  // "80-150k" on the strength of one sibling while the card's own price sits outside the range.
  // That is a TRUE result presented misleadingly, and the fix is disclosure, not narrowing the
  // predicate (a genuinely cheaper sibling ad is a real result).
  //
  // Derived entirely from members already on the card — deliberately NOT an RPC parameter
  // (§2.6: don't deepen the coupling for a presentation concern). `matched_filter` is set
  // server-side per row and is false everywhere when no filter is active, so the three
  // conditions the ruling asks for collapse into one expression: >1 member, some matched,
  // not all matched. All-matched is suppressed as noise — it explains nothing.
  const matchNote = (() => {
    if (!isListingCascade || group.items.length < 2) return null
    const ls = group.items.map(i => i.listing).filter(Boolean)
    const matched = ls.filter(l => l.matched_filter === true)
    if (!matched.length || matched.length === ls.length) return null
    // Name the CHEAPEST matched member: with a price filter on, that is the one that pulled
    // the cluster in. Ties and missing prices fall back to the first, which is stable because
    // group.items is already deterministically ordered.
    const withPrice = matched.filter(l => l.price != null)
    const pick = withPrice.length
      ? withPrice.reduce((a, b) => (Number(b.price) < Number(a.price) ? b : a))
      : matched[0]
    return { count: matched.length, total: ls.length, listing: pick }
  })()

  if (!rest.length) {
    return (
      <HistoryItem item={head} matchNote={matchNote} availableFrom={availableFrom} reportedGone={reportedGone} districts={districts} features={features} glow={glow} glowClickable={glowClickable}
        onOpen={() => onCardClick(group)} searchPolygons={searchPolygons} gallerySize={gallerySize}
        stackDates={isListingCascade ? listingStackDates(group.items) : null}
        clientMeetings={clientMeetings} onOpenMeeting={onOpenMeeting}
        platformChips={platformChips} chain={chain} strip={strip} stateSlot={stateSlot} {...coopProps} />
    )
  }

  // CLIENT thread = ONE card, final state only, NO expand. The events behind it are amends/
  // close/reopen on one demand — versions of one entity, not separate things. The DB keeps them
  // all (append-only proposal ledger); this surface folds them to the final state because it's
  // a search/browse field, not a log — the per-event history already has its own home (the
  // Activity side panel). Title anchors to the create's name (who), subtitle/status come from
  // the freshest event (current state). Clicking opens the live object for editing.
  // The stack/expand treatment below is reserved for groups of genuinely DISTINCT entities —
  // listing cascades today (origin ad + each agent's reshare have their own natural keys and
  // contacts); one client's MULTIPLE demands (apartment + commercial) would deserve the same
  // stack when that grouping lands — same person, different demand ≈ same property, different
  // listing.
  if (!isListingCascade) {
    return (
      <HistoryItem item={head} availableFrom={availableFrom} reportedGone={reportedGone} districts={districts} features={features} glow={glow} glowClickable={glowClickable}
        onOpen={() => onCardClick(group)} titleOverride={headTitle}
        clientMeetings={clientMeetings} onOpenMeeting={onOpenMeeting}
        searchPolygons={searchPolygons} gallerySize={gallerySize} strip={strip} stateSlot={stateSlot} />
    )
  }

  if (expanded) {
    // LISTING cascade expanded = every item becomes a normal, full-size grid card, each its
    // own cell flowing with the grid — each member IS a distinct listing. A shared ring marks
    // group membership; the shade ALTERNATES between two shades (amber / yellow) cycling per
    // expanded group (see expandedShade in the parent) — a solid color ring doesn't tell two
    // adjacent expanded groups apart, alternating shades do. Toned down with opacity —
    // full-strength ring-amber-300/ring-yellow-800 read as too loud against the card grid.
    const n = group.items.length
    const ringClass = shade === 1 ? 'ring-yellow-800/60' : 'ring-amber-300/60'
    return (
      <>
        {group.items.map(item => (
          <div key={item.id} className={`rounded-lg ring-2 ${ringClass}`}>
            {/* Body click OPENS the member's detail modal (the expected action on any card —
                clicking the title used to silently collapse the group, which read as a dead
                click, 2026-07-13 UI audit); collapsing lives on the corner 🧵 toggle instead. */}
            <HistoryItem item={item} districts={districts} features={features}
              onOpen={() => onCardClick({ key: group.key, items: [item] })}
              searchPolygons={searchPolygons} gallerySize={gallerySize}
              thread={{ count: n, expanded, onToggle, noun }}
              cooperations={coopsByProperty?.[item.listing?.property_id]} coopOffer={coopOffersByProperty?.[item.listing?.property_id] || null} shares={sharesByProperty?.[item.listing?.property_id]} restrictions={restrByListing?.[item.listing?.listing_id]} payTerms={termsByListing?.[item.listing?.listing_id] || null} restrictionsCleared={clearByListing?.[item.listing?.listing_id]} views={viewsByProperty?.[item.listing?.property_id]} onOpenCooperation={onOpenCooperation} onAddMeeting={onAddMeeting} onCreateContract={onCreateContract} onShare={onShare} onRepost={onRepost} onShareOthers={onShareOthers} onOpenTerms={onOpenTerms} favorites={favorites} onToggleFavorite={onToggleFavorite} archived={archived} onToggleArchive={onToggleArchive} onReportGone={onReportGone} authorMark={authorMark}
              meetingsIndex={meetingsIndex} onOpenMeeting={onOpenMeeting} privileged={privileged}
              stackDates={listingStackDates([item])} />
          </div>
        ))}
      </>
    )
  }

  // DEFAULT (collapsed) = a physical stack, same look regardless of what kind of group this is:
  // the front sheet is a normal, FULL-SIZE card (the freshest item — headTitle anchors it to the
  // client's identity when this is a demand thread) with real content, sized exactly like any
  // other card in the grid. Every sheet behind it is bare — border + background only, no image/
  // text — so only a thin top-left corner line peeks out per extra item, instead of a smear of
  // overlapping photos. Clicking anywhere on the stack opens the property detail modal (one
  // modal, every posting inside — 2026-07-09); the count chip is the secondary gesture that
  // still expands the group in-grid for side-by-side comparison.
  const n = group.items.length
  // Cap the visible sheets at 4. They are a "there's more behind this" cue, not a count — the
  // chip and the expanded view carry the real number — and deeper sheets fade out so the stack
  // reads as trailing off rather than truncated.
  const SHEETS = Math.min(n - 1, 4)
  // ── 🚨 THE FACE CARD'S GEOMETRY IS AN INVARIANT (owner ruling, 2026-08-19) ────────────────
  // *"Stacked cards are great design. But we need to not change face cards size or shape or
  // move."* … *"We should keep it aligned to one card and clusters face cards. But on the
  // background we may add stacked effect as it is now."* … *"Not touching anything and hiding
  // under everything it it touches any part."*
  //
  // So, stated as the rule a later CSS tweak has to keep: **a stacked card occupies its grid
  // cell exactly as a solo card does.** Same origin, same width, same height, whether it has
  // four sheets behind it or none.
  //
  // 🔴 IT DID NOT. The wrapper carried `paddingTop/paddingLeft: SHEETS * STEP` — up to 24px —
  // which is the whole complaint in one line, three ways at once:
  //   · MOVED — the grid is `items-start`, so the face card began 24px lower than the solo card
  //     beside it and the row visibly failed to line up;
  //   · RESIZED — the cell width is fixed, so 24px of padding-left made the face card 24px
  //     NARROWER than its neighbours, and 24px shorter;
  //   · and it grew the cell, so a stack pushed its whole row taller.
  // The sheets were drawn INSIDE that padding, i.e. up and to the LEFT of where a card starts —
  // which is why a stack read as a bigger, displaced object rather than one card with depth.
  //
  // Now: no padding. The face card is the only thing in flow, so it alone sizes the wrapper, and
  // the sheets are absolutely positioned BEHIND it and offset DOWN-RIGHT, out of its box. Depth
  // is drawn into the grid gutter instead of stolen from the card.
  //
  // ⚠️ `isolation: isolate` IS LOAD-BEARING, not tidiness — it is the "hiding under everything"
  // half of the ruling. The wrapper was `position: relative` with NO z-index, which does not
  // create a stacking context, so the sheets' z-indexes competed with the sibling CARDS (grid
  // items are unpositioned, and any positioned element with z-index ≥ 1 paints above those).
  // A sheet could therefore paint OVER a neighbouring card. Isolating makes the whole stack one
  // unit in the grid's stacking order, so a sheet can only ever go UNDER a neighbour — and the
  // neighbours it can reach are the ones down and to the right, which come later in DOM order
  // and so paint on top by default. The rule holds without depending on the offset direction.
  //
  // ⚠️ STEP IS DERIVED FROM THE GUTTER, not a constant. The grid is `gap-3` = 12px, so the
  // deepest sheet may extend at most 12px before it reaches the neighbouring card. Dividing the
  // gutter by the sheet count keeps the whole cascade inside it at every depth (1 sheet → 6px,
  // 4 sheets → 3px each, still 12px total), capped at the original 6px so a two-item stack looks
  // exactly as it did. Hardcoding 6 was what let a four-sheet stack reach 24px and cross into
  // the next card. If the grid's gap changes, change GRID_GUTTER with it.
  const GRID_GUTTER = 12
  const STEP = Math.max(2, Math.min(6, Math.floor(GRID_GUTTER / Math.max(1, SHEETS))))
  return (
    <div className="relative cursor-pointer" style={{ isolation: 'isolate' }}
      onClick={() => onCardClick(group)} title={t('desk_stack_open_tooltip')}>
      {/* keyboard door to the stack (audit 2026-10-21), same as a single card's */}
      <button type="button" onClick={e => { e.stopPropagation(); onCardClick(group) }} className="sr-only focus:not-sr-only focus:absolute focus:top-1 focus:left-1 focus:z-20 focus:rounded focus:bg-primary focus:px-2 focus:py-1 focus:text-xs focus:text-primary-foreground">
        {t('desk_stack_open_tooltip')}
      </button>
      {/* Bare sheets — each the SAME SIZE as the front card, shifted diagonally down-right by
          k*STEP so they read as identical sheets underneath rather than progressively bigger
          boxes. k counts AWAY from the face card, so the furthest is the faintest.
          `zIndex: 0` puts every sheet behind the face card (which sits at 1) while keeping the
          whole group below any later grid item, thanks to the isolate above. */}
      {Array.from({ length: SHEETS }, (_, j) => {
        const k = SHEETS - j        // 1 = closest behind the face card, SHEETS = deepest
        return (
          <div key={j} aria-hidden="true"
            className="absolute rounded-lg border border-border bg-card shadow-lg"
            style={{ top: k * STEP, left: k * STEP, right: -k * STEP, bottom: -k * STEP,
                     zIndex: 0, opacity: (SHEETS - k + 1) / (SHEETS + 1) }} />
        )
      })}
      {/* ⚠️ NO pointer-events-none here (removed 2026-07-31, owner-reported: "hover on price
          is not working when cards are stacked"). It used to blanket the front card so any
          click reached this wrapper's onClick — but pointer-events cannot separate click from
          HOVER, so it silently killed every `title=` tooltip on the front card: price,
          age/fresh/available/gone badges, the inherited-address hint, viewers, family reach,
          the lead badge — and the ♥ toggle, which became a dead control. Four regions had
          already been patched back one at a time (dates line, platform chips, strip, footer);
          each new hover added since then was born broken. The default is now correct instead.
          Clicks still open the modal: the front card's own onClick is a no-op that does not
          stopPropagation, so plain-card clicks bubble here exactly as before.
          KNOWN, DELIBERATE: clicking the PHOTO now advances the gallery instead of opening
          the modal — it already did that on an unstacked card, and matching the two is the
          point. Every other click target on the card still opens it. */}
      {/* The ONLY child in flow, so it alone sizes the wrapper — which is what makes the cell
          identical to a solo card's. `zIndex: 1` simply beats the sheets' 0; it no longer has to
          out-rank a ladder of them. */}
      <div className="relative" style={{ zIndex: 1 }}>
        <HistoryItem item={group.items[0]} matchNote={matchNote} availableFrom={availableFrom} districts={districts} features={features}
          glow={glow} glowClickable={glowClickable} onOpen={() => {}} titleOverride={headTitle}
          searchPolygons={searchPolygons} gallerySize={gallerySize}
          stackDates={listingStackDates(group.items)}
          /* Count chip on the front card — self-labels the stack AND is the secondary click
             target that expands the group in-grid (the stack body itself opens the detail
             modal). stopPropagation so the chip never also triggers the modal.
             ⚠️ Handed DOWN as cornerSlot (2026-08-03) rather than drawn by this wrapper: the
             card is the only component that knows whether it has an action row for the chip
             to sit on, and the chip was costing every stacked card a reserved empty strip.
             The wrapper still owns the CONTENT and the handler; only placement moved. */
          /* ⚠️ A cascade WITH a chain has no count chip and no in-grid expansion (owner
             2026-08-04: "Move ჯაჭვი instead of x2 — no more cards opening, only chain below").
             The chain panel IS the expansion now: same members, one line each, links included.
             Two controls that both mean "there are N things here" was the redundancy the mock
             review circled. The count survives only on chains-less cascades (nothing else
             tells their size) and on client threads, whose expansion has no chain equivalent. */
          cornerSlot={isListingCascade
            ? (chain ? null
              : <button type="button" onClick={e => { e.stopPropagation(); onToggle() }} aria-expanded={!!expanded}
                title={tLog('desk_entry_group_tooltip', { n, 'listings/updates': t(noun === 'listings' ? 'desk_thread_noun_listings' : 'desk_thread_noun_updates') })}
                className="text-xs text-foreground px-1.5 py-0.5 rounded cursor-pointer hover:bg-accent whitespace-nowrap">{tLog('desk_entry_stack_count', { n })}</button>)
            : <button type="button" onClick={e => { e.stopPropagation(); onToggle() }} aria-expanded={!!expanded}
                title={tLog('desk_entry_group_tooltip', { n, 'listings/updates': t(noun === 'listings' ? 'desk_thread_noun_listings' : 'desk_thread_noun_updates') })}
                className={`${CORNER_CHIP} bg-primary/90 text-primary-foreground cursor-pointer`}><IconText>{tLog('desk_entry_thread_generic', { n, 'listings/updates': t(noun === 'listings' ? 'desk_thread_noun_listings' : 'desk_thread_noun_updates') })}</IconText></button>}
          platformChips={platformChips} chain={chain} strip={strip} stateSlot={stateSlot} {...coopProps} />
      </div>
    </div>
  )
})
